/**
 * Super-Admin · eCourts API Console
 *
 * Five tabs:
 *   1. Connection  — platform-wide eCourts API token (eci_live_…) + test
 *   2. Endpoints   — per-endpoint enable / force_on / force_off
 *   3. Pricing     — per-endpoint base cost, markup %, fixed add-on
 *   4. Refresh     — refresh cadence buckets (within 7d → daily, etc.)
 *   5. Audit       — cross-tenant passbook
 *
 * Everything saved here flows into `platform_settings.ecourts.*` and is used
 * by routes_ecourts.py at runtime via the policy + billing helpers.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Plug, RefreshCw, Search, Settings as Cog, History, AlertTriangle, Download, CheckCircle2, CalendarCheck, Zap, Play, Send, Mail, Bell, MessageSquare, Building2, Users, XCircle, Loader2, ChevronDown, ChevronUp, Filter, UserCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { PageHeader } from "@/components/Ui";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const TABS = [
    { id: "dispatch",    label: "Dispatch",    icon: Send },
    { id: "strategy1",   label: "Strategy 1",  icon: Zap },
    { id: "strategy2",   label: "Strategy 2",  icon: Zap },
    { id: "strategy3",   label: "Strategy 3",  icon: Zap },
    { id: "strategy4",   label: "Strategy 4",  icon: Zap },
    { id: "import",      label: "Import",       icon: Download },
    { id: "connection",  label: "Connection",  icon: Plug },
    { id: "endpoints",   label: "Endpoints",   icon: Cog },
    { id: "pricing",     label: "Pricing",     icon: Search },
    { id: "audit",       label: "Audit",       icon: History },
];

export default function AdminEcourts() {
    const [tab, setTab] = useState("dispatch");
    return (
        <>
            <PageHeader title="eCourts API"
                subtitle="Configure the platform-wide eCourts integration. Per-endpoint enable/disable, pricing, and refresh cadence are all set here." />
            <div className="flex items-center gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                        <button key={t.id} onClick={() => setTab(t.id)}
                            data-testid={`tab-${t.id}`}
                            className={`flex items-center gap-2 px-4 py-3 text-xs uppercase tracking-[0.16em] whitespace-nowrap ${tab === t.id ? "text-primary border-b-2 border-primary" : "text-muted-foreground hover:text-primary"}`}>
                            <Icon className="w-3.5 h-3.5" strokeWidth={1.5} /> {t.label}
                        </button>
                    );
                })}
            </div>
            {tab === "dispatch"   && <CauseListDispatchTab/>}
            {tab === "strategy1"  && <StrategyTab/>}
            {tab === "strategy2"  && <Strategy2Tab/>}
            {tab === "strategy3"  && <Strategy3Tab/>}
            {tab === "strategy4"  && <Strategy4Tab/>}            {tab === "connection" && <ConnectionTab/>}
            {tab === "import"     && <ImportOnBehalfTab/>}
            {tab === "endpoints"  && <EndpointsTab/>}
            {tab === "pricing"    && <PricingTab/>}
            {tab === "audit"      && <AuditTab/>}
        </>
    );
}


// ============================================================================
// v2.19.4 · Strategy tab. Hearing-Anchored + Causelist-First + Delta is the
// only refresh engine — the legacy tiered-freshness path was purged.
// ============================================================================
function StrategyTab() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/ecourts/hearing-anchored/config");
            setCfg(data.config);
        } catch (e) { toast.error(apiError(e)); }
    };

    const [runs, setRuns] = useState([]);
    const loadRuns = async () => {
        try {
            const r = await api.get("/admin/ecourts/hearing-anchored/recent-runs?limit=20");
            setRuns(r.data.runs || []);
        } catch { /* ignore */ }
    };
    useEffect(() => { load(); loadRuns(); }, []);

    const save = async () => {
        setSaving(true);
        try {
            const patch = { ...cfg };
            delete patch.updated_at; delete patch.updated_by;
            await api.patch("/admin/ecourts/hearing-anchored/config", patch);
            toast.success("Strategy config saved");
            await load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const runNow = async (which) => {
        setBusy(which);
        try {
            const r = await api.post(`/admin/ecourts/hearing-anchored/run-now?which=${which}`);
            const d = r.data || {};
            if (d.queued) {
                toast.info(d.message || `${which} queued`, { duration: 6000 });
            } else {
                toast.success(d.message || `${which} completed`, { duration: 8000 });
            }
            await loadRuns();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    if (!cfg) return <div className="text-xs text-muted-foreground py-8" data-testid="strategy-loading">Loading strategy config…</div>;

    return (
        <div className="space-y-6" data-testid="strategy-tab">
            {/* Hero card — master enable/disable + strategy info */}
            <div className={`border-2 rounded-2xl p-6 transition-colors ${cfg.strategy_1_enabled !== false ? "border-primary/60 bg-primary/5" : "border-border bg-muted/30"}`}>
                <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Strategy 1 · Refresh Engine</div>
                        <div className="text-2xl font-medium text-foreground" data-testid="strategy-current">
                            Hearing-Anchored · Causelist-First + Delta
                        </div>
                        <div className="text-xs text-muted-foreground mt-2 max-w-2xl">
                            Cron sweeps route through <code className="text-primary">cnr_causelist_batch</code> (₹0.30/CNR)
                            and escalate to <code>case_detail</code> (₹1.50) only when a real date change or
                            disposal is detected. Weekly Sunday sweep, daily T+3 preponement scan, daily T-1 cause-list
                            batch, monthly stale + disposal check. <strong className="text-foreground">~60% cheaper</strong>
                            {" "}than the legacy path with the same effective fidelity.
                        </div>
                    </div>
                    {/* Master enable / disable */}
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <button
                            onClick={() => setCfg({ ...cfg, strategy_1_enabled: cfg.strategy_1_enabled === false ? true : false })}
                            data-testid="strategy-master-toggle"
                            className={`relative w-12 h-6 rounded-full transition-colors focus:outline-none
                                ${cfg.strategy_1_enabled !== false ? "bg-primary" : "bg-muted-foreground/30"}`}>
                            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all
                                ${cfg.strategy_1_enabled !== false ? "left-7" : "left-1"}`}/>
                        </button>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-medium ${cfg.strategy_1_enabled !== false ? "text-primary" : "text-muted-foreground"}`}
                            data-testid="strategy-master-status">
                            {cfg.strategy_1_enabled !== false ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                </div>
                {cfg.strategy_1_enabled === false && (
                    <div className="mt-3 border border-amber-500/40 bg-amber-500/10 rounded px-3 py-2 text-xs text-amber-300" data-testid="strategy-disabled-banner">
                        Strategy 1 is <strong>disabled</strong>. All scheduled sweeps are paused. Enabling this will automatically disable Strategy 2.
                    </div>
                )}
                {cfg.strategy_1_enabled !== false && (
                    <div className="mt-3 border border-primary/20 bg-primary/5 rounded px-3 py-2 text-xs text-muted-foreground">
                        <strong className="text-primary">Active.</strong> Only one strategy can run globally — enabling Strategy 2 will disable this.
                    </div>
                )}
            </div>

            {/* ── Save button — always visible regardless of enabled state ── */}
            <div className="flex items-center gap-3">
                <button onClick={save} disabled={saving}
                    data-testid="strategy-save"
                    className="text-[10px] uppercase tracking-[0.16em] px-5 py-2 bg-primary text-black rounded disabled:opacity-50">
                    {saving ? "Saving…" : "Save Strategy 1"}
                </button>
                <div className="text-[10px] text-muted-foreground">
                    {cfg.strategy_1_enabled !== false
                        ? "Enabling this will disable Strategy 2 globally."
                        : "Disabled — sweeps paused. Per-tenant overrides run independently."}
                </div>
            </div>

            {/* Schedule config — each sweep as a row with toggle + time */}
            {/* ↓ Everything below grayed-out when S1 is disabled. */}
            <div className={cfg.strategy_1_enabled === false
                ? "pointer-events-none opacity-40 select-none space-y-6"
                : "space-y-6"}>
            <div className="border border-border rounded-2xl bg-card p-6" data-testid="strategy-config">
                <div className="text-sm font-medium text-foreground mb-1">Sweeps — Schedule & Enable/Disable</div>
                <div className="text-xs text-muted-foreground mb-4">
                    Toggle each sweep on or off platform-wide. Times are IST. Per-tenant overrides are in the section below.
                </div>
                <div className="divide-y divide-border border border-border rounded-xl overflow-hidden">
                    {[
                        { key: "sunday_sweep", label: "Sunday weekly sweep", hint: "Mon–Sat pre-ponement probe", timeKey: "sunday_sweep_ist", badge: "Weekly" },
                        { key: "t_plus_3",     label: "Daily T+3 case-refresh", hint: "Cases with next hearing = today+3", timeKey: "t_plus_3_ist", badge: "Daily" },
                        { key: "t_minus_2",    label: "Daily T-2 post-hearing", hint: "Cases heard 2 days ago — post-hearing follow-up", timeKey: "t_minus_2_ist", badge: "Daily" },
                        { key: "t_minus_1",    label: "Daily T-1 cause-list batch", hint: "100 CNRs/call · tomorrow's hearings · feeds PDF", timeKey: "t_minus_1_ist", badge: "Daily" },
                        { key: "monthly_stale",label: "Monthly stale sweep", hint: "Overdue + disposal detection", timeKey: "monthly_stale_ist", badge: "Monthly" },
                    ].map(({ key, label, hint, timeKey, badge }) => {
                        const enabled = cfg[`${key}_enabled`] !== false;
                        return (
                            <div key={key}
                                className={`flex items-center gap-3 px-4 py-3 ${!enabled ? "opacity-50" : ""}`}
                                data-testid={`sweep-row-${key}`}>
                                {/* Toggle */}
                                <button
                                    onClick={() => setCfg({ ...cfg, [`${key}_enabled`]: !enabled })}
                                    data-testid={`sweep-toggle-${key}`}
                                    className={`relative flex-shrink-0 w-9 h-5 rounded-full transition-colors ${enabled ? "bg-primary" : "bg-muted-foreground/30"}`}>
                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${enabled ? "left-[18px]" : "left-0.5"}`}/>
                                </button>
                                {/* Label */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-medium text-foreground">{label}</span>
                                        <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider font-medium ${enabled ? "bg-primary/10 text-primary" : "bg-muted text-muted-foreground"}`}>
                                            {badge}
                                        </span>
                                    </div>
                                    <div className="text-[10px] text-muted-foreground mt-0.5">{hint}</div>
                                </div>
                                {/* Time input */}
                                <input type="time" value={cfg[timeKey] || ""} disabled={!enabled}
                                    onChange={e => setCfg({ ...cfg, [timeKey]: e.target.value })}
                                    data-testid={`sweep-time-${key}`}
                                    className="text-xs bg-background border border-border rounded px-2 py-1 text-foreground w-[90px] disabled:opacity-40"/>
                            </div>
                        );
                    })}
                </div>

                {/* Advanced numeric settings */}
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mt-4 pt-4 border-t border-border">
                    <NumField label="T+3 offset (days)" value={cfg.t_plus_3_days}
                        onChange={v => setCfg({ ...cfg, t_plus_3_days: v })}
                        min={1} max={7} testid="cfg-t-plus-3-days" />
                    <NumField label="Cause-list batch size" hint="eCourts caps at 100"
                        value={cfg.batch_size}
                        onChange={v => setCfg({ ...cfg, batch_size: v })}
                        min={1} max={100} testid="cfg-batch-size" />
                    <NumField label="Monthly stale — day" value={cfg.monthly_stale_day}
                        onChange={v => setCfg({ ...cfg, monthly_stale_day: v })}
                        min={1} max={28} testid="cfg-monthly-day" />
                    <NumField label="Disposed recheck (days)"
                        hint="After disposal, recheck after N days"
                        value={cfg.disposed_recheck_days}
                        onChange={v => setCfg({ ...cfg, disposed_recheck_days: v })}
                        min={7} max={365} testid="cfg-disposed-days" />
                    <NumField label="Monthly min gap (days)"
                        hint="Don't re-sweep a case within N days"
                        value={cfg.monthly_stale_min_days}
                        onChange={v => setCfg({ ...cfg, monthly_stale_min_days: v })}
                        min={1} max={90} testid="cfg-monthly-mingap" />
                </div>
            </div>

            {/* Manual triggers */}
                <div className="border border-border rounded-2xl bg-card p-6" data-testid="strategy-triggers">
                    <div className="text-sm font-medium text-foreground mb-3">Run a sweep now (manual)</div>
                    <div className="text-xs text-muted-foreground mb-4">These fire the same jobs the cron would fire at their scheduled IST time. Useful for testing + urgent refresh.</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-2">
                        {[
                            ["sunday", "Sunday sweep", "Mon–Sat CASE_REFRESH"],
                            ["t_plus_3", "T+3 refresh", "Cases where next hearing = today+3"],
                            ["t_minus_2", "T-2 post-hearing", "Cases heard 2 days ago"],
                            ["t_minus_1", "T-1 cause-list", "Tomorrow's hearings, batch of 100"],
                            ["monthly_stale", "Monthly stale sweep", "next_hearing_date < today"],
                        ].map(([which, label, hint]) => (
                            <button key={which}
                                onClick={() => runNow(which)}
                                disabled={busy === which || cfg.strategy_1_enabled === false}
                                data-testid={`run-${which.replace(/_/g, "-")}`}
                                className="text-left border border-border hover:border-primary/60 rounded-lg p-3 disabled:opacity-50 disabled:cursor-not-allowed">
                                <div className="flex items-center gap-2 text-xs text-foreground">
                                    <Play className="w-3 h-3 text-primary"/>
                                    {busy === which ? "Queueing…" : label}
                                </div>
                                <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>
                            </button>
                        ))}
                    </div>
                </div>

            {/* Recent runs — silent-but-successful runs (like 0 hits) show here */}
                <div className="border border-border rounded-2xl bg-card p-6" data-testid="strategy-recent-runs">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-sm font-medium text-foreground">Recent runs</div>
                        <button onClick={loadRuns} className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-primary"
                            data-testid="recent-runs-refresh">Refresh</button>
                    </div>
                    {runs.length === 0 ? (
                        <div className="text-xs text-muted-foreground py-4">
                            No runs yet. Trigger a sweep above — result will appear here immediately.
                        </div>
                    ) : (
                        <div className="border border-border rounded-lg overflow-x-auto" data-testid="recent-runs-table">
                            <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-b border-border">
                                    <tr>
                                        <th className="text-left px-3 py-2">When</th>
                                        <th className="text-left px-3 py-2">Sweep</th>
                                        <th className="text-right px-3 py-2">Refreshed / Hits</th>
                                        <th className="text-right px-3 py-2">Checked / Skipped</th>
                                        <th className="text-left px-3 py-2">Details</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {runs.map(r => (
                                        <tr key={r._id} className="border-b border-card" data-testid={`run-row-${r._id}`}>
                                            <td className="px-3 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(r.ts)}</td>
                                            <td className="px-3 py-2"><code className="text-primary text-[10px]">{r.kind}</code></td>
                                            <td className="px-3 py-2 text-right text-emerald-500 font-medium">{r.refreshed ?? r.hits ?? 0}</td>
                                            <td className="px-3 py-2 text-right text-muted-foreground">
                                                {r.cnrs_checked !== undefined ? `${r.cnrs_checked}` : (r.skipped !== undefined ? `— / ${r.skipped}` : "—")}
                                            </td>
                                            <td className="px-3 py-2 text-[10px] text-muted-foreground">
                                                {r.target_date ? `for ${r.target_date}` : (r.range ? `${r.range[0]}→${r.range[1]}` : "")}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

            </div>{/* end disabled wrapper */}

            {/* Auto-Sync on Open + Per-tenant overrides — always interactive */}
            <AutoSyncCard/>

            {/* Per-tenant sweep overrides */}
            <SweepTenantOverridesCard globalCfg={cfg}/>
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Auto-Sync on case open — global toggle + per-tenant overrides
// ─────────────────────────────────────────────────────────────────────────────
function AutoSyncCard() {
    const [globalEnabled, setGlobalEnabled] = useState(false);
    const [overrides, setOverrides] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(null);

    const load = async () => {
        try {
            const { data } = await api.get("/admin/ecourts/auto-sync-settings");
            setGlobalEnabled(data.global_enabled ?? false);
            setOverrides(data.tenant_overrides || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoaded(true); }
    };
    useEffect(() => { load(); }, []);

    const saveGlobal = async (val) => {
        setSaving("global");
        try {
            await api.put("/admin/ecourts/auto-sync-settings", { enabled: val });
            setGlobalEnabled(val);
            toast.success(`Auto-sync on open ${val ? "enabled" : "disabled"} globally`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(null); }
    };

    const saveTenant = async (tenantId, val) => {
        setSaving(tenantId);
        try {
            await api.put(`/admin/ecourts/tenants/${tenantId}/auto-sync`, { enabled: val });
            setOverrides(prev => prev.map(t =>
                t.tenant_id === tenantId ? { ...t, enabled: val } : t));
            toast.success(val === null ? "Tenant will inherit global setting" : `Tenant override saved: ${val ? "ON" : "OFF"}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(null); }
    };

    if (!loaded) return null;

    return (
        <div className="border border-border rounded-2xl bg-card p-6" data-testid="auto-sync-card">
            <div className="flex items-start justify-between gap-4 mb-1">
                <div>
                    <div className="text-sm font-medium text-foreground">Auto-Sync on Case Open</div>
                    <div className="text-xs text-muted-foreground mt-1 max-w-xl">
                        When ON, opening any litigation automatically triggers a live eCourts refresh
                        (costs <strong className="text-foreground">₹1.50/case</strong> per open).
                        Default is <span className="text-rose-500 font-medium">OFF</span> — use the manual
                        "Refresh & Update" button on the case page instead.
                    </div>
                </div>
                <Toggle
                    on={globalEnabled}
                    onChange={saveGlobal}
                    disabled={saving === "global"}
                    testId="global-auto-sync-toggle"
                />
            </div>

            {overrides.length > 0 && (
                <div className="border-t border-border mt-4 pt-4">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-3">
                        Per-tenant override
                    </div>
                    <div className="border border-border rounded-lg overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground border-b border-border">
                                <tr>
                                    <th className="text-left px-3 py-2">Tenant</th>
                                    <th className="text-left px-3 py-2">Override</th>
                                    <th className="text-left px-3 py-2">Effective</th>
                                    <th className="text-right px-3 py-2">Set override</th>
                                </tr>
                            </thead>
                            <tbody>
                                {overrides.map(t => {
                                    const effective = t.enabled === null ? globalEnabled : t.enabled;
                                    return (
                                        <tr key={t.tenant_id} className="border-b border-border last:border-0"
                                            data-testid={`auto-sync-tenant-${t.tenant_id}`}>
                                            <td className="px-3 py-2 text-foreground font-medium">{t.name}</td>
                                            <td className="px-3 py-2 text-muted-foreground">
                                                {t.enabled === null
                                                    ? <span className="italic">inherit global</span>
                                                    : t.enabled
                                                        ? <span className="text-emerald-500">Force ON</span>
                                                        : <span className="text-rose-500">Force OFF</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={effective ? "text-emerald-500 font-medium" : "text-muted-foreground"}>
                                                    {effective ? "ON" : "OFF"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center justify-end gap-1">
                                                    <button onClick={() => saveTenant(t.tenant_id, true)}
                                                        disabled={!!saving}
                                                        data-testid={`auto-sync-force-on-${t.tenant_id}`}
                                                        className="px-2 py-0.5 text-[10px] rounded border border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10 disabled:opacity-40 whitespace-nowrap">
                                                        Force ON
                                                    </button>
                                                    <button onClick={() => saveTenant(t.tenant_id, false)}
                                                        disabled={!!saving}
                                                        data-testid={`auto-sync-force-off-${t.tenant_id}`}
                                                        className="px-2 py-0.5 text-[10px] rounded border border-rose-500/40 text-rose-500 hover:bg-rose-500/10 disabled:opacity-40 whitespace-nowrap">
                                                        Force OFF
                                                    </button>
                                                    <button onClick={() => saveTenant(t.tenant_id, null)}
                                                        disabled={!!saving}
                                                        data-testid={`auto-sync-inherit-${t.tenant_id}`}
                                                        className="px-2 py-0.5 text-[10px] rounded border border-border text-muted-foreground hover:bg-muted disabled:opacity-40 whitespace-nowrap">
                                                        Inherit
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}


// ─────────────────────────────────────────────────────────────────────────────
// Per-tenant sweep overrides — enable/disable each sweep per tenant
// ─────────────────────────────────────────────────────────────────────────────
const SWEEP_META = [
    { key: "sunday_sweep", short: "Sunday" },
    { key: "t_plus_3",     short: "T+3" },
    { key: "t_minus_2",    short: "T-2" },
    { key: "t_minus_1",    short: "T-1" },
    { key: "monthly_stale",short: "Monthly" },
];

function SweepTenantOverridesCard({ globalCfg }) {
    const [tenants, setTenants] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [saving, setSaving] = useState(null); // tenant_id while saving

    const load = async () => {
        try {
            const { data } = await api.get("/admin/ecourts/hearing-anchored/tenant-sweep-overrides");
            setTenants(data.tenants || []);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoaded(true); }
    };
    useEffect(() => { load(); }, []);

    const setOverride = async (tenantId, sweepKey, value) => {
        setSaving(tenantId);
        try {
            const tenant = tenants.find(t => t.tenant_id === tenantId);
            const next = { ...(tenant?.overrides || {}), [sweepKey]: value };
            await api.put(`/admin/ecourts/tenants/${tenantId}/sweep-overrides`, next);
            setTenants(prev => prev.map(t =>
                t.tenant_id === tenantId ? { ...t, overrides: next } : t));
            toast.success("Tenant sweep override saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(null); }
    };

    if (!loaded) return null;

    return (
        <div className="border border-border rounded-2xl bg-card p-6" data-testid="sweep-tenant-overrides-card">
            <div className="text-sm font-medium text-foreground mb-1">Per-tenant Sweep Overrides</div>
            <div className="text-xs text-muted-foreground mb-4">
                Override individual sweeps for each tenant.
                <strong className="text-foreground"> Inherit</strong> = follow the platform toggle above.
                <strong className="text-emerald-500"> ON</strong> = force enabled.
                <strong className="text-rose-500"> OFF</strong> = force disabled.
            </div>
            <div className="border border-border rounded-xl overflow-x-auto">
                <table className="w-full text-xs min-w-[640px]">
                    <thead className="border-b border-border bg-muted/30">
                        <tr>
                            <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">Tenant</th>
                            {SWEEP_META.map(s => {
                                const globalOn = globalCfg ? globalCfg[`${s.key}_enabled`] !== false : true;
                                return (
                                    <th key={s.key} className="text-center px-2 py-2 text-[10px] uppercase tracking-[0.14em] text-muted-foreground font-medium">
                                        <div>{s.short}</div>
                                        <div className={`text-[9px] mt-0.5 ${globalOn ? "text-emerald-500" : "text-rose-500"}`}>
                                            global: {globalOn ? "ON" : "OFF"}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {tenants.map(t => (
                            <tr key={t.tenant_id} className="border-b border-border last:border-0 hover:bg-muted/10"
                                data-testid={`sweep-override-row-${t.tenant_id}`}>
                                <td className="px-3 py-2 font-medium text-foreground whitespace-nowrap">{t.name}</td>
                                {SWEEP_META.map(s => {
                                    const val = t.overrides?.[s.key];
                                    const globalOn = globalCfg ? globalCfg[`${s.key}_enabled`] !== false : true;
                                    const effective = val === null || val === undefined ? globalOn : val;
                                    return (
                                        <td key={s.key} className="px-2 py-2 text-center">
                                            <div className="flex items-center justify-center gap-0.5">
                                                <button
                                                    onClick={() => setOverride(t.tenant_id, s.key, true)}
                                                    disabled={saving === t.tenant_id}
                                                    title="Force ON"
                                                    data-testid={`sweep-on-${t.tenant_id}-${s.key}`}
                                                    className={`px-1.5 py-0.5 text-[9px] rounded-l border transition-colors disabled:opacity-40
                                                        ${val === true ? "bg-emerald-500 border-emerald-500 text-white" : "border-border text-muted-foreground hover:border-emerald-500 hover:text-emerald-500"}`}>
                                                    ON
                                                </button>
                                                <button
                                                    onClick={() => setOverride(t.tenant_id, s.key, null)}
                                                    disabled={saving === t.tenant_id}
                                                    title="Inherit global"
                                                    data-testid={`sweep-inherit-${t.tenant_id}-${s.key}`}
                                                    className={`px-1.5 py-0.5 text-[9px] border-y transition-colors disabled:opacity-40
                                                        ${val === null || val === undefined ? "bg-muted border-border text-foreground" : "border-border text-muted-foreground hover:bg-muted"}`}>
                                                    ~
                                                </button>
                                                <button
                                                    onClick={() => setOverride(t.tenant_id, s.key, false)}
                                                    disabled={saving === t.tenant_id}
                                                    title="Force OFF"
                                                    data-testid={`sweep-off-${t.tenant_id}-${s.key}`}
                                                    className={`px-1.5 py-0.5 text-[9px] rounded-r border transition-colors disabled:opacity-40
                                                        ${val === false ? "bg-rose-500 border-rose-500 text-white" : "border-border text-muted-foreground hover:border-rose-500 hover:text-rose-500"}`}>
                                                    OFF
                                                </button>
                                            </div>
                                            <div className={`text-[9px] mt-0.5 ${effective ? "text-emerald-500" : "text-muted-foreground"}`}>
                                                {effective ? "active" : "skip"}
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function TimeField({ label, hint, value, onChange, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{label}</div>
            <input type="time" value={value || ""} onChange={e => onChange(e.target.value)}
                data-testid={testid}
                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground font-mono"/>
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}

function NumField({ label, hint, value, onChange, min, max, testid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">{label}</div>
            <input type="number" min={min} max={max} value={value ?? ""}
                onChange={e => onChange(parseInt(e.target.value, 10))}
                data-testid={testid}
                className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground font-mono"/>
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}


// ---------- Connection ----------
function ConnectionTab() {
    const [conn, setConn] = useState(null);
    const [token, setToken] = useState("");
    const [baseUrl, setBaseUrl] = useState("");
    const [enabled, setEnabled] = useState(false);
    const [busy, setBusy] = useState(false);
    const [testing, setTesting] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/ecourts/connection");
            setConn(r.data);
            setBaseUrl(r.data.base_url || "");
            setEnabled(!!r.data.enabled);
        } catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        setBusy(true);
        try {
            const body = { enabled, base_url: baseUrl };
            if (token.trim()) body.token = token.trim();
            await api.patch("/admin/ecourts/connection", body);
            setToken("");
            toast.success("Connection settings saved");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };

    const test = async () => {
        setTesting(true);
        try {
            await api.post("/admin/ecourts/connection/test");
            toast.success("Connection OK — eCourts responded successfully");
            await load();
        } catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
    };

    if (!conn) return <div className="text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="space-y-6 max-w-2xl">
            <div className="bg-muted border border-border p-6 space-y-4">
                <div className="overline text-primary">Platform credentials</div>
                <div className="text-xs text-muted-foreground leading-relaxed">
                    Single eCourts API token used for all tenants. Each call is billed against the firm's AI wallet at (base × markup + add-on).
                    Get a token at <a href="https://ecourtsindia.com/api/pricing" target="_blank" rel="noreferrer" className="text-primary underline">ecourtsindia.com/api/pricing</a> — ₹200 free credits on signup.
                </div>

                <Field label="Master enable">
                    <Toggle on={enabled} onChange={setEnabled} testId="ecourts-enable-master"/>
                    <div className="text-[10px] text-muted-foreground mt-1">When OFF, no eCourts call is allowed across the entire platform.</div>
                </Field>

                <Field label="API token (eci_live_…)">
                    <input type="password" value={token} onChange={e => setToken(e.target.value)}
                        placeholder={conn.has_token ? `Saved · ${conn.token_masked}` : "Paste new token"}
                        className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none font-mono"
                        data-testid="ecourts-token-input"/>
                    <div className="text-[10px] text-muted-foreground mt-1">Token is write-only. We show a masked preview after save.</div>
                </Field>

                <Field label="Base URL">
                    <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)}
                        className="w-full bg-background border border-border px-3 py-2 text-sm font-mono"
                        data-testid="ecourts-baseurl-input"/>
                </Field>

                <div className="flex items-center gap-3 pt-2">
                    <button onClick={save} disabled={busy} data-testid="ecourts-save-conn"
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-40">
                        {busy ? "Saving…" : "Save"}
                    </button>
                    {conn.has_token && (
                        <button onClick={test} disabled={testing} data-testid="ecourts-test-conn"
                            className="px-4 py-2 border border-border text-xs uppercase tracking-[0.16em] hover:border-primary">
                            {testing ? "Testing…" : "Test connection"}
                        </button>
                    )}
                </div>

                {conn.last_test_status && (
                    <div className="flex items-center gap-2 text-xs">
                        {conn.last_test_status === "ok" ? (
                            <><CheckCircle2 className="w-4 h-4 text-emerald-500"/> <span className="text-emerald-400">Last test OK</span></>
                        ) : (
                            <><AlertTriangle className="w-4 h-4 text-red-500"/> <span className="text-red-400">Last test: {conn.last_test_status}</span></>
                        )}
                        <span className="text-muted-foreground">· {conn.last_test_at && fmtDateTime(conn.last_test_at)}</span>
                    </div>
                )}
            </div>
        </div>
    );
}

// ---------- Endpoints ----------
function EndpointsTab() {
    const [rows, setRows] = useState([]);
    const load = useCallback(async () => {
        try { const r = await api.get("/admin/ecourts/endpoints"); setRows(r.data.endpoints); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = async (key, body) => {
        try {
            await api.patch(`/admin/ecourts/endpoints/${key}`, body);
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const epCtrl = useTableControls(rows, {
        accessors: {
            enabled: (e) => e.enabled ? "on" : "off",
            force_on: (e) => e.force_on ? "on" : "off",
            force_off: (e) => e.force_off ? "on" : "off",
            billed: (e) => e.billable ? e.billed_inr_preview : -1,
        },
    });

    return (
        <div className="space-y-3">
            <div className="text-xs text-muted-foreground mb-4">
                <strong className="text-primary">Force ON</strong> overrides every tenant's "disabled" preference (use sparingly — usually for legally required endpoints).{" "}
                <strong className="text-primary">Force OFF</strong> blocks the endpoint for all tenants regardless of their setting.
            </div>
            <FilterChip ctrl={epCtrl} className="mb-2"/>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <SortableTh field="label" ctrl={epCtrl} className="text-left p-3">Endpoint</SortableTh>
                            <SortableTh field="category" ctrl={epCtrl} className="text-left p-3">Category</SortableTh>
                            <SortableTh field="enabled" ctrl={epCtrl} className="text-center p-3" align="center">Enabled</SortableTh>
                            <SortableTh field="force_on" ctrl={epCtrl} className="text-center p-3" align="center">Force ON</SortableTh>
                            <SortableTh field="force_off" ctrl={epCtrl} className="text-center p-3" align="center">Force OFF</SortableTh>
                            <SortableTh field="billed" ctrl={epCtrl} className="text-right p-3" align="right">Billed (preview)</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {epCtrl.rows.map(e => (
                            <tr key={e.key} className="border-t border-border hover:bg-muted" data-testid={`ep-row-${e.key}`}>
                                <td className="p-3">
                                    <div className="text-primary">{e.label}</div>
                                    <div className="text-[10px] text-muted-foreground">{e.description}</div>
                                </td>
                                <td className="p-3 text-muted-foreground">{e.category}</td>
                                <td className="p-3 text-center">
                                    <Toggle on={e.enabled} onChange={v => patch(e.key, { enabled: v })} testId={`ep-${e.key}-enabled`}/>
                                </td>
                                <td className="p-3 text-center">
                                    <Toggle on={e.force_on} onChange={v => patch(e.key, { force_on: v, force_off: v ? false : e.force_off })} testId={`ep-${e.key}-forceon`}/>
                                </td>
                                <td className="p-3 text-center">
                                    <Toggle on={e.force_off} onChange={v => patch(e.key, { force_off: v, force_on: v ? false : e.force_on })} testId={`ep-${e.key}-forceoff`}/>
                                </td>
                                <td className="p-3 text-right font-mono">
                                    {e.billable ? `₹${e.billed_inr_preview.toFixed(2)}` : <span className="text-emerald-400">Free</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Pricing ----------
function PricingTab() {
    const [rows, setRows] = useState([]);
    const [edits, setEdits] = useState({});
    const load = useCallback(async () => {
        try { const r = await api.get("/admin/ecourts/endpoints"); setRows(r.data.endpoints); setEdits({}); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const setEdit = (k, f, v) => setEdits(prev => ({ ...prev, [k]: { ...(prev[k] || {}), [f]: v } }));

    const save = async (k) => {
        const body = {};
        const e = edits[k] || {};
        ["base_cost_inr", "markup_percent", "fixed_addon_inr"].forEach(f => {
            if (e[f] !== undefined && e[f] !== "") body[f] = parseFloat(e[f]);
        });
        if (!Object.keys(body).length) return;
        try {
            await api.patch(`/admin/ecourts/endpoints/${k}`, body);
            toast.success("Pricing updated");
            await load();
        } catch (err) { toast.error(apiError(err)); }
    };

    return (
        <div className="space-y-3">
            <div className="text-xs text-muted-foreground mb-2">
                Formula: <strong>Billed = base × (1 + markup/100) + fixed add-on</strong>. Default markup is 100% (i.e. 2× of base).
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <th className="text-left p-3">Endpoint</th>
                            <th className="text-right p-3">Base ₹</th>
                            <th className="text-right p-3">Markup %</th>
                            <th className="text-right p-3">Add-on ₹</th>
                            <th className="text-right p-3">Final ₹</th>
                            <th className="text-right p-3 w-20"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map(e => {
                            const ed = edits[e.key] || {};
                            const base = parseFloat(ed.base_cost_inr ?? e.base_cost_inr);
                            const mk   = parseFloat(ed.markup_percent ?? e.markup_percent);
                            const addn = parseFloat(ed.fixed_addon_inr ?? e.fixed_addon_inr);
                            const final = (base * (1 + mk/100) + addn).toFixed(2);
                            const dirty = Object.keys(ed).length > 0;
                            return (
                                <tr key={e.key} className="border-t border-border" data-testid={`price-row-${e.key}`}>
                                    <td className="p-3 text-primary">{e.label}</td>
                                    <td className="p-3 text-right">
                                        <NumInput value={ed.base_cost_inr ?? e.base_cost_inr} onChange={v => setEdit(e.key, "base_cost_inr", v)} tid={`price-base-${e.key}`}/>
                                    </td>
                                    <td className="p-3 text-right">
                                        <NumInput value={ed.markup_percent ?? e.markup_percent} onChange={v => setEdit(e.key, "markup_percent", v)} tid={`price-mk-${e.key}`}/>
                                    </td>
                                    <td className="p-3 text-right">
                                        <NumInput value={ed.fixed_addon_inr ?? e.fixed_addon_inr} onChange={v => setEdit(e.key, "fixed_addon_inr", v)} tid={`price-add-${e.key}`}/>
                                    </td>
                                    <td className="p-3 text-right font-mono text-primary">₹{e.billable ? final : "0.00"}</td>
                                    <td className="p-3 text-right">
                                        <button onClick={() => save(e.key)} disabled={!dirty}
                                            data-testid={`price-save-${e.key}`}
                                            className="px-2 py-1 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary hover:text-primary-foreground disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-primary">
                                            Save
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <TenantPricingPanel/>
        </div>
    );
}

// =============================================================================
// Per-tenant pricing — super_admin sets custom markup per firm
// =============================================================================
function TenantPricingPanel() {
    const [data, setData] = useState(null);
    const [search, setSearch] = useState("");
    const [sub, setSub] = useState("");
    const [expanded, setExpanded] = useState(null);   // tenant id whose row is open
    const [bulkMarkup, setBulkMarkup] = useState("");
    const [bulkEndpoint, setBulkEndpoint] = useState("*");
    const [selected, setSelected] = useState(new Set());

    const load = useCallback(async () => {
        try {
            const params = {};
            if (search.trim()) params.search = search.trim();
            if (sub) params.subscription = sub;
            const r = await api.get("/admin/ecourts/tenant-pricing", { params });
            setData(r.data);
        } catch (e) { toast.error(apiError(e)); }
    }, [search, sub]);
    useEffect(() => { load(); }, [load]);

    const subscriptions = useMemo(() => {
        if (!data) return [];
        return Array.from(new Set(data.tenants.map(t => t.subscription))).filter(Boolean).sort();
    }, [data]);

    const saveTenant = async (tid, endpoint_key, markup_percent, fixed_addon_inr) => {
        try {
            await api.put(`/admin/ecourts/tenants/${tid}/pricing`,
                { endpoint_key, markup_percent, fixed_addon_inr });
            toast.success("Tenant markup saved");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const clearTenantEndpoint = async (tid, endpoint_key) => {
        try {
            await api.put(`/admin/ecourts/tenants/${tid}/pricing`, { endpoint_key, clear: true });
            toast.success("Override removed · tenant now uses platform default");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };
    const runBulk = async () => {
        if (selected.size === 0) { toast.error("Select at least one tenant"); return; }
        if (bulkMarkup === "") { toast.error("Enter a markup % to apply"); return; }
        try {
            const r = await api.put("/admin/ecourts/tenant-pricing/bulk", {
                tenant_ids: [...selected], endpoint_key: bulkEndpoint,
                markup_percent: parseFloat(bulkMarkup),
            });
            toast.success(`Applied to ${r.data.modified} tenant(s) × ${r.data.endpoints} endpoint(s)`);
            setSelected(new Set()); setBulkMarkup("");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return <div className="text-xs text-muted-foreground mt-6">Loading per-tenant pricing…</div>;

    const endpoints = Object.entries(data.platform).filter(([, ep]) => ep.billable);

    return (
        <div className="border-t border-border pt-6 mt-8" data-testid="tenant-pricing-panel">
            <div className="overline text-primary mb-2">Per-tenant pricing</div>
            <p className="text-xs text-muted-foreground mb-4">
                Only super-admin controls these. Tenants cannot see or change their own markup.
                Empty / unset = tenant uses the platform default above.
            </p>

            {/* Filters */}
            <div className="flex flex-wrap items-end gap-3 mb-4">
                <input type="search" placeholder="Search tenants…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="tenant-pricing-search"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs w-64"/>
                <select value={sub} onChange={e => setSub(e.target.value)}
                    data-testid="tenant-pricing-sub-filter"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs">
                    <option value="">All subscriptions</option>
                    {subscriptions.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <div className="text-[10px] text-muted-foreground">{data.tenants.length} tenants shown</div>
            </div>

            {/* Bulk apply */}
            <div className="bg-card border border-border p-3 mb-4" data-testid="tenant-pricing-bulk">
                <div className="overline text-muted-foreground mb-2">Bulk apply markup</div>
                <div className="flex flex-wrap items-end gap-3">
                    <select value={bulkEndpoint} onChange={e => setBulkEndpoint(e.target.value)}
                        data-testid="tenant-pricing-bulk-endpoint"
                        className="bg-background border border-border outline-none px-3 py-2 text-xs">
                        <option value="*">All endpoints</option>
                        {endpoints.map(([k, ep]) => <option key={k} value={k}>{ep.label}</option>)}
                    </select>
                    <input type="number" placeholder="Markup % (e.g. 50)" value={bulkMarkup}
                        onChange={e => setBulkMarkup(e.target.value)}
                        data-testid="tenant-pricing-bulk-markup"
                        className="bg-background border border-border outline-none px-3 py-2 text-xs w-40"/>
                    <button onClick={runBulk}
                        data-testid="tenant-pricing-bulk-apply"
                        className="px-3 py-2 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium disabled:opacity-30"
                        disabled={selected.size === 0 || bulkMarkup === ""}>
                        Apply to {selected.size} tenant{selected.size === 1 ? "" : "s"}
                    </button>
                </div>
            </div>

            {/* Tenant list */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <th className="p-2 w-8 text-center">
                                <input type="checkbox" checked={selected.size > 0 && selected.size === data.tenants.length}
                                    onChange={() => setSelected(selected.size ? new Set() : new Set(data.tenants.map(t => t.id)))}
                                    data-testid="tenant-pricing-select-all" className="accent-primary"/>
                            </th>
                            <th className="text-left p-2">Tenant</th>
                            <th className="text-center p-2">Subscription</th>
                            <th className="text-right p-2">Overrides</th>
                            <th className="text-center p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.tenants.length === 0 ? (
                            <tr><td colSpan="5" className="text-center text-muted-foreground py-6 text-[10px]">No tenants match your filter</td></tr>
                        ) : data.tenants.map(t => {
                            const isOpen = expanded === t.id;
                            const overrideCount = Object.keys(t.pricing_overrides || {}).length;
                            return (
                                <React.Fragment key={t.id}>
                                <tr className="border-t border-border" data-testid={`tp-row-${t.id}`}>
                                    <td className="p-2 text-center">
                                        <input type="checkbox" checked={selected.has(t.id)}
                                            onChange={() => {
                                                const s = new Set(selected); s.has(t.id) ? s.delete(t.id) : s.add(t.id); setSelected(s);
                                            }}
                                            data-testid={`tp-select-${t.id}`} className="accent-primary"/>
                                    </td>
                                    <td className="p-2">
                                        <div className="text-foreground">{t.name}</div>
                                        <div className="text-[10px] text-muted-foreground">{t.slug}</div>
                                    </td>
                                    <td className="p-2 text-center">
                                        <span className="text-[10px] text-muted-foreground bg-background border border-border px-2 py-0.5 uppercase">{t.subscription}</span>
                                    </td>
                                    <td className="p-2 text-right font-mono">
                                        {overrideCount === 0
                                          ? <span className="text-muted-foreground text-[10px]">(platform default)</span>
                                          : <span className="text-emerald-400">{overrideCount} endpoint{overrideCount === 1 ? "" : "s"} overridden</span>}
                                    </td>
                                    <td className="p-2 text-center">
                                        <button onClick={() => setExpanded(isOpen ? null : t.id)}
                                            data-testid={`tp-expand-${t.id}`}
                                            className="text-[10px] text-primary hover:text-primary">
                                            {isOpen ? "Close" : "Edit"}
                                        </button>
                                    </td>
                                </tr>
                                {isOpen && (
                                    <tr className="bg-background">
                                        <td colSpan="5" className="p-3" data-testid={`tp-edit-${t.id}`}>
                                            <table className="w-full text-[10px]">
                                                <thead className="text-muted-foreground uppercase tracking-[0.16em]">
                                                    <tr><th className="text-left p-1">Endpoint</th><th className="text-right p-1">Base ₹</th><th className="text-right p-1">Platform mk %</th><th className="text-right p-1">Override mk %</th><th className="text-right p-1">Override addon ₹</th><th></th></tr>
                                                </thead>
                                                <tbody>
                                                    {endpoints.map(([k, ep]) => {
                                                        const ov = (t.pricing_overrides || {})[k];
                                                        return <TenantPricingRow key={k} ep={ep} ekey={k}
                                                            override={ov}
                                                            onSave={(mk, addon) => saveTenant(t.id, k, mk, addon)}
                                                            onClear={() => clearTenantEndpoint(t.id, k)}/>;
                                                    })}
                                                </tbody>
                                            </table>
                                        </td>
                                    </tr>
                                )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function TenantPricingRow({ ep, ekey, override, onSave, onClear }) {
    const [mk, setMk] = useState(override?.markup_percent ?? "");
    const [addon, setAddon] = useState(override?.fixed_addon_inr ?? "");
    useEffect(() => {
        setMk(override?.markup_percent ?? "");
        setAddon(override?.fixed_addon_inr ?? "");
    }, [override]);
    const has = override != null;
    return (
        <tr className="border-t border-card">
            <td className="p-1 text-foreground">{ep.label}</td>
            <td className="p-1 text-right font-mono text-muted-foreground">{ep.base_cost_inr.toFixed(2)}</td>
            <td className="p-1 text-right font-mono text-muted-foreground">{ep.markup_percent.toFixed(0)}%</td>
            <td className="p-1 text-right">
                <input type="number" value={mk} onChange={e => setMk(e.target.value)}
                    data-testid={`tp-row-mk-${ekey}`}
                    placeholder={`${ep.markup_percent}`}
                    className="bg-muted border border-border outline-none px-2 py-1 text-[10px] w-16 text-right font-mono"/>
            </td>
            <td className="p-1 text-right">
                <input type="number" value={addon} onChange={e => setAddon(e.target.value)}
                    data-testid={`tp-row-addon-${ekey}`}
                    placeholder="0"
                    className="bg-muted border border-border outline-none px-2 py-1 text-[10px] w-16 text-right font-mono"/>
            </td>
            <td className="p-1 text-right space-x-2">
                <button onClick={() => onSave(
                    mk === "" ? null : parseFloat(mk),
                    addon === "" ? null : parseFloat(addon)
                )} data-testid={`tp-row-save-${ekey}`}
                    className="text-[10px] text-primary hover:text-primary">Save</button>
                {has && <button onClick={onClear} data-testid={`tp-row-clear-${ekey}`}
                    className="text-[10px] text-muted-foreground hover:text-red-400">Reset</button>}
            </td>
        </tr>
    );
}

// ---------- Refresh Policy ----------
/**
 * StrategyExplainerCard — makes the "Option J · Ultra-Lean PAYG" logic
 * transparent to super admins. Renders:
 *   • Strategy header + primary/fallback endpoint rates
 *   • Platform daily/monthly totals (headline)
 *   • v2.17 Tier Distribution — T1/T2/T3/T4 case counts across the platform
 *   • Live per-tenant cost projection (past-due / hot / middle / far
 *     counts + ₹/day + ₹/month at the vendor PAYG rate)
 */
function StrategyExplainerCard({ strategy }) {
    const [expandTenants, setExpandTenants] = useState(false);
    const p = strategy.cost_projection || {};
    const primary = strategy.primary_endpoint || {};
    const fallback = strategy.fallback_endpoint || {};
    // Ranked descending so admins see the highest-cost tenants first
    const tenantsSorted = (p.tenants || []).slice().sort(
        (a, b) => (b.estimated_cost_inr_daily_payg || 0) - (a.estimated_cost_inr_daily_payg || 0));
    const topN = expandTenants ? tenantsSorted : tenantsSorted.slice(0, 5);

    return (
        <div className="bg-muted border border-border" data-testid="strategy-explainer">
            {/* Header */}
            <div className="p-4 border-b border-border flex flex-wrap items-center gap-4 justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-primary">Active refresh strategy</div>
                    <div className="text-lg font-semibold mt-0.5">{strategy.strategy_label}</div>
                </div>
                <div className="flex items-center gap-6 text-xs">
                    <div>
                        <div className="text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Primary endpoint</div>
                        <div className="font-mono">{primary.name} · ₹{primary.rate_payg_inr_per_cnr}/CNR PAYG</div>
                        <div className="text-muted-foreground">Chunk size {primary.chunk_size} · ₹{primary.rate_subscription_inr_per_cnr} on subscription</div>
                    </div>
                    <div>
                        <div className="text-muted-foreground uppercase tracking-[0.12em] text-[10px]">Fallback endpoint</div>
                        <div className="font-mono">{fallback.name} · ₹{fallback.rate_payg_inr_per_call}/call</div>
                        <div className="text-muted-foreground">{fallback.expected_frequency_pct}</div>
                    </div>
                </div>
            </div>

            {/* Platform totals — headline */}
            <div className="p-4 border-b border-border grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Platform daily</div>
                    <div className="text-2xl font-semibold mt-1">₹{p.platform_daily_estimate_inr ?? 0}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Platform monthly</div>
                    <div className="text-2xl font-semibold mt-1">₹{p.platform_monthly_estimate_inr ?? 0}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Batch rate</div>
                    <div className="text-2xl font-semibold mt-1 font-mono">₹{p.per_cnr_batch_inr}</div>
                </div>
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Fallback rate</div>
                    <div className="text-2xl font-semibold mt-1 font-mono">₹{p.per_cnr_fallback_inr}</div>
                </div>
            </div>

            {/* v2.17 · Tier Distribution — Phase A/B counts + costs at a glance */}
            <div className="border-b border-border">
                <div className="p-4 space-y-3">
                    <div className="text-xs uppercase tracking-[0.16em] text-primary">
                        Tier Distribution · v2.17 Phase A + B
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {[
                            { label: "T1 · Daily batch", key: "t1_daily_batch",
                              detail: "past-due + ≤3 days + unknown", accent: "text-red-400 border-red-500/40" },
                            { label: "T2 · Weekly batch", key: "t2_weekly_batch",
                              detail: "Sunday sweep · days 4-8", accent: "text-amber-300 border-amber-500/40" },
                            { label: "T3 · Weekly bulk_refresh", key: "t3_weekly_bulk_refresh",
                              detail: "Phase B · days >8", accent: "text-sky-300 border-sky-500/40" },
                            { label: "T4 · Past-due (subset)", key: "t4_past_due",
                              detail: "Included in T1 · needs attention", accent: "text-orange-300 border-orange-500/40" },
                        ].map(tier => {
                            const platformTotal = ((strategy.cost_projection && strategy.cost_projection.tenants) || []).reduce(
                                (s, t) => s + ((t.tiers || {})[tier.key] || 0), 0);
                            return (
                                <div key={tier.key} className={`border p-3 ${tier.accent}`}
                                    data-testid={`tier-summary-${tier.key}`}>
                                    <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                        {tier.label}
                                    </div>
                                    <div className="text-2xl font-mono mt-1">{platformTotal}</div>
                                    <div className="text-[10px] text-muted-foreground mt-1">{tier.detail}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="text-[11px] text-muted-foreground">
                        <strong>Cost model:</strong> T1 → <code>cnr_causelist_batch</code> ₹{strategy.primary_endpoint?.rate_payg_inr_per_cnr}/CNR daily · T2 → same endpoint amortised weekly · T3 → <code>bulk_refresh</code> ₹0.15/CNR amortised weekly. Fallback <code>case_detail</code> ₹{strategy.fallback_endpoint?.rate_payg_inr_per_call}/call hit on ~3% of T1 cases.
                    </div>
                </div>
            </div>

            {/* Per-tenant projection */}
            <div>
                <button className="w-full p-3 flex items-center justify-between text-xs uppercase tracking-[0.16em] text-primary hover:bg-background/40"
                    onClick={() => setExpandTenants(v => !v)}
                    data-testid="strategy-toggle-tenants">
                    <span>Per-tenant cost projection ({tenantsSorted.length} tenants)</span>
                    <span className="text-[10px]">{expandTenants ? "SHOW TOP 5" : "SHOW ALL"}</span>
                </button>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                        <thead className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground bg-background/30">
                            <tr>
                                <th className="text-left p-2">Tenant</th>
                                <th className="text-right p-2">Active CNRs</th>
                                <th className="text-right p-2 text-amber-500" title="T4 · Past-due (subset of T1)">Past-due</th>
                                <th className="text-right p-2 text-red-500" title="T1 · Daily batch">Hot ≤3d</th>
                                <th className="text-right p-2 text-amber-300" title="T2 · Weekly Sunday batch">Middle 4-8d</th>
                                <th className="text-right p-2 text-sky-300" title="T3 · Weekly bulk_refresh (Phase B)">Far &gt;8d</th>
                                <th className="text-right p-2">₹/day</th>
                                <th className="text-right p-2">₹/month</th>
                            </tr>
                        </thead>
                        <tbody>
                            {topN.map(t => (
                                <tr key={t.id} className="border-t border-border">
                                    <td className="p-2 truncate max-w-[220px]" title={t.name}>{t.name}</td>
                                    <td className="p-2 text-right font-mono">{t.total_active_cnrs}</td>
                                    <td className="p-2 text-right font-mono">{t.past_due}</td>
                                    <td className="p-2 text-right font-mono">{t.hot_next_3d}</td>
                                    <td className="p-2 text-right font-mono">{t.middle_days_4_8}</td>
                                    <td className="p-2 text-right font-mono">{t.far_beyond_8d}</td>
                                    <td className="p-2 text-right font-mono font-medium">₹{t.estimated_cost_inr_daily_payg}</td>
                                    <td className="p-2 text-right font-mono text-muted-foreground">₹{t.estimated_cost_inr_monthly_payg}</td>
                                </tr>
                            ))}
                            {topN.length === 0 && (
                                <tr><td colSpan={8} className="p-3 text-center text-muted-foreground">No tenants with active CNRs</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="p-3 text-[11px] text-muted-foreground border-t border-border">
                    Costs are PAYG estimates using vendor-published rates. Fallback assumes {p.assumed_fallback_pct}% of daily-scan cases hit case_detail (postponement detection). Past-due cases dominate the initial cost; steady-state drops sharply once the past-due queue catches up.
                </div>
            </div>
        </div>
    );
}

function RefreshTab() {
    const [policy, setPolicy] = useState(null);
    const [edited, setEdited] = useState(null);
    const [strategy, setStrategy] = useState(null);
    const load = useCallback(async () => {
        try {
            // Load policy IMMEDIATELY so the tab is interactive within ~200ms.
            // The strategy projector is O(all tenants) and can take 20-30s on
            // large deployments — fire it in the background and populate the
            // strategy card when ready. Users can edit + save policy meanwhile.
            const r = await api.get("/admin/ecourts/refresh-policy");
            setPolicy(r.data); setEdited(r.data);
            api.get("/admin/ecourts/refresh-strategy")
                .then(s => setStrategy(s.data))
                .catch(() => setStrategy(null));
        }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            await api.patch("/admin/ecourts/refresh-policy", {
                enabled: edited.enabled,
                mode: edited.mode,
                interval_minutes: edited.interval_minutes,
                schedule_times_ist: edited.schedule_times_ist,
                platform_cap_per_tenant: edited.platform_cap_per_tenant,
                eager_fetch_days: edited.eager_fetch_days ?? 7,
            });
            toast.success("Refresh policy saved");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const dirty = useMemo(() => policy && edited && JSON.stringify(policy) !== JSON.stringify(edited), [policy, edited]);
    if (!edited) return <div className="text-sm text-muted-foreground">Loading…</div>;

    // 12-hour AM/PM time helpers for the schedule-mode picker
    const to12h = (hhmm) => {
        if (!hhmm) return "";
        const [h, m] = hhmm.split(":").map(n => parseInt(n));
        const period = h >= 12 ? "PM" : "AM";
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${period}`;
    };
    const addScheduleTime = () => setEdited({ ...edited, schedule_times_ist: [...(edited.schedule_times_ist || []), "12:00"] });
    const updateScheduleTime = (i, v) => setEdited({ ...edited, schedule_times_ist: edited.schedule_times_ist.map((t, j) => j === i ? v : t) });
    const removeScheduleTime = (i) => setEdited({ ...edited, schedule_times_ist: edited.schedule_times_ist.filter((_, j) => j !== i) });

    return (
        <div className="space-y-4 max-w-3xl">
            {strategy && <StrategyExplainerCard strategy={strategy}/>}
            <div className="bg-muted border border-border p-4 flex items-center justify-between">
                <div>
                    <div className="text-xs text-primary uppercase tracking-[0.16em]">Auto-refresh cron</div>
                    <div className="text-xs text-muted-foreground mt-1">When OFF, nightly bulk-refresh is paused (CNR lookups still work).</div>
                </div>
                <Toggle on={edited.enabled} onChange={v => setEdited({ ...edited, enabled: v })} testId="refresh-cron-toggle"/>
            </div>

            {/* Schedule mode controls */}
            <div className="bg-muted border border-border p-4">
                <div className="text-xs text-primary uppercase tracking-[0.16em] mb-3">Cron schedule</div>
                <div className="flex items-center gap-3 mb-4">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="radio" name="cron-mode" checked={edited.mode === "interval"}
                            onChange={() => setEdited({ ...edited, mode: "interval" })}
                            className="accent-primary" data-testid="cron-mode-interval"/>
                        Interval (wake every N minutes)
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="radio" name="cron-mode" checked={edited.mode === "schedule"}
                            onChange={() => setEdited({ ...edited, mode: "schedule" })}
                            className="accent-primary" data-testid="cron-mode-schedule"/>
                        Schedule (specific clock times in IST)
                    </label>
                </div>

                {edited.mode === "interval" ? (
                    <div className="flex items-end gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">Run every (minutes)</label>
                            <NumInput value={edited.interval_minutes} onChange={v => setEdited({ ...edited, interval_minutes: parseInt(v) || 10 })} tid="cron-interval-minutes"/>
                        </div>
                        <div className="text-xs text-muted-foreground pb-2">
                            Range: 1–1440 (1 min … 24 hrs). Default 10 min.
                            <br/>The cron wakes up every minute internally and runs a pass when the interval elapses.
                            <br/>API calls only happen for cases that are DUE per their bucket cadence — most ticks make 0 calls.
                        </div>
                    </div>
                ) : (
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 block">Run only at these IST times</label>
                        <div className="space-y-2" data-testid="schedule-times-list">
                            {(edited.schedule_times_ist || []).map((t, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input type="time" value={t}
                                        onChange={e => updateScheduleTime(i, e.target.value)}
                                        data-testid={`schedule-time-${i}`}
                                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono"/>
                                    <span className="text-[10px] text-muted-foreground w-20">{to12h(t)}</span>
                                    <button type="button" onClick={() => removeScheduleTime(i)}
                                        data-testid={`schedule-time-remove-${i}`}
                                        className="text-muted-foreground hover:text-red-400 text-xs">✕</button>
                                </div>
                            ))}
                            <button type="button" onClick={addScheduleTime}
                                data-testid="schedule-time-add"
                                className="text-xs text-primary hover:text-primary mt-2">+ Add time</button>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-3">
                            All times in IST. The cron checks once a minute and runs at the next matching slot (±2 min tolerance).
                        </div>
                    </div>
                )}

                <div className="mt-4 pt-4 border-t border-border flex items-end gap-3 flex-wrap">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">Platform default · cap per tenant per tick</label>
                        <NumInput value={edited.platform_cap_per_tenant} onChange={v => setEdited({ ...edited, platform_cap_per_tenant: parseInt(v) || 50 })} tid="cron-platform-cap"/>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block flex items-center gap-1.5">
                            <span className="text-amber-400">⚡</span>Eager-fetch days (cost knob)
                        </label>
                        <NumInput value={edited.eager_fetch_days ?? 7} onChange={v => setEdited({ ...edited, eager_fetch_days: parseInt(v) || 0 })} tid="cron-eager-fetch-days"/>
                    </div>
                    <div className="text-xs text-muted-foreground pb-2 max-w-md">
                        <div><b>Eager-fetch days</b>: Cases with a hearing within this many days use case_detail (1.5 cr, fresh data + push diff). Cases beyond it use bulk_refresh (~0.5 cr/CNR, no immediate push). Lower = cheaper, fewer pushes for distant hearings. Tenants can override individually below.</div>
                    </div>
                </div>
            </div>

            <div className="overflow-x-auto">
                <div className="p-4 text-[11px] text-muted-foreground bg-background/20 border border-border">
                    <span className="text-primary font-medium">v2.17 auto-tier · </span>
                    Refresh cadence is now derived from each case&apos;s next-hearing date automatically:
                    daily <code>cnr_causelist_batch</code> for ≤3 days &amp; past-due (T1),
                    Sunday sweep for 4-8 days (T2),
                    weekly <code>bulk_refresh</code> for &gt;8 days (T3).
                    No manual bucket-tuning needed — see Tier Distribution above for live counts.
                </div>
            </div>

            <button onClick={save} disabled={!dirty}
                data-testid="refresh-save"
                className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-30">
                Save refresh policy
            </button>

            <div className="border-t border-border pt-4 mt-6">
                <div className="overline text-primary mb-2">Manual trigger</div>
                <div className="text-xs text-muted-foreground mb-3 space-y-1">
                    <div>v2.19.4 — the legacy tiered-freshness cron was <strong>PURGED</strong>. Use the four Hearing-Anchored sweep buttons on the <strong>Strategy</strong> tab (Sunday · T+3 · T-1 · Monthly stale) instead. This panel remains for the &ldquo;Refresh All Tenants&rdquo; utility below.</div>
                </div>
                <RefreshAllTenantsButton/>
            </div>

            <CauselistHealthPanel/>
            <RecentTicksPanel/>

            <TenantOverridesPanel platformCap={edited.platform_cap_per_tenant} platformEagerDays={edited.eager_fetch_days ?? 7}/>
        </div>
    );
}

// ============================================================================
// Recent ticks history
// ============================================================================
function RefreshAllTenantsButton() {
    const [open, setOpen] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [throttle, setThrottle] = useState(0);   // seconds between bulk_refresh chunks
    // v2.15.3 — case-status allow-list. Default active+open to keep costs sane.
    const [statuses, setStatuses] = useState(["active", "open"]);
    const AVAILABLE_STATUSES = ["active", "open", "disposed", "closed", "withdrawn"];

    const fetchEstimate = async (nextStatuses) => {
        setBusy(true);
        try {
            const list = (nextStatuses ?? statuses);
            const qs = list.length ? list.join(",") : "all";
            const r = await api.get(`/admin/ecourts/refresh-all-tenants/estimate?statuses=${encodeURIComponent(qs)}`);
            setEstimate(r.data);
            setOpen(true);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const toggleStatus = async (s) => {
        const next = statuses.includes(s)
            ? statuses.filter(x => x !== s)
            : [...statuses, s];
        setStatuses(next);
        await fetchEstimate(next);
    };

    const trigger = async () => {
        if (!estimate) return;
        setBusy(true);
        setResult(null);
        try {
            // v2.15.4 — Background-job pattern: POST returns immediately with
            // a job_id; we poll for progress every 2s until completed/failed.
            // No more axios/ingress 60s timeouts on large refreshes.
            const start = await api.post("/admin/ecourts/refresh-all-tenants",
                { confirm: true, fire_push: true, throttle_seconds: throttle,
                  statuses: statuses.length ? statuses.join(",") : "all" });
            const jobId = start.data.job_id;
            toast.info(`Job queued (${jobId.slice(-6)}) — refreshing in background…`);
            let done = false;
            while (!done) {
                await new Promise(r => setTimeout(r, 2000));
                let js;
                try {
                    js = (await api.get(`/admin/ecourts/refresh-all-tenants/status/${jobId}`)).data;
                } catch (e) { continue; }
                setResult({
                    _running: js.status !== "completed" && js.status !== "failed",
                    status: js.status,
                    progress: js.progress,
                    ...(js.result || {}),
                    error: js.error,
                });
                if (js.status === "completed" || js.status === "failed") done = true;
                if (js.status === "failed") {
                    toast.error(`Refresh job failed: ${js.error || "unknown error"}`);
                } else if (js.status === "completed") {
                    const r = js.result || {};
                    toast.success(
                        `Refreshed ${r.cases_refreshed} cases across ${r.tenants_refreshed} tenants · ${r.pushes_fired} pushes fired`);
                }
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button type="button" onClick={fetchEstimate} disabled={busy}
                data-testid="admin-refresh-all-tenants-btn"
                className="inline-flex items-center gap-2 px-4 py-2 border border-amber-500/60 text-amber-300 text-xs uppercase tracking-[0.16em] hover:bg-amber-500/10 disabled:opacity-50">
                <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5}/>
                {busy && !open ? "Loading…" : "Refresh ALL tenants"}
            </button>
            {open && estimate && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                     onClick={() => !busy && setOpen(false)}>
                    <div className="bg-card border border-amber-500/30 max-w-2xl w-full p-6 space-y-4"
                         onClick={e => e.stopPropagation()}
                         data-testid="admin-refresh-all-modal">
                        <div className="flex items-start justify-between gap-4">
                            <div>
                                <h3 className="text-foreground text-lg flex items-center gap-2">
                                    <span className="text-amber-400">⚡</span>
                                    Platform-wide bulk refresh
                                </h3>
                                <div className="text-muted-foreground text-xs mt-1">
                                    Queues a fresh scrape at eCourts for EVERY case across ALL enabled tenants. Push notifications fire per case. Wallet must cover the spend per tenant.
                                </div>
                            </div>
                            <button onClick={() => !busy && setOpen(false)} disabled={busy}
                                className="text-muted-foreground hover:text-foreground text-xs disabled:opacity-50">✕</button>
                        </div>
                        <div className="grid grid-cols-3 gap-3 text-center">
                            <Kpi label="Tenants" value={estimate.tenants_in_scope}/>
                            <Kpi label="Total CNRs" value={estimate.total_cnrs}/>
                            <Kpi label="Est. ₹ total" value={`₹${estimate.estimated_total_inr.toFixed(2)}`} tone="amber"/>
                        </div>
                        <div className="text-[10px] text-muted-foreground">
                            {estimate.chunks} bulk_refresh HTTP call(s) at ₹{estimate.per_cnr_inr.toFixed(2)}/CNR.
                        </div>

                        {/* v2.15.3 — Case-status filter */}
                        <div className="border border-border p-3 space-y-2" data-testid="admin-refresh-all-status-filter">
                            <div className="flex items-baseline justify-between gap-3">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Case status filter</div>
                                    <div className="text-[11px] text-muted-foreground mt-0.5">
                                        Only refresh cases whose <code className="text-foreground">status</code> matches one of the selected values. Leave empty (or click <em className="not-italic">All</em>) to refresh every case regardless of status.
                                    </div>
                                </div>
                                <button type="button"
                                    onClick={() => { setStatuses(AVAILABLE_STATUSES); fetchEstimate(AVAILABLE_STATUSES); }}
                                    disabled={busy}
                                    data-testid="admin-refresh-all-status-all"
                                    className="text-[10px] uppercase tracking-[0.12em] px-2 py-1 border border-border text-muted-foreground hover:border-amber-400 hover:text-amber-300 disabled:opacity-50">
                                    All
                                </button>
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {AVAILABLE_STATUSES.map(s => {
                                    const on = statuses.includes(s);
                                    return (
                                        <button key={s} type="button"
                                            onClick={() => toggleStatus(s)}
                                            disabled={busy}
                                            data-testid={`admin-refresh-all-status-${s}`}
                                            className={`text-[10px] uppercase tracking-[0.12em] px-2.5 py-1 border disabled:opacity-50 ${
                                                on
                                                    ? "border-amber-400 text-amber-300 bg-amber-500/10"
                                                    : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-300"
                                            }`}>
                                            {on ? "✓ " : ""}{s}
                                        </button>
                                    );
                                })}
                            </div>
                            {statuses.length === 0 && (
                                <div className="text-[10px] text-rose-400">
                                    No statuses selected → will refresh EVERY case (including disposed/closed).
                                </div>
                            )}
                        </div>

                        {estimate.total_cnrs > 0 && (
                            <div className="border border-border p-3 space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div>
                                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Throttle between chunks</div>
                                        <div className="text-[11px] text-muted-foreground mt-0.5">
                                            Pace consecutive bulk_refresh calls to avoid eCourts rate-limit. Recommended for &gt; 5k CNRs.
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-foreground text-lg font-mono leading-none">
                                            {throttle === 0 ? "OFF" : `${throttle}s`}
                                        </div>
                                        <div className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">delay</div>
                                    </div>
                                </div>
                                <input
                                    type="range" min="0" max="30" step="1"
                                    value={throttle}
                                    onChange={e => setThrottle(Number(e.target.value))}
                                    disabled={busy}
                                    data-testid="admin-refresh-all-throttle"
                                    className="w-full accent-amber-400 disabled:opacity-50"/>
                                <div className="flex flex-wrap gap-1.5">
                                    {[
                                        { label: "Off", v: 0 },
                                        { label: "2s", v: 2 },
                                        { label: "5s · recommended", v: 5 },
                                        { label: "10s", v: 10 },
                                    ].map(p => (
                                        <button key={p.v} type="button"
                                            onClick={() => setThrottle(p.v)}
                                            disabled={busy}
                                            data-testid={`admin-refresh-all-throttle-preset-${p.v}`}
                                            className={`text-[10px] uppercase tracking-[0.12em] px-2 py-1 border disabled:opacity-50 ${throttle === p.v ? "border-amber-400 text-amber-300 bg-amber-500/10" : "border-border text-muted-foreground hover:border-amber-400 hover:text-amber-300"}`}>
                                            {p.label}
                                        </button>
                                    ))}
                                </div>
                                {throttle > 0 && estimate.chunks > 1 && (
                                    <div className="text-[10px] text-amber-300/80 font-mono">
                                        ETA ~ {Math.ceil((estimate.chunks - 1) * throttle)}s (sleep) + API time
                                    </div>
                                )}
                            </div>
                        )}
                        {estimate.per_tenant?.length > 0 && (
                            <div className="border border-border max-h-48 overflow-y-auto">
                                <table className="w-full text-xs">
                                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em] sticky top-0">
                                        <tr>
                                            <th className="text-left p-2">Tenant</th>
                                            <th className="text-right p-2">CNRs</th>
                                            <th className="text-right p-2">Est. ₹</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {estimate.per_tenant.map(t => (
                                            <tr key={t.id} className="border-t border-border">
                                                <td className="p-2 text-foreground">{t.name}</td>
                                                <td className="p-2 text-right font-mono">{t.cnrs}</td>
                                                <td className="p-2 text-right font-mono text-amber-300">₹{t.estimated_inr.toFixed(2)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {result && (
                            <div className={`border p-3 text-xs space-y-1 ${
                                    result._running ? "border-amber-500/30 bg-amber-500/[0.04]" :
                                    result.status === "failed" ? "border-red-500/40 bg-red-500/[0.04]" :
                                    "border-emerald-500/30 bg-emerald-500/[0.04]"}`}
                                 data-testid="admin-refresh-all-result">
                                <div className={result._running ? "text-amber-300" : result.status === "failed" ? "text-red-300" : "text-emerald-300"}>
                                    {result._running ? (
                                        <span className="inline-flex items-center gap-2">
                                            <span className="inline-block w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"/>
                                            Running — {result.progress?.current_tenant || "…"}
                                        </span>
                                    ) : result.status === "failed" ? "Failed" : "Completed"}
                                </div>
                                <div className="text-muted-foreground">
                                    {result._running
                                        ? `${result.progress?.cases_refreshed || 0} cases refreshed so far · ${result.progress?.tenants_processed || 0} tenants processed · ${result.progress?.pushes_fired || 0} pushes fired`
                                        : `${result.cases_refreshed || 0} cases refreshed · ${result.tenants_refreshed || 0} tenants · ${result.pushes_fired || 0} pushes fired`
                                    }
                                </div>
                                {result.error && (
                                    <div className="text-red-300 font-mono text-[10px]">{result.error}</div>
                                )}
                                {result.skipped_no_wallet?.length > 0 && (
                                    <div className="text-amber-300">
                                        {result.skipped_no_wallet.length} tenant(s) skipped — wallet underflow
                                    </div>
                                )}
                                {result.failed_tenants?.length > 0 && (
                                    <div className="text-red-300">
                                        {result.failed_tenants.length} chunk(s) failed
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
                            <button onClick={() => setOpen(false)} disabled={busy && result?._running}
                                className="text-[10px] uppercase tracking-[0.16em] px-4 py-2 border border-border text-muted-foreground hover:text-foreground disabled:opacity-50">
                                {result && !result._running ? "Close" : "Cancel"}
                            </button>
                            {(!result || result._running) && (
                                <button onClick={trigger} disabled={busy || estimate.total_cnrs === 0}
                                    data-testid="admin-refresh-all-confirm"
                                    className="text-[10px] uppercase tracking-[0.16em] px-4 py-2 border border-amber-500/60 text-amber-300 hover:bg-amber-500/[0.08] disabled:opacity-50">
                                    {busy ? (result?._running ? "Running in background…" : "Queueing…") : "Confirm — start bulk refresh"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

function Kpi({ label, value, tone = "default" }) {
    const cls = tone === "amber" ? "text-amber-300" : "text-foreground";
    return (
        <div className="border border-border p-3">
            <div className={`text-2xl font-mono ${cls}`}>{value}</div>
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-1">{label}</div>
        </div>
    );
}


// ============================================================================
// v2.13 · Per-tenant Cause-list Health panel
// Shows the outcome of the LAST cause-list tick for every tenant — including
// skip reasons (previously invisible), hits, calls, and last-run age. Admin
// can spot mis-configured tenants BEFORE the tenant notices a missed hearing.
// ============================================================================
function CauselistHealthPanel() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("all");   // all | skipped | ran
    const [search, setSearch] = useState("");
    const reload = useCallback(async () => {
        try { const r = await api.get("/admin/ecourts/causelist/last-runs?limit=200"); setRows(r.data || []); }
        catch (e) { console.warn("causelist/last-runs load failed", e); }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const filtered = rows.filter(r => {
        if (filter === "skipped" && r.last_status !== "skipped") return false;
        if (filter === "ran" && r.last_status !== "ran" && r.last_status !== "batch_only") return false;
        if (search) {
            const s = search.toLowerCase();
            return (r.tenant_name || "").toLowerCase().includes(s)
                || (r.last_skip_reason || "").toLowerCase().includes(s);
        }
        return true;
    });

    const counts = rows.reduce((acc, r) => {
        acc.total++;
        if (r.last_status === "skipped") acc.skipped++;
        else if (r.last_status === "ran" || r.last_status === "batch_only") acc.ran++;
        return acc;
    }, { total: 0, ran: 0, skipped: 0 });

    if (!rows.length) return null;
    return (
        <div className="border-t border-border pt-4 mt-6" data-testid="causelist-health-panel">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div>
                    <div className="overline text-primary">Cause-list health · per tenant</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                        Last outcome for every tenant. Skipped rows show WHY — mis-configurations become visible without waiting for a missed-hearing complaint.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] uppercase tracking-[0.15em] text-emerald-400">{counts.ran} ran</span>
                    <span className="text-[10px] uppercase tracking-[0.15em] text-amber-400">{counts.skipped} skipped</span>
                    <button onClick={reload} data-testid="causelist-health-reload"
                        className="text-[10px] text-muted-foreground hover:text-foreground">↻ Refresh</button>
                </div>
            </div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
                {[
                    ["all",     "All"],
                    ["ran",     "Ran"],
                    ["skipped", "Skipped"],
                ].map(([k, label]) => (
                    <button key={k} onClick={() => setFilter(k)} data-testid={`causelist-health-filter-${k}`}
                        className={`text-[10px] uppercase tracking-[0.15em] px-2.5 py-1 border transition-colors ${filter === k ? "border-primary text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}>
                        {label}
                    </button>
                ))}
                <input type="text" value={search} onChange={e => setSearch(e.target.value)}
                    placeholder="Search tenant or reason…" data-testid="causelist-health-search"
                    className="flex-1 min-w-[180px] bg-background border border-border focus:border-primary outline-none px-3 py-1.5 text-xs"/>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border" data-testid="causelist-health-table">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <th className="text-left p-2">Tenant</th>
                            <th className="text-center p-2">Status</th>
                            <th className="text-left p-2">Skip reason / details</th>
                            <th className="text-right p-2">Hits</th>
                            <th className="text-right p-2">Calls</th>
                            <th className="text-right p-2">Last ran</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map(r => {
                            const status = r.last_status || (r.last_run_at ? "ran" : "—");
                            const badgeClass =
                                status === "skipped" ? "bg-amber-500/15 text-amber-400 border-amber-500/40" :
                                status === "batch_only" ? "bg-blue-500/15 text-blue-400 border-blue-500/40" :
                                status === "ran" ? "bg-emerald-500/15 text-emerald-400 border-emerald-500/40" :
                                "bg-muted text-muted-foreground border-border";
                            const reason = r.last_skip_reason
                                || (r.last_status === "batch_only" ? `batch-only · ${r.last_outcome?.batch_hits ?? 0} hits` : "")
                                || (r.last_status === "ran" ? `${r.last_outcome?.dates_scanned ?? 0} dates × ${r.last_outcome?.cases_scanned ?? 0} cases` : "");
                            return (
                                <tr key={r.id} className="border-t border-border hover:bg-muted/40">
                                    <td className="p-2 text-foreground">{r.tenant_name || r.tenant_id}</td>
                                    <td className="p-2 text-center">
                                        <span className={`text-[9px] uppercase tracking-[0.15em] px-2 py-0.5 border ${badgeClass}`}>{status}</span>
                                    </td>
                                    <td className="p-2 text-muted-foreground text-[11px] max-w-md truncate" title={reason}>{reason || "—"}</td>
                                    <td className={`p-2 text-right font-mono ${r.last_hits > 0 ? "text-emerald-400" : "text-muted-foreground"}`}>{r.last_hits ?? 0}</td>
                                    <td className="p-2 text-right font-mono text-muted-foreground">{r.last_calls ?? 0}</td>
                                    <td className="p-2 text-right text-muted-foreground font-mono">{r.last_run_at ? new Date(r.last_run_at).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" }) : "—"}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}


function RecentTicksPanel() {
    const [rows, setRows] = useState([]);
    const reload = useCallback(async () => {
        try { const r = await api.get("/admin/ecourts/recent-ticks?limit=10"); setRows(r.data || []); }
        catch (e) { console.warn("recent-ticks load failed", e); }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    if (!rows.length) return null;
    return (
        <div className="border-t border-border pt-4 mt-6">
            <div className="flex items-center justify-between mb-3">
                <div className="overline text-primary">Recent ticks</div>
                <button onClick={reload} className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="recent-ticks-reload">↻ Refresh</button>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border" data-testid="recent-ticks-table">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <th className="text-left p-2">Ran at (IST)</th>
                            <th className="text-right p-2">Tenants</th>
                            <th className="text-right p-2">Refreshed</th>
                            <th className="text-right p-2">Duration</th>
                            <th className="text-center p-2">Mode</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((t, i) => (
                            <tr key={t.ran_at || i} className="border-t border-border">
                                <td className="p-2 font-mono text-muted-foreground">{t.ran_at ? new Date(t.ran_at).toLocaleString("en-IN", {timeZone:"Asia/Kolkata"}) : "—"}</td>
                                <td className="p-2 text-right">{t.tenants_processed ?? "—"}</td>
                                <td className={`p-2 text-right font-mono ${t.cases_refreshed > 0 ? "text-emerald-400" : "text-muted-foreground"}`}>{t.cases_refreshed ?? 0}</td>
                                <td className="p-2 text-right text-muted-foreground">{t.duration_seconds != null ? `${t.duration_seconds}s` : "—"}</td>
                                <td className="p-2 text-center">
                                    {t.skipped ? <span className="text-amber-400 text-[10px]">SKIPPED · {t.reason || ""}</span>
                                       : t.manual ? <span className="text-primary text-[10px]">MANUAL</span>
                                       : <span className="text-muted-foreground text-[10px]">AUTO</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ============================================================================
// Per-tenant "Refresh ALL" — super-admin manual Option-J bulk refresh
// scoped to a single tenant. Preview → confirm → fires the same shared
// helper the tenant-facing button uses.
// ============================================================================
function PerTenantRefreshAllButton({ tenantId, tenantName }) {
    const [open, setOpen] = useState(false);
    const [estimate, setEstimate] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);

    const fetchEstimate = async () => {
        setBusy(true);
        setResult(null);
        try {
            const r = await api.get(`/admin/ecourts/tenants/${tenantId}/refresh-all/estimate`);
            setEstimate(r.data);
            setOpen(true);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const trigger = async () => {
        if (!estimate) return;
        setBusy(true);
        try {
            const r = await api.post(`/admin/ecourts/tenants/${tenantId}/refresh-all`,
                { confirm: true });
            setResult(r.data);
            toast.success(
                `Refreshed ${r.data.refreshed} CNRs · ${r.data.date_updated} dates updated · ₹${r.data.total_cost_inr}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <>
            <button type="button" onClick={fetchEstimate} disabled={busy}
                data-testid={`tenant-refresh-all-btn-${tenantId}`}
                className="text-[10px] text-emerald-300 hover:text-emerald-200 disabled:opacity-50">
                {busy && !open ? "…" : "Refresh all"}
            </button>
            {open && estimate && (
                <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
                    onClick={() => !busy && setOpen(false)}
                    data-testid={`tenant-refresh-all-modal-${tenantId}`}>
                    <div className="bg-background border border-border max-w-md w-full p-6"
                        onClick={e => e.stopPropagation()}>
                        <div className="text-lg text-foreground mb-1">
                            Refresh all cases · {estimate.tenant?.name || tenantName}
                        </div>
                        <div className="text-xs text-muted-foreground mb-4">
                            Option J: cnr_causelist_batch (₹{estimate.per_cnr_batch_inr}/CNR) with bounded case_detail fallback (₹{estimate.per_case_detail_inr}/call) for past-due hearings.
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-xs mb-4">
                            <div className="border border-border p-3">
                                <div className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">Synced cases</div>
                                <div className="text-2xl text-foreground font-mono mt-1"
                                    data-testid={`tenant-refresh-all-cases-${tenantId}`}>{estimate.synced_cases}</div>
                            </div>
                            <div className="border border-border p-3">
                                <div className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">Past-due</div>
                                <div className="text-2xl text-amber-300 font-mono mt-1">{estimate.past_due_cases}</div>
                            </div>
                            <div className="border border-border p-3">
                                <div className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">Batch cost</div>
                                <div className="text-lg text-foreground font-mono mt-1">₹{estimate.batch_cost_inr}</div>
                                <div className="text-[10px] text-muted-foreground">{estimate.chunks} chunk(s)</div>
                            </div>
                            <div className="border border-border p-3">
                                <div className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">Est. fallback</div>
                                <div className="text-lg text-foreground font-mono mt-1">₹{estimate.estimated_fallback_cost_inr}</div>
                                <div className="text-[10px] text-muted-foreground">{estimate.estimated_fallback_calls} call(s)</div>
                            </div>
                            <div className={`border p-3 col-span-2 ${estimate.wallet_sufficient ? "border-emerald-500/50" : "border-red-500/60"}`}>
                                <div className="text-muted-foreground uppercase tracking-[0.14em] text-[10px]">
                                    Estimated total · Wallet ₹{estimate.wallet_balance_inr}
                                </div>
                                <div className={`text-2xl font-mono mt-1 ${estimate.wallet_sufficient ? "text-emerald-300" : "text-red-400"}`}
                                    data-testid={`tenant-refresh-all-total-${tenantId}`}>
                                    ₹{estimate.estimated_total_inr}
                                </div>
                                {!estimate.wallet_sufficient && (
                                    <div className="text-[10px] text-red-400 mt-1">
                                        Tenant wallet balance is below the estimated cost.
                                        The call will still be attempted — expect partial completion.
                                    </div>
                                )}
                            </div>
                        </div>
                        {result && (
                            <div className="border border-emerald-500/50 p-3 mb-4 text-xs" data-testid={`tenant-refresh-all-result-${tenantId}`}>
                                <div className="text-emerald-300 uppercase tracking-[0.14em] text-[10px] mb-1">Done</div>
                                <div>Refreshed {result.refreshed} CNRs · {result.date_updated} dates updated · fallbacks {result.fallback_used}/{result.fallback_used + result.fallback_failed} · ₹{result.total_cost_inr}</div>
                                {result.last_error && <div className="text-amber-300 mt-1">Last chunk error: {result.last_error}</div>}
                            </div>
                        )}
                        <div className="flex items-center justify-end gap-2 text-xs">
                            <button type="button" onClick={() => setOpen(false)} disabled={busy}
                                data-testid={`tenant-refresh-all-cancel-${tenantId}`}
                                className="px-3 py-2 border border-border text-muted-foreground hover:text-foreground disabled:opacity-50">
                                Close
                            </button>
                            <button type="button" onClick={trigger} disabled={busy || result}
                                data-testid={`tenant-refresh-all-confirm-${tenantId}`}
                                className="px-4 py-2 border border-emerald-500/60 text-emerald-300 uppercase tracking-[0.14em] hover:bg-emerald-500/10 disabled:opacity-50">
                                {busy ? "Working…" : result ? "Done" : "Fire refresh"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

// ============================================================================
// Per-tenant overrides (enabled toggle + cap override + match-mode override)
// ============================================================================
function TenantOverridesPanel({ platformCap, platformEagerDays = 7 }) {
    const [tenants, setTenants] = useState([]);
    const [search, setSearch] = useState("");
    const reload = useCallback(async () => {
        try { const r = await api.get("/admin/ecourts/tenants-summary"); setTenants(r.data || []); }
        catch (e) { toast.error(apiError(e)); }
    }, []);
    useEffect(() => { reload(); }, [reload]);

    const updateTenant = async (id, payload) => {
        try {
            await api.put(`/admin/ecourts/tenants/${id}/refresh-config`, payload);
            toast.success("Tenant config saved");
            await reload();
        } catch (e) { toast.error(apiError(e)); }
    };

    const filtered = tenants.filter(t => {
        if (!search.trim()) return true;
        const q = search.toLowerCase().trim();
        return (t.name || "").toLowerCase().includes(q) ||
                (t.slug || "").toLowerCase().includes(q);
    });

    if (!tenants.length) return null;
    return (
        <div className="border-t border-border pt-4 mt-6" data-testid="tenant-overrides-panel">
            <div className="flex items-center justify-between mb-2">
                <div className="overline text-primary">Per-tenant overrides</div>
                <input type="search" placeholder="Search tenants…" value={search}
                    onChange={e => setSearch(e.target.value)}
                    data-testid="tenant-overrides-search"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-1.5 text-xs w-56"/>
            </div>
            <div className="text-xs text-muted-foreground mb-3">
                Disable eCourts entirely for a specific tenant · override their per-tick cap · override match-mode. Blank cap/mode = use platform default.
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <th className="text-left p-2">Tenant</th>
                            <th className="text-center p-2">eCourts enabled</th>
                            <th className="text-right p-2">Cap override</th>
                            <th className="text-right p-2">⚡ Eager days</th>
                            <th className="text-center p-2">Match mode override</th>
                            <th className="text-center p-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 ? (
                            <tr><td colSpan="6" className="text-center text-muted-foreground py-6 text-[10px]" data-testid="tenant-overrides-empty">No tenants match &quot;{search}&quot;</td></tr>
                        ) : filtered.map(t => (
                            <tr key={t.id} className="border-t border-border" data-testid={`tenant-row-${t.id}`}>
                                <td className="p-2">
                                    <div className="text-foreground">{t.name}</div>
                                    <div className="text-[10px] text-muted-foreground">{t.slug}</div>
                                </td>
                                <td className="p-2 text-center">
                                    <Toggle on={t.enabled} onChange={v => updateTenant(t.id, { enabled: v })} testId={`tenant-enabled-${t.id}`}/>
                                </td>
                                <td className="p-2 text-right">
                                    <span className={t.cap_per_tick_override != null ? "text-foreground font-mono" : "text-muted-foreground text-[10px]"}>
                                        {t.cap_per_tick_override ?? `(default ${platformCap})`}
                                    </span>
                                </td>
                                <td className="p-2 text-right">
                                    <span className={t.eager_fetch_days_override != null ? "text-amber-300 font-mono" : "text-muted-foreground text-[10px]"}>
                                        {t.eager_fetch_days_override ?? `(default ${platformEagerDays})`}
                                    </span>
                                </td>
                                <td className="p-2 text-center">
                                    <select value={t.causelist_match_mode || ""}
                                        onChange={e => {
                                            const v = e.target.value;
                                            if (!v) updateTenant(t.id, { clear_match_mode_override: true });
                                            else updateTenant(t.id, { causelist_match_mode: v });
                                        }}
                                        data-testid={`tenant-match-mode-${t.id}`}
                                        className="bg-background border border-border focus:border-primary outline-none px-2 py-1 text-[10px]">
                                        <option value="">(platform default)</option>
                                        <option value="advocate_only">Advocate name only</option>
                                        <option value="cnr_only">CNR only</option>
                                        <option value="both">Both</option>
                                    </select>
                                </td>
                                <td className="p-2 text-center space-x-2">
                                    <button onClick={() => {
                                        const v = prompt(`New cap for ${t.name} (1–1000):`, t.cap_per_tick_override ?? platformCap);
                                        if (v && parseInt(v) > 0) updateTenant(t.id, { cap_per_tick_override: parseInt(v) });
                                    }} className="text-[10px] text-primary hover:text-primary"
                                        data-testid={`tenant-cap-set-${t.id}`}>Set cap</button>
                                    {t.cap_per_tick_override != null && (
                                        <button onClick={() => updateTenant(t.id, { clear_cap_override: true })}
                                            className="text-[10px] text-muted-foreground hover:text-foreground"
                                            data-testid={`tenant-cap-clear-${t.id}`}>Reset cap</button>
                                    )}
                                    <button onClick={() => {
                                        const v = prompt(`Eager-fetch days for ${t.name} (0–90, lower=cheaper):`, t.eager_fetch_days_override ?? platformEagerDays);
                                        const n = parseInt(v);
                                        if (!isNaN(n) && n >= 0 && n <= 90) updateTenant(t.id, { eager_fetch_days_override: n });
                                    }} className="text-[10px] text-amber-300 hover:text-amber-200"
                                        data-testid={`tenant-eager-set-${t.id}`}>Set ⚡</button>
                                    {t.eager_fetch_days_override != null && (
                                        <button onClick={() => updateTenant(t.id, { clear_eager_fetch_override: true })}
                                            className="text-[10px] text-muted-foreground hover:text-foreground"
                                            data-testid={`tenant-eager-clear-${t.id}`}>Reset ⚡</button>
                                    )}
                                    <span className="text-[10px] text-muted-foreground">·</span>
                                    <PerTenantRefreshAllButton tenantId={t.id} tenantName={t.name}/>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Cause List Sync ----------
function CauseListTab() {
    const [data, setData] = useState(null);
    const [edited, setEdited] = useState(null);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/admin/ecourts/causelist-policy");
            setData(r.data); setEdited(r.data.policy);
        } catch (e) { toast.error(apiError(e)); }
        try {
            const r2 = await api.get("/admin/ecourts/causelist/last-runs", { params: { limit: 50 }});
            setRuns(r2.data || []);
        } catch (e) { console.warn("Causelist last-runs fetch failed (non-fatal):", e); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const save = async () => {
        try {
            await api.patch("/admin/ecourts/causelist-policy", {
                enabled: edited.enabled,
                cadence_hours: edited.cadence_hours,
                look_ahead_days: edited.look_ahead_days,
                match_advocates_from: edited.match_advocates_from,
                match_mode: edited.match_mode,
                max_cases_per_tick: edited.max_cases_per_tick,
                mode: edited.mode,
                interval_minutes: edited.interval_minutes,
                schedule_times_ist: edited.schedule_times_ist,
                buckets: edited.buckets,
                skip_sundays: edited.skip_sundays,
                max_estimated_cost_inr: edited.max_estimated_cost_inr,
                use_cnr_batch: edited.use_cnr_batch,
                use_causelist_search: edited.use_causelist_search,
                use_advocate_discovery: edited.use_advocate_discovery,
                unknown_hearing_mode: edited.unknown_hearing_mode,
            });
            toast.success("Cause list policy saved");
            await load();
        } catch (e) { toast.error(apiError(e)); }
    };

    // 12-hour AM/PM helpers + bucket / schedule mutators (parity with RefreshTab)
    const to12h = (hhmm) => {
        if (!hhmm) return "";
        const [h, m] = hhmm.split(":").map(n => parseInt(n));
        const period = h >= 12 ? "PM" : "AM";
        const h12 = h % 12 === 0 ? 12 : h % 12;
        return `${h12.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")} ${period}`;
    };
    const addScheduleTime = () => setEdited({ ...edited, schedule_times_ist: [...(edited.schedule_times_ist || []), "06:00"] });
    const updateScheduleTime = (i, v) => setEdited({ ...edited, schedule_times_ist: (edited.schedule_times_ist || []).map((t, j) => j === i ? v : t) });
    const removeScheduleTime = (i) => setEdited({ ...edited, schedule_times_ist: (edited.schedule_times_ist || []).filter((_, j) => j !== i) });
    const updateCauseBucket = (i, field, value) => setEdited(prev => ({
        ...prev,
        buckets: (prev.buckets || []).map((b, j) => j === i ? { ...b, [field]: value } : b),
    }));

    const runNow = async () => {
        // v2.14 · Pre-flight estimate — show projected cost before firing
        let est = null;
        try {
            const r = await api.get("/admin/ecourts/causelist/estimate");
            est = r.data;
        } catch (e) { /* if estimate fails we still allow the run */ }
        if (est) {
            const rupee = (v) => `₹${(Number(v) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
            const mode = est.unknown_hearing_mode || "strict";
            const modeLabel = mode === "aggressive"
                ? `AGGRESSIVE — bucket ∪ ALL unknown/past-dated cases`
                : `STRICT — bucket-only`;
            const msg =
                `You are about to trigger a manual cause-list sync across ${est.tenants} tenants.\n\n` +
                `CNR-batch scope (${modeLabel}):\n` +
                `  • Known-date cases in bucket window: ${est.known_in_window ?? "-"}\n` +
                `  • Unknown/past-date cases included: ${est.unknown_included ?? 0} of ${est.unknown_total ?? "-"} pool\n` +
                `  ────────────────────────────────\n` +
                `  Batch cases actually checked: ${est.open_cases_in_window} / ${est.open_cases_total} open cases\n\n` +
                `Projected billing (UPPER BOUND):\n` +
                `  • Batch: ${est.batch_api_calls} API calls covering ${est.batch_distinct_cnrs} CNRs → ${rupee(est.batch_billed_inr)}\n` +
                `  • Search: ${est.search_api_calls} API calls → ${rupee(est.search_billed_inr)}\n` +
                `  ────────────────────────────────\n` +
                `  Total: ${rupee(est.total_projected_billed_inr)}\n\n` +
                `Note: batch endpoint reports actual credits used, real cost may differ by ±20%. Proceed?`;
            if (!window.confirm(msg)) return;
        }
        setBusy(true);
        try {
            const r = await api.post("/admin/ecourts/causelist/run-now");
            if (r.data?.queued) {
                toast.success(r.data.message || "Cause-list sync started in background — refresh in 1–5 min.");
            } else if (r.data?.skipped) {
                toast.message("Sync skipped", { description: r.data.reason });
            } else {
                toast.success(`Cause list synced — ${r.data.hearings_created || 0} hearings created across ${r.data.tenants_processed || 0} tenants`);
            }
            setTimeout(() => { load(); }, 8000);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const dirty = useMemo(() =>
        data && edited && JSON.stringify(data.policy) !== JSON.stringify(edited),
        [data, edited]);

    if (!edited) return <div className="text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="space-y-6 max-w-3xl">
            <div className="bg-muted border border-border p-4 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-xs text-primary uppercase tracking-[0.16em]">Daily cause list sync</div>
                        <div className="text-xs text-muted-foreground mt-1">
                            Each morning, eCourts cause lists are scanned for tenant advocates and open cases — matched listings auto-create hearing records and notify the litigation team.
                        </div>
                    </div>
                    <Toggle on={edited.enabled} onChange={v => setEdited({ ...edited, enabled: v })} testId="causelist-enabled-toggle"/>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-border">
                    <Field label="Cadence (hours)">
                        <input type="number" min={1} max={336}
                            data-testid="causelist-cadence-input"
                            value={edited.cadence_hours ?? 24}
                            onChange={e => setEdited({...edited, cadence_hours: parseInt(e.target.value) || 1})}
                            className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary outline-none"/>
                        <div className="text-[10px] text-muted-foreground mt-1">How often each tenant is scanned. 24 = daily.</div>
                    </Field>
                    <Field label="Look-ahead days">
                        <input type="number" min={0} max={14}
                            data-testid="causelist-lookahead-input"
                            value={edited.look_ahead_days ?? 1}
                            onChange={e => setEdited({...edited, look_ahead_days: parseInt(e.target.value) || 0})}
                            className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary outline-none"/>
                        <div className="text-[10px] text-muted-foreground mt-1">0 = today only · 1 = today + tomorrow · 2 = +day after, etc.</div>
                    </Field>
                    <Field label="Match advocate names from">
                        <select value={edited.match_advocates_from || "staff"}
                            data-testid="causelist-match-source"
                            onChange={e => setEdited({...edited, match_advocates_from: e.target.value})}
                            className="bg-background border border-border px-3 py-2 text-xs w-full">
                            <option value="staff">Each staff member&apos;s enrolled advocate name</option>
                            <option value="firm">Firm-wide advocate name list</option>
                        </select>
                    </Field>
                    <Field label="Max cases scanned per tick">
                        <input type="number" min={0} max={100000}
                            data-testid="causelist-maxcases-input"
                            value={edited.max_cases_per_tick ?? 200}
                            onChange={e => setEdited({...edited, max_cases_per_tick: parseInt(e.target.value) || 0})}
                            className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary outline-none"/>
                        <div className="text-[10px] text-muted-foreground mt-1">Safety cap on case-number scans per firm per cadence. Set <b>0 = unlimited</b>.</div>
                    </Field>
                </div>

                <div className="pt-3 border-t border-border">
                    <div className="text-xs text-foreground mb-1">Cause list match mode <span className="text-primary">· platform default</span></div>
                    <div className="text-[10px] text-muted-foreground mb-3">
                        Controls HOW the daily cause-list scan finds your firm&apos;s cases. Tenants can override this in &quot;Per-tenant overrides&quot; below.
                    </div>
                    <select value={edited.match_mode || "advocate_only"}
                        onChange={e => setEdited({...edited, match_mode: e.target.value})}
                        data-testid="causelist-match-mode"
                        className="bg-background border border-border px-3 py-2 text-xs w-full focus:border-primary outline-none">
                        <option value="advocate_only">Advocate name only · cheapest (recommended default)</option>
                        <option value="cnr_only">CNR only · skip advocate scan</option>
                        <option value="both">Both · most coverage, most expensive</option>
                    </select>
                    <div className="mt-3 bg-background border border-card p-3 text-[10px] text-muted-foreground space-y-1">
                        <div><span className="text-primary font-medium">Cost guide</span> for a tenant with 50 cases + 5 advocates (1 day look-ahead):</div>
                        <div className="pl-3">• <span className="text-emerald-400">Advocate only</span> → 5 calls × ₹3 = <b>₹15/day</b></div>
                        <div className="pl-3">• <span className="text-amber-400">CNR only</span> → 50 calls × ₹3 = <b>₹150/day</b></div>
                        <div className="pl-3">• <span className="text-red-400">Both</span> → ₹165/day · maximum coverage but expensive</div>
                        <div className="text-muted-foreground pt-1">Most hearings are listed under the advocate&apos;s name on the cause list anyway, so &quot;Advocate only&quot; catches 95%+ of real hearings.</div>
                    </div>
                </div>

                <div className="flex gap-3">
                    <button onClick={save} disabled={!dirty}
                        data-testid="causelist-save"
                        className="px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary disabled:opacity-30">
                        Save cause-list policy
                    </button>
                    <button onClick={runNow} disabled={busy}
                        data-testid="causelist-run-now"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-primary text-primary text-xs uppercase tracking-[0.16em] hover:bg-primary hover:text-primary-foreground disabled:opacity-30">
                        <CalendarCheck className="w-3.5 h-3.5" strokeWidth={1.5}/> {busy ? "Running…" : "Sync cause list now"}
                    </button>
                </div>
            </div>

            {/* ---- v2.11.3 — Cron schedule (interval / specific IST clock times) ---- */}
            <div className="bg-muted border border-border p-4">
                <div className="text-xs text-primary uppercase tracking-[0.16em] mb-3">Cron schedule</div>
                <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="radio" name="cl-cron-mode" checked={(edited.mode || "interval") === "interval"}
                            onChange={() => setEdited({ ...edited, mode: "interval" })}
                            className="accent-primary" data-testid="causelist-mode-interval"/>
                        Interval (wake every N minutes)
                    </label>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                        <input type="radio" name="cl-cron-mode" checked={edited.mode === "schedule"}
                            onChange={() => setEdited({ ...edited, mode: "schedule" })}
                            className="accent-primary" data-testid="causelist-mode-schedule"/>
                        Schedule (specific clock times in IST)
                    </label>
                </div>

                {(edited.mode || "interval") === "interval" ? (
                    <div className="flex items-end gap-3">
                        <div>
                            <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">Run every (minutes)</label>
                            <input type="number" min={1} max={1440}
                                value={edited.interval_minutes ?? 60}
                                onChange={e => setEdited({ ...edited, interval_minutes: parseInt(e.target.value) || 60 })}
                                data-testid="causelist-interval-minutes"
                                className="bg-background border border-border px-3 py-2 text-xs w-28 focus:border-primary outline-none"/>
                        </div>
                        <div className="text-xs text-muted-foreground pb-2">
                            Range: 1–1440 (1 min … 24 hrs). Default 60 min.
                            <br/>The cron wakes once a minute and runs a pass when the interval elapses.
                            <br/>Per-bucket cadence gates the actual API calls — most ticks make zero calls.
                        </div>
                    </div>
                ) : (
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2 block">Run only at these IST times</label>
                        <div className="space-y-2" data-testid="causelist-schedule-times">
                            {(edited.schedule_times_ist || []).map((t, i) => (
                                <div key={i} className="flex items-center gap-2">
                                    <input type="time" value={t}
                                        onChange={e => updateScheduleTime(i, e.target.value)}
                                        data-testid={`causelist-schedule-time-${i}`}
                                        className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs font-mono"/>
                                    <span className="text-[10px] text-muted-foreground w-20">{to12h(t)}</span>
                                    <button type="button" onClick={() => removeScheduleTime(i)}
                                        data-testid={`causelist-schedule-time-remove-${i}`}
                                        className="text-muted-foreground hover:text-red-400 text-xs">✕</button>
                                </div>
                            ))}
                            <button type="button" onClick={addScheduleTime}
                                data-testid="causelist-schedule-time-add"
                                className="text-xs text-primary hover:text-primary mt-2">+ Add time</button>
                        </div>
                        <div className="text-[10px] text-muted-foreground mt-3">
                            All times in IST. The cron checks once a minute and runs at the next matching slot (±2 min tolerance, 30-min cooldown).
                        </div>
                    </div>
                )}
            </div>

            {/* ---- Match paths (v2.12) — three independent toggles ---- */}
            <div className="bg-card border border-primary/30 p-4 space-y-3" data-testid="causelist-match-paths">
                <div className="text-xs text-primary uppercase tracking-[0.16em]">Cause-list match paths</div>
                <div className="text-[11px] text-muted-foreground -mt-1">
                    Enable any combination. The batch endpoint is cheaper + more precise for known-CNR cases; classic search stays available for advocate-name discovery (finding cases NOT in the firm&apos;s DB yet).
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <label className="flex items-start gap-3 text-xs cursor-pointer border border-border p-3 hover:border-primary/60">
                        <input type="checkbox"
                            checked={!!edited.use_cnr_batch}
                            onChange={e => setEdited({ ...edited, use_cnr_batch: e.target.checked })}
                            data-testid="causelist-use-cnr-batch"
                            className="accent-primary mt-0.5"/>
                        <span>
                            <span className="text-foreground">CNR batch check</span>{" "}
                            <span className="text-[9px] uppercase tracking-[0.15em] text-emerald-400">NEW · cheaper</span>
                            <br/>
                            <span className="text-muted-foreground">
                                <code>POST /causelist/cnr/batch</code> — one call per 100 open CNRs. Returns <code>hasCauselist</code> + <code>nextListing</code> (with <code>listingFor</code>: EVIDENCE / ARGUMENTS / ORDER). Billed per distinct CNR. Skip this if your firm relies on advocate-name matching only.
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start gap-3 text-xs cursor-pointer border border-border p-3 hover:border-primary/60">
                        <input type="checkbox"
                            checked={edited.use_causelist_search !== false}
                            onChange={e => setEdited({ ...edited, use_causelist_search: e.target.checked })}
                            data-testid="causelist-use-search"
                            className="accent-primary mt-0.5"/>
                        <span>
                            <span className="text-foreground">Cause-list search</span>
                            <br/>
                            <span className="text-muted-foreground">
                                <code>GET /causelist/search</code> — legacy path. CNRs already resolved by batch are skipped automatically to avoid double billing.
                            </span>
                        </span>
                    </label>
                    <label className="flex items-start gap-3 text-xs cursor-pointer border border-border p-3 hover:border-primary/60">
                        <input type="checkbox"
                            checked={edited.use_advocate_discovery !== false}
                            onChange={e => setEdited({ ...edited, use_advocate_discovery: e.target.checked })}
                            data-testid="causelist-use-advocate"
                            className="accent-primary mt-0.5"/>
                        <span>
                            <span className="text-foreground">Advocate-name discovery</span>
                            <br/>
                            <span className="text-muted-foreground">
                                Sub-mode of search that finds listings by advocate name — useful for catching cases the firm hasn&apos;t imported yet. Requires <span className="text-foreground">Cause-list search</span> enabled.
                            </span>
                        </span>
                    </label>
                </div>
            </div>

            {/* ---- Unknown-date discovery (v2.14.2) — Strict vs Aggressive ---- */}
            <div className="bg-card border border-emerald-500/40 p-4 space-y-3" data-testid="causelist-unknown-discovery">
                <div className="text-xs text-emerald-400 uppercase tracking-[0.16em]">CNR batch: scope</div>
                <div className="text-[11px] text-muted-foreground -mt-1">
                    Which open CNRs get shipped to the vendor's bulk cause-list endpoint on every tick.
                    Advocate-name / court-code discovery still runs through the SEARCH path regardless of this setting.
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className={`flex items-start gap-3 text-xs cursor-pointer border p-3 hover:border-emerald-400 ${(edited.unknown_hearing_mode === "strict" || edited.unknown_hearing_mode === "balanced" || !edited.unknown_hearing_mode) ? "border-emerald-400 bg-emerald-500/5" : "border-border"}`}>
                        <input type="radio" name="unknown_hearing_mode"
                            checked={edited.unknown_hearing_mode === "strict" || edited.unknown_hearing_mode === "balanced" || !edited.unknown_hearing_mode}
                            onChange={() => setEdited({ ...edited, unknown_hearing_mode: "strict" })}
                            data-testid="unknown-mode-strict"
                            className="accent-emerald-500 mt-0.5"/>
                        <span>
                            <span className="text-foreground">B · Strict bucket-only (default)</span>{" "}
                            <span className="text-[9px] uppercase tracking-[0.15em] text-emerald-400">lowest cost</span>
                            <br/>
                            <span className="text-muted-foreground">
                                Send ONLY CNRs whose known next-hearing date falls INSIDE
                                the enabled bucket window. Cases with unknown or past
                                hearing dates are NOT probed by batch — they are
                                discovered via the search path (advocate/court-code).
                            </span>
                        </span>
                    </label>
                    <label className={`flex items-start gap-3 text-xs cursor-pointer border p-3 hover:border-emerald-400 ${edited.unknown_hearing_mode === "aggressive" ? "border-amber-400 bg-amber-500/5" : "border-border"}`}>
                        <input type="radio" name="unknown_hearing_mode"
                            checked={edited.unknown_hearing_mode === "aggressive"}
                            onChange={() => setEdited({ ...edited, unknown_hearing_mode: "aggressive" })}
                            data-testid="unknown-mode-aggressive"
                            className="accent-amber-500 mt-0.5"/>
                        <span>
                            <span className="text-foreground">A · Aggressive discovery</span>{" "}
                            <span className="text-[9px] uppercase tracking-[0.15em] text-amber-400">higher cost</span>
                            <br/>
                            <span className="text-muted-foreground">
                                Bucket window <b>plus</b> every open case with an unknown
                                or past hearing date. Ships the full open pool to the
                                vendor every tick — use only when you want the fastest
                                possible discovery of new listings.
                            </span>
                        </span>
                    </label>
                </div>
            </div>
            <div className="bg-muted border border-amber-500/40 p-4 space-y-3">
                <div className="text-xs text-amber-500 uppercase tracking-[0.16em]">Cost safety guards</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex items-start gap-3 text-xs text-muted-foreground cursor-pointer">
                        <input type="checkbox"
                            checked={edited.skip_sundays !== false}
                            onChange={e => setEdited({ ...edited, skip_sundays: e.target.checked })}
                            data-testid="causelist-skip-sundays"
                            className="accent-primary mt-0.5"/>
                        <span>
                            <span className="text-foreground">Skip Sundays automatically</span>
                            <br/>Indian courts don&apos;t sit on Sundays — never call paid <code>/causelist/search</code> on a Sunday candidate date. Default ON.
                        </span>
                    </label>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1.5 block">
                            Max estimated cost per tenant per tick (₹)
                        </label>
                        <input type="number" min={0} max={100000} step={10}
                            value={edited.max_estimated_cost_inr ?? 250}
                            onChange={e => setEdited({ ...edited, max_estimated_cost_inr: parseFloat(e.target.value) || 0 })}
                            data-testid="causelist-max-cost"
                            className="bg-background border border-border px-3 py-2 text-xs w-32 focus:border-primary outline-none"/>
                        <div className="text-[10px] text-muted-foreground mt-1">
                            Aborts the tick BEFORE any paid call when
                            (advocates+cases) × dates × per-call exceeds this.
                            <br/>0 = unlimited. Default ₹250.
                        </div>
                    </div>
                </div>
            </div>

            {/* ---- Buckets — distinct day-windows with independent cadences ---- */}
            <div>
                <div className="overline text-primary mb-2">Bucketed scan windows</div>
                <div className="text-[10px] text-muted-foreground mb-3">
                    Each bucket scans a distinct range of upcoming days with its own cadence — e.g. refresh
                    today&apos;s listings every 2 hours, but only check next-week&apos;s listings once a day.
                    Disable any bucket to skip that window entirely.
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-border">
                        <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                            <tr>
                                <th className="text-left p-3">Bucket</th>
                                <th className="text-right p-3">From (days ahead)</th>
                                <th className="text-right p-3">To (days ahead)</th>
                                <th className="text-right p-3">Refresh every (hrs)</th>
                                <th className="text-center p-3">Enabled</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(edited.buckets || []).map((b, i) => (
                                <tr key={b.key} className="border-t border-border" data-testid={`causelist-bucket-row-${b.key}`}>
                                    <td className="p-3 text-primary">{b.label}</td>
                                    <td className="p-3 text-right">
                                        <input type="number" min={0} max={30}
                                            value={b.from_days_ahead ?? 0}
                                            onChange={e => updateCauseBucket(i, "from_days_ahead", parseInt(e.target.value) || 0)}
                                            data-testid={`causelist-bucket-${b.key}-from`}
                                            className="bg-background border border-border px-2 py-1 text-xs w-16 text-right focus:border-primary outline-none"/>
                                    </td>
                                    <td className="p-3 text-right">
                                        <input type="number" min={0} max={30}
                                            value={b.to_days_ahead ?? 0}
                                            onChange={e => updateCauseBucket(i, "to_days_ahead", parseInt(e.target.value) || 0)}
                                            data-testid={`causelist-bucket-${b.key}-to`}
                                            className="bg-background border border-border px-2 py-1 text-xs w-16 text-right focus:border-primary outline-none"/>
                                    </td>
                                    <td className="p-3 text-right">
                                        <input type="number" min={1} max={24 * 14}
                                            value={b.cadence_hours ?? 24}
                                            onChange={e => updateCauseBucket(i, "cadence_hours", parseInt(e.target.value) || 1)}
                                            data-testid={`causelist-bucket-${b.key}-cadence`}
                                            className="bg-background border border-border px-2 py-1 text-xs w-20 text-right focus:border-primary outline-none"/>
                                    </td>
                                    <td className="p-3 text-center">
                                        <Toggle on={b.enabled !== false}
                                            onChange={v => updateCauseBucket(i, "enabled", v)}
                                            testId={`causelist-bucket-${b.key}-enabled`}/>
                                    </td>
                                </tr>
                            ))}
                            {(!edited.buckets || edited.buckets.length === 0) && (
                                <tr>
                                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                                        No buckets configured — the legacy single <code>cadence_hours</code> setting above will apply to the full look-ahead window.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div>
                <div className="overline text-primary mb-2">Recent tenant runs</div>
                <div className="overflow-x-auto">
                    <table className="w-full text-xs border border-border">
                        <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                            <tr>
                                <th className="text-left p-2">Tenant</th>
                                <th className="text-left p-2">Last run (IST)</th>
                                <th className="text-right p-2">Adv scanned</th>
                                <th className="text-right p-2">Cases scanned</th>
                                <th className="text-right p-2">Listings</th>
                                <th className="text-right p-2">Hearings created</th>
                                <th className="text-right p-2">Calls</th>
                            </tr>
                        </thead>
                        <tbody>
                            {runs.length === 0 && (
                                <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No runs yet — click &ldquo;Sync cause list now&rdquo; above.</td></tr>
                            )}
                            {runs.map(r => (
                                <tr key={r.id} className="border-t border-border" data-testid={`causelist-run-${r.id}`}>
                                    <td className="p-2">
                                        <div className="text-foreground">{r.tenant_name || r.tenant_id}</div>
                                        <div className="text-[10px] text-muted-foreground font-mono">{r.tenant_id}</div>
                                    </td>
                                    <td className="p-2 text-muted-foreground">{r.last_run_at ? fmtDateTime(r.last_run_at) : "—"}</td>
                                    <td className="p-2 text-right font-mono">{r.advocates_scanned ?? "—"}</td>
                                    <td className="p-2 text-right font-mono">{r.cases_scanned ?? "—"}</td>
                                    <td className="p-2 text-right font-mono">{r.listings_seen ?? 0}</td>
                                    <td className="p-2 text-right font-mono text-primary">{r.hearings_created ?? 0}</td>
                                    <td className="p-2 text-right font-mono">{r.calls_made ?? 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// ---------- Audit ----------
function AuditTab() {
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState({ tenant_id: "", endpoint_key: "", status: "" });

    const load = useCallback(async () => {
        const params = Object.fromEntries(Object.entries(filter).filter(([, v]) => v));
        try { const r = await api.get("/admin/ecourts/passbook", { params }); setRows(r.data); }
        catch (e) { toast.error(apiError(e)); }
    }, [filter]);
    useEffect(() => { load(); }, [load]);

    const auditCtrl = useTableControls(rows, {
        accessors: {
            tenant: (r) => r.tenant_id,
            user: (r) => `${r.user_email || ""} ${r.user_role || ""}`,
            endpoint: (r) => r.endpoint_label,
            request: (r) => r.request_summary || "",
        },
    });

    return (
        <div className="space-y-4">
            <NegativeWalletBackfillCard/>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <input placeholder="Tenant ID" value={filter.tenant_id} onChange={e => setFilter({...filter, tenant_id: e.target.value})}
                    data-testid="audit-tenant-filter"
                    className="bg-background border border-border px-3 py-2 text-xs font-mono"/>
                <input placeholder="Endpoint key" value={filter.endpoint_key} onChange={e => setFilter({...filter, endpoint_key: e.target.value})}
                    data-testid="audit-endpoint-filter"
                    className="bg-background border border-border px-3 py-2 text-xs"/>
                <select value={filter.status} onChange={e => setFilter({...filter, status: e.target.value})}
                    data-testid="audit-status-filter"
                    className="bg-background border border-border px-3 py-2 text-xs">
                    <option value="">All statuses</option>
                    <option value="ok">OK</option>
                    <option value="failed">Failed</option>
                </select>
            </div>
            <FilterChip ctrl={auditCtrl}/>
            <div className="overflow-x-auto">
                <table className="w-full text-xs border border-border">
                    <thead className="bg-muted text-muted-foreground uppercase tracking-[0.16em]">
                        <tr>
                            <SortableTh field="created_at" ctrl={auditCtrl} className="text-left p-2">When</SortableTh>
                            <SortableTh field="tenant" ctrl={auditCtrl} className="text-left p-2">Tenant</SortableTh>
                            <SortableTh field="user" ctrl={auditCtrl} className="text-left p-2">User</SortableTh>
                            <SortableTh field="endpoint" ctrl={auditCtrl} className="text-left p-2">Endpoint</SortableTh>
                            <SortableTh field="request" ctrl={auditCtrl} className="text-left p-2">Request</SortableTh>
                            <SortableTh field="base_inr" ctrl={auditCtrl} className="text-right p-2" align="right">Base ₹</SortableTh>
                            <SortableTh field="billed_inr" ctrl={auditCtrl} className="text-right p-2" align="right">Billed ₹</SortableTh>
                            <SortableTh field="status" ctrl={auditCtrl} className="text-center p-2" align="center">Status</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {auditCtrl.rows.length === 0 && (
                            <tr><td colSpan={8} className="p-6 text-center text-muted-foreground">No passbook entries yet.</td></tr>
                        )}
                        {auditCtrl.rows.map(r => (
                            <tr key={r.id} className="border-t border-border hover:bg-muted" data-testid={`pb-row-${r.id}`}>
                                <td className="p-2 text-muted-foreground">{fmtDateTime(r.created_at)}</td>
                                <td className="p-2 font-mono text-[10px]">{r.tenant_id}</td>
                                <td className="p-2">{r.user_email}<div className="text-[10px] text-muted-foreground">{r.user_role}</div></td>
                                <td className="p-2 text-primary">{r.endpoint_label}</td>
                                <td className="p-2 font-mono text-[10px] text-muted-foreground">{r.request_summary}</td>
                                <td className="p-2 text-right font-mono">₹{r.base_inr.toFixed(2)}</td>
                                <td className="p-2 text-right font-mono">₹{r.billed_inr.toFixed(2)}</td>
                                <td className="p-2 text-center">
                                    {r.status === "ok"
                                        ? <span className="text-emerald-400">OK</span>
                                        : <span className="text-red-400" title={r.error_message || ""}>{r.error_code || "FAIL"}</span>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// ---------- Atoms ----------
function Field({ label, children }) {
    return (
        <div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.16em] mb-1.5">{label}</div>
            {children}
        </div>
    );
}

function Toggle({ on, onChange, testId, disabled }) {
    return (
        <button type="button" disabled={disabled} onClick={() => !disabled && onChange(!on)}
            data-testid={testId}
            className={`relative inline-flex h-5 w-9 items-center transition-colors ${on ? "bg-primary" : "bg-border"} ${disabled ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}>
            <span className={`inline-block h-3.5 w-3.5 transform bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`}/>
        </button>
    );
}

function NumInput({ value, onChange, tid }) {
    return (
        <input type="number" step="0.01" value={value ?? ""} onChange={e => onChange(e.target.value)}
            data-testid={tid}
            className="bg-background border border-border px-2 py-1 text-xs w-24 text-right font-mono focus:border-primary outline-none"/>
    );
}

// v2.19.2 · Negative-wallet backfill card — one-click zero-out for wallets
// that went < 0 before the non-negative rule was enforced. Idempotent.
function NegativeWalletBackfillCard() {
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const run = async () => {
        if (!window.confirm("Zero-out every wallet with balance < 0?\n\nEach correction writes a credit txn tagged category='correction' for audit traceability. Idempotent — safe to run twice.")) return;
        setBusy(true); setResult(null);
        try {
            const { data } = await api.post("/admin/ecourts/wallet-backfill");
            setResult(data);
            toast.success(`${data.corrected || 0} wallet(s) corrected · ₹${(data.total_forgiven || 0).toFixed(2)} forgiven`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="border border-amber-500/40 bg-amber-500/5 p-3 flex flex-wrap items-center justify-between gap-3"
            data-testid="ecourts-wallet-backfill-card">
            <div>
                <div className="text-xs font-medium text-amber-500">Negative wallet backfill (one-time)</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">
                    Zeroes every tenant wallet with balance &lt; 0 (legacy debits before v2.19.2). Idempotent — writes an audit-tagged credit for every correction.
                    {result && <span className="text-emerald-400 ml-2">Last run: {result.corrected} wallets · ₹{(result.total_forgiven || 0).toFixed(2)} forgiven</span>}
                </div>
            </div>
            <button onClick={run} disabled={busy} data-testid="ecourts-wallet-backfill-btn"
                className="text-[10px] uppercase tracking-[0.14em] border border-amber-500 text-amber-500 hover:bg-amber-500/10 px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                {busy ? "Working…" : "Run backfill"}
            </button>
        </div>
    );
}


// ─── Cause List Dispatch Tab ─────────────────────────────────────────────────
function CauseListDispatchTab() {
    const tomorrow = (() => {
        const d = new Date(); d.setDate(d.getDate() + 1);
        return d.toISOString().split("T")[0];
    })();

    const [targetMode, setTargetMode] = useState("all");
    const [tenants, setTenants] = useState([]);
    const [selectedTenants, setSelectedTenants] = useState(new Set());
    const [tenantsLoading, setTenantsLoading] = useState(false);
    const [date, setDate] = useState(tomorrow);
    const [chWA, setChWA] = useState(true);
    const [chEmail, setChEmail] = useState(true);
    const [chPush, setChPush] = useState(true);
    const [dispatching, setDispatching] = useState(false);
    const [results, setResults] = useState(null);
    const [tenantSearch, setTenantSearch] = useState("");

    useEffect(() => {
        if (targetMode === "selected" && tenants.length === 0) {
            setTenantsLoading(true);
            api.get("/tenants").then(r => setTenants(r.data || [])).catch(() => {}).finally(() => setTenantsLoading(false));
        }
    }, [targetMode]);

    const filteredTenants = tenants.filter(t =>
        !tenantSearch || (t.name || "").toLowerCase().includes(tenantSearch.toLowerCase())
    );

    const toggleTenant = (id) => setSelectedTenants(s => {
        const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n;
    });

    const dispatch = async () => {
        if (!chWA && !chEmail && !chPush) { toast.error("Select at least one channel"); return; }
        if (targetMode === "selected" && selectedTenants.size === 0) { toast.error("Select at least one tenant"); return; }
        const label = targetMode === "all" ? "ALL tenants" : `${selectedTenants.size} tenant(s)`;
        const channels = [chWA && "WhatsApp", chEmail && "Email", chPush && "Push"].filter(Boolean).join(" + ");
        if (!window.confirm(`Send cause list for ${date} to ${label} via ${channels}?`)) return;
        setDispatching(true); setResults(null);
        try {
            const { data } = await api.post("/admin/cause-list/dispatch", {
                target: targetMode,
                tenant_ids: targetMode === "selected" ? [...selectedTenants] : [],
                date, whatsapp: chWA, email: chEmail, push: chPush,
            });
            setResults(data);
            toast.success(`Cause list dispatched to ${data.ok_count}/${data.tenant_count} tenants`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setDispatching(false); }
    };

    return (
        <div className="space-y-6 max-w-3xl">
            <div className="bg-muted border border-border p-5 space-y-5">
                <div>
                    <div className="overline text-primary mb-1">Cause List Dispatch</div>
                    <p className="text-xs text-muted-foreground">
                        Manually push today's cause list to tenants via WhatsApp (Deropo), Email, and/or Push Notification.
                        Each channel fires independently — a Deropo failure will not block email or push.
                    </p>
                </div>

                {/* Date */}
                <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Cause List Date</label>
                    <input type="date" value={date} onChange={e => setDate(e.target.value)}
                        data-testid="cl-dispatch-date"
                        className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                </div>

                {/* Channels */}
                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Channels</label>
                    <div className="flex flex-wrap gap-5">
                        <DispatchChannelToggle icon={<MessageSquare className="w-3.5 h-3.5"/>} label="WhatsApp (Deropo)" checked={chWA} onChange={setChWA} testId="cl-ch-wa" color="text-emerald-400"/>
                        <DispatchChannelToggle icon={<Mail className="w-3.5 h-3.5"/>} label="Email" checked={chEmail} onChange={setChEmail} testId="cl-ch-email" color="text-blue-400"/>
                        <DispatchChannelToggle icon={<Bell className="w-3.5 h-3.5"/>} label="Push Notification" checked={chPush} onChange={setChPush} testId="cl-ch-push" color="text-primary"/>
                    </div>
                </div>

                {/* Target */}
                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Send To</label>
                    <div className="flex gap-3">
                        <DispatchTargetBtn active={targetMode === "all"} onClick={() => setTargetMode("all")}
                            icon={<Users className="w-3.5 h-3.5"/>} label="All Tenants" testId="cl-target-all"/>
                        <DispatchTargetBtn active={targetMode === "selected"} onClick={() => setTargetMode("selected")}
                            icon={<Building2 className="w-3.5 h-3.5"/>} label="Select Tenants" testId="cl-target-selected"/>
                    </div>
                </div>

                {/* Tenant picker */}
                {targetMode === "selected" && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                Tenants — {selectedTenants.size} selected
                            </label>
                            {selectedTenants.size > 0 && (
                                <button onClick={() => setSelectedTenants(new Set())}
                                    className="text-[10px] text-muted-foreground hover:text-foreground" data-testid="cl-clear-tenants">Clear all</button>
                            )}
                        </div>
                        <input type="text" placeholder="Search firms…" value={tenantSearch}
                            onChange={e => setTenantSearch(e.target.value)}
                            className="w-full bg-background border border-border px-3 py-2 text-xs focus:border-primary outline-none"/>
                        <div className="bg-background border border-border max-h-52 overflow-y-auto">
                            {tenantsLoading && <div className="p-4 text-center text-xs text-muted-foreground"><Loader2 className="w-4 h-4 inline-block animate-spin"/></div>}
                            {!tenantsLoading && filteredTenants.length === 0 && <div className="p-4 text-center text-xs text-muted-foreground">No tenants found</div>}
                            {filteredTenants.map(t => {
                                const id = t.id || t._id;
                                const sel = selectedTenants.has(id);
                                return (
                                    <div key={id} onClick={() => toggleTenant(id)} data-testid={`cl-tenant-${id}`}
                                        className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer border-b border-border/50 hover:bg-muted transition-colors ${sel ? "bg-primary/5" : ""}`}>
                                        <input type="checkbox" checked={sel} readOnly className="accent-primary pointer-events-none"/>
                                        <div>
                                            <div className="text-xs text-foreground">{t.name || t.firm_name || id}</div>
                                            <div className="text-[10px] text-muted-foreground">{t.owner_email || t.plan_slug || ""}</div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <div className="flex justify-end pt-1">
                    <button onClick={dispatch} disabled={dispatching}
                        data-testid="cl-dispatch-btn"
                        className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary/80 disabled:opacity-30">
                        {dispatching ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4" strokeWidth={1.5}/>}
                        {dispatching ? "Dispatching…" : targetMode === "all" ? "Send to All Tenants" : `Send to ${selectedTenants.size || 0} Tenant(s)`}
                    </button>
                </div>
            </div>

            {/* Results */}
            {results && (
                <div className="space-y-2">
                    <div className="flex items-center justify-between">
                        <div className="overline text-primary">Results — {results.date}</div>
                        <span className="text-xs text-muted-foreground">{results.ok_count}/{results.tenant_count} tenants reached</span>
                    </div>
                    <div className="bg-muted border border-border overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead className="bg-background/30 text-muted-foreground uppercase tracking-[0.16em] text-[10px]">
                                <tr>
                                    <th className="text-left p-3">Firm</th>
                                    {results.results[0]?.channels?.push !== undefined && <th className="text-center p-3">Push</th>}
                                    {results.results[0]?.channels?.email !== undefined && <th className="text-center p-3">Email</th>}
                                    {results.results[0]?.channels?.whatsapp !== undefined && <th className="text-center p-3">WhatsApp</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {results.results.map((r, i) => (
                                    <tr key={i} className="border-t border-border" data-testid={`cl-result-${r.tenant_id}`}>
                                        <td className="p-3">
                                            <div className="text-foreground">{r.firm}</div>
                                            <div className="text-[10px] text-muted-foreground font-mono">{r.tenant_id}</div>
                                        </td>
                                        {r.channels?.push !== undefined && (
                                            <td className="p-3 text-center">
                                                {r.channels.push.ok
                                                    ? <CheckCircle2 className="w-4 h-4 inline-block text-emerald-400" title={`Sent to ${r.channels.push.sent} users`}/>
                                                    : <XCircle className="w-4 h-4 inline-block text-red-400" title={r.channels.push.reason}/>}
                                                {r.channels.push.sent !== undefined && <span className="text-muted-foreground ml-1">{r.channels.push.sent} users</span>}
                                            </td>
                                        )}
                                        {r.channels?.email !== undefined && (
                                            <td className="p-3 text-center">
                                                {r.channels.email.ok
                                                    ? <CheckCircle2 className="w-4 h-4 inline-block text-emerald-400"/>
                                                    : <XCircle className="w-4 h-4 inline-block text-red-400" title={r.channels.email.reason}/>}
                                            </td>
                                        )}
                                        {r.channels?.whatsapp !== undefined && (
                                            <td className="p-3 text-center">
                                                {r.channels.whatsapp.ok
                                                    ? <CheckCircle2 className="w-4 h-4 inline-block text-emerald-400"/>
                                                    : <XCircle className="w-4 h-4 inline-block text-red-400" title={r.channels.whatsapp.reason}/>}
                                                {r.channels.whatsapp.provider && <span className="text-[10px] text-muted-foreground ml-1">{r.channels.whatsapp.provider}</span>}
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function DispatchChannelToggle({ icon, label, checked, onChange, testId, color }) {
    return (
        <label className="flex items-center gap-2 text-xs cursor-pointer select-none">
            <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} data-testid={testId} className="accent-primary"/>
            <span className={color}>{icon}</span>
            {label}
        </label>
    );
}

function DispatchTargetBtn({ active, onClick, icon, label, testId }) {
    return (
        <button onClick={onClick} data-testid={testId}
            className={`flex items-center gap-2 px-4 py-2 text-xs border transition-colors
                ${active ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {icon}{label}
        </button>
    );
}


// ─── Strategy 2: HOT/COLD Tiered Sweep ────────────────────────────────────────
function Strategy2Tab() {
    const [cfg, setCfg]                   = useState(null);
    const [saving, setSaving]             = useState(false);
    const [running, setRunning]           = useState(false);
    const [runningCL, setRunningCL]         = useState(false);
    const [lastCLResult, setLastCLResult]   = useState(null);
    const [runningYHR, setRunningYHR]       = useState(false);
    const [lastYHRResult, setLastYHRResult] = useState(null);
    const [runningSHR, setRunningSHR]       = useState(false);
    const [lastSHRResult, setLastSHRResult] = useState(null);
    const [shrTenantId, setShrTenantId]     = useState("");
    // Default to tomorrow's date in IST (UTC+5:30)
    const [bulkCLDate, setBulkCLDate] = useState(() => {
        const now = new Date(Date.now() + 5.5 * 60 * 60 * 1000);
        const tomorrow = new Date(now);
        tomorrow.setUTCDate(tomorrow.getUTCDate() + 1);
        return tomorrow.toISOString().split("T")[0]; // YYYY-MM-DD
    });
    const [showPerTenant, setShowPerTenant] = useState(false);
    const [tenantRows, setTenantRows]     = useState([]);
    const [tenantSearch, setTenantSearch] = useState("");
    const [savingTenant, setSavingTenant] = useState(null);

    // ── Bulk Refresh state ────────────────────────────────────────────────
    const [selectedTenants, setSelectedTenants] = useState(new Set());
    const [refreshBusy, setRefreshBusy]   = useState(false);
    const [refreshJob, setRefreshJob]     = useState(null); // { status, progress, result, error }

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ecourts/strategy2/config");
            setCfg(data);
        } catch { setCfg({}); }
    }, []);

    const loadTenants = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ecourts/strategy2/tenant-overrides");
            setTenantRows(data);
        } catch { setTenantRows([]); }
    }, []);

    useEffect(() => { load(); loadTenants(); }, [load, loadTenants]);
    useEffect(() => { if (showPerTenant) loadTenants(); }, [showPerTenant, loadTenants]);

    const save = async () => {
        setSaving(true);
        try {
            const payload = { ...cfg };
            delete payload.all_case_statuses;
            delete payload.default_cold_statuses;
            const { data } = await api.patch("/admin/ecourts/strategy2/config", payload);
            setCfg(prev => ({ ...prev, ...data }));
            toast.success("Strategy 2 config saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const runNow = async () => {
        if (!window.confirm("Run Strategy 2 HOT/COLD sweep for ALL tenants right now?\n\nForce mode: ALL eligible COLD cases will be refreshed immediately — bypasses the daily 1/7 rotation and stale threshold.")) return;
        setRunning(true);
        try {
            const { data } = await api.post("/admin/ecourts/strategy2/run-now");
            if (data.ok === false) {
                toast.warning(`Sweep could not run: ${data.reason || "unknown reason"}`);
            } else {
                toast.success(`Sweep done — HOT: ${data.total_hot ?? 0}, District COLD: ${data.total_cold_district ?? data.total_cold ?? 0}, HC/SC: ${data.total_cold_hcsc ?? 0}, Promoted: ${data.total_promoted ?? 0}, Cost: ₹${data.total_cost_inr ?? 0}`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(false); }
    };

    const runBulkCL = async () => {
        const dateLabel = bulkCLDate || "tomorrow";
        if (!window.confirm(`Pull cause list for ALL tenants with hearings on ${dateLabel}?\nThis will charge each tenant's wallet.`)) return;
        setRunningCL(true);
        setLastCLResult(null);
        try {
            const { data } = await api.post("/admin/ecourts/strategy2/run-bulk-causelist",
                { target_date: bulkCLDate || null },
                { timeout: 180000 });
            if (data.ok === false) {
                toast.warning(`Bulk cause list could not run: ${data.reason || "unknown reason"}`);
            } else {
                setLastCLResult(data);
                toast.success(`Bulk cause list done for ${dateLabel} — ${data.total_notified ?? data.notifications_sent ?? 0} notified`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunningCL(false); }
    };


    const runYHR = async () => {
        if (!window.confirm("Run Yesterday Hearing Refresh now?\nStep 1: bulk_refresh (₹0.15/CNR) — queues server-side scrape for yesterday's cases.\nStep 2: case_detail (₹1.50/CNR) — pulls full updated data into DB.\nEach tenant's wallet will be charged.")) return;
        setRunningYHR(true); setLastYHRResult(null);
        try {
            const { data } = await api.post("/admin/ecourts/strategy2/run-yesterday-hearing-refresh", {}, { timeout: 300000 });
            setLastYHRResult(data);
            toast.success(`Yesterday Hearing Refresh done — ${data.total_step2 ?? 0} cases refreshed`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunningYHR(false); }
    };

    const runSHR = async () => {
        const scope = shrTenantId ? "selected tenant" : "ALL tenants";
        if (!window.confirm(`Run Stale Hearing Refresh for ${scope}?\nStep 1: bulk_refresh (₹0.15/CNR) — queues server-side scrape for all past-due cases.\nStep 2: case_detail (₹1.50/CNR) — pulls full updated data.\nEach tenant's wallet will be charged.`)) return;
        setRunningSHR(true); setLastSHRResult(null);
        try {
            const body = shrTenantId ? { tenant_id: shrTenantId } : {};
            const { data } = await api.post("/admin/ecourts/strategy2/run-stale-hearing-refresh", body, { timeout: 600000 });
            setLastSHRResult(data);
            toast.success(`Stale Hearing Refresh done — ${data.total_step2 ?? 0} cases refreshed, ${data.total_promoted ?? 0} promoted to HOT`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunningSHR(false); }
    };

    const saveTenantOverride = async (tenantId, overrides) => {
        setSavingTenant(tenantId);
        try {
            const { data } = await api.put(`/admin/ecourts/tenants/${tenantId}/strategy2-overrides`, overrides);
            setTenantRows(rows => rows.map(r => r.tenant_id === tenantId ? { ...r, overrides: data.overrides } : r));
            toast.success(`Saved overrides for ${data.name}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setSavingTenant(null); }
    };

    // ── Bulk Refresh helpers ─────────────────────────────────────────────
    const filteredTenants = tenantRows.filter(t => !tenantSearch || (t.name || "").toLowerCase().includes(tenantSearch.toLowerCase()));

    const allFilteredSelected = filteredTenants.length > 0 &&
        filteredTenants.every(t => selectedTenants.has(t.tenant_id));

    const toggleSelectAll = () => {
        if (allFilteredSelected) {
            // deselect all currently filtered
            setSelectedTenants(prev => {
                const next = new Set(prev);
                filteredTenants.forEach(t => next.delete(t.tenant_id));
                return next;
            });
        } else {
            setSelectedTenants(prev => {
                const next = new Set(prev);
                filteredTenants.forEach(t => next.add(t.tenant_id));
                return next;
            });
        }
    };

    const toggleTenant = (tid) => {
        setSelectedTenants(prev => {
            const next = new Set(prev);
            next.has(tid) ? next.delete(tid) : next.add(tid);
            return next;
        });
    };

    const refreshSelected = async () => {
        if (selectedTenants.size === 0) { toast.error("Select at least one tenant"); return; }
        const names = [...selectedTenants]
            .map(tid => tenantRows.find(r => r.tenant_id === tid)?.name || tid)
            .join(", ");
        if (!window.confirm(`Bulk refresh ALL active cases for:\n${names}\n\nThis will charge each tenant's wallet. Proceed?`)) return;

        setRefreshBusy(true);
        setRefreshJob({ status: "queued", progress: null });
        try {
            const start = await api.post("/admin/ecourts/refresh-all-tenants", {
                confirm: true,
                fire_push: true,
                throttle_seconds: 0,
                statuses: "active,open,pending",
                tenant_ids: [...selectedTenants],
            });
            const jobId = start.data.job_id;
            toast.info(`Refresh job queued — polling for progress…`);

            let done = false;
            while (!done) {
                await new Promise(r => setTimeout(r, 2000));
                try {
                    const js = (await api.get(`/admin/ecourts/refresh-all-tenants/status/${jobId}`)).data;
                    const newJob = {
                        status: js.status,
                        progress: js.progress,
                        result: js.result,
                        error: js.error,
                    };
                    setRefreshJob(newJob);
                    if (js.status === "completed") {
                        done = true;
                        const r = js.result || {};
                        toast.success(`Done — ${r.cases_refreshed ?? 0} cases refreshed across ${r.tenants_refreshed ?? selectedTenants.size} tenant(s) · ${r.pushes_fired ?? 0} pushes`);
                        setSelectedTenants(new Set());
                    } else if (js.status === "failed") {
                        done = true;
                        const errMsg = js.error || "unknown error";
                        if (errMsg.includes("disabled by super-admin")) {
                            toast.error("eCourts is disabled. Enable it in Admin → eCourts → Connection tab first.");
                        } else {
                            toast.error(`Refresh failed: ${errMsg}`);
                        }
                    }
                } catch { /* poll again */ }
            }
        } catch (e) { toast.error(apiError(e)); setRefreshJob(null); }
        finally { setRefreshBusy(false); }
    };

    if (!cfg) return (
        <div className="p-8 text-center text-xs text-muted-foreground">
            <Loader2 className="w-4 h-4 inline-block animate-spin mr-2"/>Loading…
        </div>
    );

    const isEnabled   = !!cfg.strategy_2_enabled;
    const isBulkCLOn  = cfg.bulk_causelist_enabled !== false;
    const isYHROn     = cfg.yesterday_hearing_refresh_enabled !== false;
    const allStatuses = cfg.all_case_statuses || [];
    const coldStatuses = cfg.cold_case_statuses || cfg.default_cold_statuses || allStatuses.filter(s => s.value !== "disposed").map(s => s.value);

    const toggleStatus = (val) => {
        const cur = coldStatuses;
        const next = cur.includes(val) ? cur.filter(v => v !== val) : [...cur, val];
        setCfg(c => ({ ...c, cold_case_statuses: next }));
    };

    return (
        <div className="space-y-6 max-w-3xl" data-testid="strategy2-tab">
            {/* ── Hero / Master Toggle ─────────────────────────────── */}
            <div className={`border-2 rounded-2xl p-6 transition-colors ${isEnabled ? "border-orange-400/50 bg-orange-500/5" : "border-border bg-muted/30"}`}>
                <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Strategy 2 · HOT/COLD Tiered Refresh</div>
                        <div className="text-2xl font-medium">Cost-Optimised Daily Sweep</div>
                        <p className="text-xs text-muted-foreground mt-2 max-w-xl">
                            HOT cases (auto-promoted if hearing within {cfg.hot_auto_promote_days ?? 7} days, or manually tagged) get full <code>case_detail</code> daily.
                            District COLD cases rotate 1/7th per day via <code>bulk_refresh</code> (₹0.15).
                            HC/SC COLD cases get <code>bulk_refresh</code> + <code>case_detail</code> daily since dates appear 1–2 days before hearing.
                        </p>
                    </div>
                    <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        <button onClick={() => setCfg(c => ({ ...c, strategy_2_enabled: !c.strategy_2_enabled }))}
                            data-testid="s2-master-toggle"
                            className={`relative w-12 h-6 rounded-full transition-colors ${isEnabled ? "bg-orange-500" : "bg-muted-foreground/30"}`}>
                            <span className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${isEnabled ? "left-7" : "left-1"}`}/>
                        </button>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-medium ${isEnabled ? "text-orange-400" : "text-muted-foreground"}`} data-testid="s2-status">
                            {isEnabled ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                </div>
            </div>

            {/* ── Numeric Config ───────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                    { label: "HOT Slots per Tenant",           key: "hot_slots_max",            def: 50,   min: 1,  max: 200, step: 5,  hint: "Max HOT cases per firm" },
                    { label: "Daily Cost Limit (₹/tenant)",    key: "daily_cost_limit_inr",      def: 20,   min: 5,  max: 500, step: 5,  hint: "Trim HOT + alert if exceeded" },
                    { label: "Stale Threshold (hours)",        key: "auto_refresh_stale_hours",  def: 15,   min: 1,  max: 72,  step: 1,  hint: "Skip auto-open refresh if updated within N hours" },
                    { label: "Inactive Weeks → Flag",          key: "cold_inactive_weeks",       def: 6,    min: 1,  max: 26,  step: 1,  hint: "Flag COLD case as 'possibly closed' after N weeks" },
                    { label: "HOT Auto-Promote Window (days)", key: "hot_auto_promote_days",     def: 7,    min: 1,  max: 30,  step: 1,  hint: "Auto-promote COLD cases to HOT if hearing is within N days" },
                    { label: "Bulk Refresh Wait (seconds)",    key: "bulk_refresh_wait_sec",     def: 35,   min: 0,  max: 120, step: 5,  hint: "Wait after bulk_refresh before calling case_detail — allows govt server to process the queue. Recommended ≥ 30s." },
                ].map(f => (
                    <div key={f.key} className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">{f.label}</label>
                        <input type="number" value={cfg[f.key] ?? f.def} min={f.min} max={f.max} step={f.step}
                            onChange={e => setCfg(c => ({ ...c, [f.key]: +e.target.value }))}
                            data-testid={`s2-${f.key}`}
                            className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                        <p className="text-[10px] text-muted-foreground">{f.hint}</p>
                    </div>
                ))}
                <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Daily Sweep Time (IST)</label>
                    <input type="time" value={cfg.s2_sweep_ist ?? "20:00"}
                        onChange={e => setCfg(c => ({ ...c, s2_sweep_ist: e.target.value }))}
                        data-testid="s2-sweep-time"
                        className="w-full bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                    <p className="text-[10px] text-muted-foreground">24h IST time for the daily HOT/COLD sweep</p>
                </div>
                <div className="space-y-1.5">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">HC/SC Daily Full Check</label>
                    <div className="flex items-center gap-3 pt-1">
                        <Toggle on={cfg.hcsc_daily_check_enabled !== false}
                            onChange={v => setCfg(c => ({ ...c, hcsc_daily_check_enabled: v }))}
                            testId="s2-hcsc-daily-toggle"/>
                        <span className={`text-xs ${cfg.hcsc_daily_check_enabled !== false ? "text-emerald-400" : "text-muted-foreground"}`}>
                            {cfg.hcsc_daily_check_enabled !== false ? "Enabled" : "Disabled"}
                        </span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">Daily <code>bulk_refresh</code> + <code>case_detail</code> for all HC/SC COLD cases (dates appear 1–2 days before hearing)</p>
                </div>
            </div>

            {/* ── COLD Case Status Multi-Select ────────────────────── */}
            <div className="border border-border rounded-xl p-5 space-y-3">
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-primary"/>
                    <span className="text-sm font-medium">COLD Case Statuses</span>
                </div>
                <p className="text-xs text-muted-foreground">
                    Only cases with <strong>checked statuses</strong> are included in automated sweeps (HOT or COLD).
                    Un-checked statuses are excluded — those cases can only be updated via the manual <strong>Refresh</strong> button.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 pt-1" data-testid="s2-cold-statuses">
                    {allStatuses.length === 0 ? (
                        <p className="text-xs text-muted-foreground col-span-3">Loading statuses…</p>
                    ) : allStatuses.map(st => {
                        const isChecked = coldStatuses.includes(st.value);
                        return (
                            <label key={st.value}
                                className={`flex items-center gap-2 p-2.5 rounded-lg border cursor-pointer transition-all select-none
                                    ${isChecked ? "border-primary/60 bg-primary/5" : "border-border bg-muted/20 opacity-60"}`}
                                data-testid={`s2-status-${st.value}`}>
                                <input type="checkbox" className="hidden" checked={isChecked}
                                    onChange={() => toggleStatus(st.value)}/>
                                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors
                                    ${isChecked ? "bg-primary border-primary" : "bg-background border-border"}`}>
                                    {isChecked && <svg className="w-2.5 h-2.5 text-primary-foreground" viewBox="0 0 10 8" fill="none">
                                        <path d="M1 4l2.5 3L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>}
                                </span>
                                <div className="min-w-0">
                                    <div className="text-xs font-medium capitalize truncate">{st.label}</div>
                                    {st.ecourts && (
                                        <div className="text-[10px] text-muted-foreground">eCourts: {st.ecourts}</div>
                                    )}
                                </div>
                            </label>
                        );
                    })}
                </div>
                <p className="text-[10px] text-amber-400/80 border-t border-border pt-2">
                    Note: "Disposed" cases are excluded by default. They can only be updated via manual Refresh.
                </p>
            </div>

            {/* ── Bulk Cause List Section ───────────────────────────── */}
            <div className="border border-border rounded-xl p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <CalendarCheck className="w-4 h-4 text-primary"/>
                        <span className="text-sm font-medium">Bulk Cause List Check</span>
                    </div>

                {/* ── Manual Pull Trigger — prominent card at top ── */}
                <div className="flex flex-col gap-3 bg-primary/5 border border-primary/30 rounded-xl px-4 py-3"
                    data-testid="s2-manual-cl-card">
                    {/* Top row: icon + label */}
                    <div className="flex items-center gap-2">
                        <Download className="w-4 h-4 text-primary flex-shrink-0"/>
                        <div>
                            <div className="text-sm font-medium text-foreground">Manual Bulk Cause List Pull</div>
                            <div className="text-[11px] text-muted-foreground">
                                Fetch cause list for all tenants with hearings on the selected date — outside the auto-schedule.
                            </div>
                        </div>
                    </div>
                    {/* Date picker + Pull button */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                        <div className="flex flex-col gap-0.5 flex-1">
                            <label className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground font-medium">
                                Hearing Date (IST)
                            </label>
                            <input
                                type="date"
                                value={bulkCLDate}
                                onChange={e => setBulkCLDate(e.target.value)}
                                data-testid="s2-bulk-cl-date-picker"
                                className="bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-primary w-full sm:w-44 cursor-pointer"
                            />
                            <span className="text-[10px] text-muted-foreground">
                                Default: tomorrow · change to pull any date
                            </span>
                        </div>
                        <button onClick={runBulkCL} disabled={runningCL}
                            data-testid="s2-manual-cl-pull-btn"
                            className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] font-medium hover:bg-primary/80 disabled:opacity-40 rounded-lg transition-colors shrink-0 whitespace-nowrap self-end sm:self-auto mt-1 sm:mt-6">
                            {runningCL
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/>Pulling…</>
                                : <><Download className="w-3.5 h-3.5"/>Pull Now</>}
                        </button>
                    </div>
                </div>
                {/* Last run result */}
                {lastCLResult && (
                    <div className="flex flex-wrap items-center gap-4 text-xs border border-emerald-500/30 bg-emerald-500/5 rounded-xl px-4 py-2.5"
                        data-testid="s2-last-cl-result">
                        <span className="text-emerald-600 font-semibold">Last pull:</span>
                        <span><strong>{lastCLResult.total_cnrs ?? "—"}</strong> CNRs fetched</span>
                        <span><strong>{lastCLResult.total_notified ?? lastCLResult.notifications_sent ?? "—"}</strong> tenants notified</span>
                        <span className="text-rose-500"><strong>₹{Number(lastCLResult.total_cost_inr ?? 0).toFixed(2)}</strong> charged</span>
                        <button onClick={() => setLastCLResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}
                </div>

                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 opacity-0 pointer-events-none">
                        <span/>
                    </div>
                    <div className="flex items-center gap-2">
                        <button onClick={() => setCfg(c => ({ ...c, bulk_causelist_enabled: !isBulkCLOn }))}
                            data-testid="s2-bulk-cl-toggle"
                            className={`relative w-10 h-5 rounded-full transition-colors ${isBulkCLOn ? "bg-primary" : "bg-muted-foreground/30"}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isBulkCLOn ? "left-5" : "left-0.5"}`}/>
                        </button>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-medium ${isBulkCLOn ? "text-primary" : "text-muted-foreground"}`}
                            data-testid="s2-bulk-cl-status">
                            Auto-Schedule: {isBulkCLOn ? "On" : "Off"}
                        </span>
                    </div>
                </div>
                <p className="text-xs text-muted-foreground">
                    Every day at the configured IST time, this checks all cases with <strong>tomorrow's hearing date</strong>,
                    fetches their cause list via <code>cnr_causelist_batch</code>, and notifies tenants via
                    <strong> Push + Email + WhatsApp</strong>.
                </p>
                <div className="flex items-end gap-4">
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Run Time (IST)</label>
                        <input type="time" value={cfg.bulk_causelist_ist ?? "19:30"}
                            onChange={e => setCfg(c => ({ ...c, bulk_causelist_ist: e.target.value }))}
                            data-testid="s2-bulk-cl-time"
                            className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                        <p className="text-[10px] text-muted-foreground">Default: 19:30 IST daily</p>
                    </div>
                    <button onClick={runBulkCL} disabled={runningCL} data-testid="s2-run-bulk-cl-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-border text-xs uppercase tracking-[0.14em] hover:border-primary hover:text-primary disabled:opacity-30 mb-6">
                        {runningCL ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                        {runningCL ? "Checking…" : "Run Now"}
                    </button>
                </div>
                <div className="text-xs text-muted-foreground bg-muted rounded p-3 flex items-start gap-2">
                    <Bell className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-primary"/>
                    <span>Notifications include tomorrow's cause list link. Only cases with eligible statuses (from the COLD statuses selection above) are included.</span>
                </div>
            </div>

            {/* ── Cost Reference ───────────────────────────────────── */}
            <div className="bg-muted border border-border rounded p-4 text-xs space-y-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Cost Reference</div>
                <div className="flex justify-between"><span className="text-orange-300">HOT — case_detail</span><code>₹1.50 / case / day</code></div>
                <div className="flex justify-between"><span className="text-amber-400">Yesterday Hearing — bulk_refresh + case_detail</span><code>₹1.65 / case / run</code></div>
                <div className="flex justify-between"><span className="text-rose-400">Stale Hearing — bulk_refresh + case_detail</span><code>₹1.65 / case / run</code></div>
                <div className="flex justify-between"><span>District COLD — bulk_refresh (1/7 rotation)</span><code>₹0.15 / case / day</code></div>
                <div className="flex justify-between"><span className="text-sky-400">HC/SC COLD — bulk_refresh + case_detail (daily)</span><code>₹1.65 / case / day</code></div>
                <div className="flex justify-between"><span className="text-primary">Bulk CL — cnr_causelist_batch</span><code>₹0.30 / 100 CNRs / day</code></div>
                <p className="text-muted-foreground pt-1 border-t border-border">
                    Example: 50 HOT + 500 District COLD (~71/day) + 5 HC/SC = ₹83.10/day. Per-tenant limit applies individually.
                </p>
            </div>

            {/* ── Yesterday Hearing Refresh toggle ─────────────────── */}
            <div className="border border-border rounded-xl p-4 space-y-4" data-testid="s2-yhr-card">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-sm font-medium">Yesterday Hearing Refresh</div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Runs daily at the configured IST time. Finds all cases with
                            <strong> next_hearing_date = yesterday</strong>, then:
                            <br/>① <code>bulk_refresh</code> (₹0.15/CNR) — queues server-side scrape → ② <code>case_detail</code> (₹1.50/CNR) — pulls full updated data.
                            Total cost: <strong>₹1.65/case</strong>. Captures overnight eCourts updates before advocates start their day.
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <button onClick={() => setCfg(c => ({ ...c, yesterday_hearing_refresh_enabled: !isYHROn }))}
                            data-testid="s2-yhr-toggle"
                            className={`relative w-10 h-5 rounded-full transition-colors ${isYHROn ? "bg-amber-500" : "bg-muted-foreground/30"}`}>
                            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${isYHROn ? "left-5" : "left-0.5"}`}/>
                        </button>
                        <span className={`text-[10px] uppercase tracking-[0.14em] font-medium ${isYHROn ? "text-amber-500" : "text-muted-foreground"}`}
                            data-testid="s2-yhr-status">
                            {isYHROn ? "On" : "Off"}
                        </span>
                    </div>
                </div>
                <div className="flex items-end gap-4">
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Run Time (IST)</label>
                        <input type="time"
                            value={cfg.yesterday_hearing_refresh_ist ?? "04:30"}
                            onChange={e => setCfg(c => ({ ...c, yesterday_hearing_refresh_ist: e.target.value }))}
                            data-testid="s2-yhr-time"
                            className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none"/>
                        <p className="text-[10px] text-muted-foreground">Default 04:30 IST</p>
                    </div>
                    <button onClick={runYHR} disabled={runningYHR} data-testid="s2-run-yhr-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-border text-xs uppercase tracking-[0.14em] hover:border-amber-500 hover:text-amber-500 disabled:opacity-30 mb-6">
                        {runningYHR ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                        {runningYHR ? "Refreshing…" : "Run Now"}
                    </button>
                </div>
                {lastYHRResult && (
                    <div className="flex flex-wrap gap-4 text-xs border border-amber-500/30 bg-amber-500/5 rounded-xl px-4 py-2.5"
                        data-testid="s2-last-yhr-result">
                        <span className="text-amber-600 font-semibold">Last run:</span>
                        <span><strong>{lastYHRResult.total_found ?? "—"}</strong> found</span>
                        <span><strong>{lastYHRResult.total_step1 ?? "—"}</strong> bulk_refresh sent</span>
                        <span><strong>{lastYHRResult.total_step2 ?? "—"}</strong> case_detail refreshed</span>
                        <span className="text-rose-500"><strong>₹{Number(lastYHRResult.total_cost_inr ?? 0).toFixed(2)}</strong></span>
                        <button onClick={() => setLastYHRResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}
            </div>

            {/* ── Stale Hearing Refresh ────────────────────────────── */}
            <div className="border border-border rounded-xl p-4 space-y-4" data-testid="s2-shr-card">
                <div className="flex items-center justify-between gap-4">
                    <div>
                        <div className="text-sm font-medium flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-rose-400"/>
                            Stale Hearing Refresh
                        </div>
                        <div className="text-[11px] text-muted-foreground mt-0.5">
                            Bulk-refresh <strong>all</strong> cases with <code>next_hearing_date &lt; today</code> (not just yesterday).
                            <br/>① <code>bulk_refresh</code> (₹0.15/CNR) — queues scrape → ② <code>case_detail</code> (₹1.50/CNR) — pulls fresh data.
                            Use for manual recovery after downtime, or to batch-update overdue dates.
                        </div>
                    </div>
                </div>
                <div className="flex items-end gap-4 flex-wrap">
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Filter by Tenant (optional)</label>
                        <select value={shrTenantId} onChange={e => setShrTenantId(e.target.value)}
                            data-testid="s2-shr-tenant-filter"
                            className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none min-w-[200px]">
                            <option value="">— All Tenants —</option>
                            {tenantRows.map(t => (
                                <option key={t.tenant_id} value={t.tenant_id}>{t.name || t.tenant_id}</option>
                            ))}
                        </select>
                        <p className="text-[10px] text-muted-foreground">Leave blank to run for all tenants</p>
                    </div>
                    <button onClick={runSHR} disabled={runningSHR} data-testid="s2-run-shr-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-rose-500/50 text-rose-400 text-xs uppercase tracking-[0.14em] hover:border-rose-500 hover:text-rose-300 disabled:opacity-30 mb-6">
                        {runningSHR ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
                        {runningSHR ? "Refreshing…" : "Run Stale Refresh"}
                    </button>
                </div>
                {lastSHRResult && (
                    <div className="flex flex-wrap gap-4 text-xs border border-rose-500/30 bg-rose-500/5 rounded-xl px-4 py-2.5"
                        data-testid="s2-last-shr-result">
                        <span className="text-rose-400 font-semibold">Last run:</span>
                        <span><strong>{lastSHRResult.total_found ?? "—"}</strong> stale cases found</span>
                        <span><strong>{lastSHRResult.total_step1 ?? "—"}</strong> bulk_refresh sent</span>
                        <span><strong>{lastSHRResult.total_step2 ?? "—"}</strong> case_detail refreshed</span>
                        <span className="text-emerald-400"><strong>{lastSHRResult.total_promoted ?? 0}</strong> auto-promoted to HOT</span>
                        <span className="text-rose-500"><strong>₹{Number(lastSHRResult.total_cost_inr ?? 0).toFixed(2)}</strong></span>
                        <button onClick={() => setLastSHRResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}
            </div>

            {/* ── Save + Run Buttons ───────────────────────────────── */}
            <div className="flex gap-3 flex-wrap">
                <button onClick={save} disabled={saving} data-testid="s2-save-btn"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.16em] hover:bg-primary/80 disabled:opacity-30">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null}
                    {saving ? "Saving…" : "Save Strategy 2"}
                </button>
                <button onClick={runNow} disabled={running} data-testid="s2-run-now-btn"
                    title="Force mode: bypasses daily rotation — processes ALL eligible COLD cases immediately"
                    className="inline-flex items-center gap-2 px-5 py-2.5 border border-border text-xs uppercase tracking-[0.16em] hover:border-primary hover:text-primary disabled:opacity-30">
                    {running ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5" strokeWidth={1.5}/>}
                    {running ? "Running…" : "Run HOT/COLD Sweep Now"}
                    {!running && <span className="text-[9px] bg-amber-500/15 text-amber-600 border border-amber-500/30 px-1.5 py-0.5 rounded uppercase tracking-[0.1em]">Force</span>}
                </button>
            </div>

            {/* ── Per-Tenant Overrides ─────────────────────────────── */}
            <div className="border border-border rounded-xl overflow-hidden" data-testid="s2-tenant-overrides-card">
                <button
                    onClick={() => setShowPerTenant(v => !v)}
                    className="w-full flex items-center justify-between p-5 hover:bg-muted/30 transition-colors">
                    <div className="flex items-center gap-2">
                        <Users className="w-4 h-4 text-primary"/>
                        <span className="text-sm font-medium">Per-Tenant Overrides</span>
                        <span className="text-[10px] text-muted-foreground ml-1">Override platform defaults per tenant</span>
                    </div>
                    {showPerTenant ? <ChevronUp className="w-4 h-4 text-muted-foreground"/> : <ChevronDown className="w-4 h-4 text-muted-foreground"/>}
                </button>

                {showPerTenant && (
                    <div className="border-t border-border p-5 space-y-4">
                        {/* Search + select summary row */}
                        <div className="flex flex-wrap items-center gap-3">
                            <input
                                type="text" placeholder="Search tenants…"
                                value={tenantSearch} onChange={e => setTenantSearch(e.target.value)}
                                data-testid="s2-tenant-search"
                                className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none max-w-xs flex-1 min-w-[180px]"/>
                            {selectedTenants.size > 0 && (
                                <span className="text-xs text-primary font-medium" data-testid="s2-selected-count">
                                    {selectedTenants.size} tenant{selectedTenants.size > 1 ? "s" : ""} selected
                                </span>
                            )}
                        </div>
                        <p className="text-[10px] text-muted-foreground">
                            Override per tenant: toggle S2 on/off, custom HOT slots, daily cost limit, and eligible COLD statuses.
                            Leave blank to inherit platform default.
                        </p>

                        {/* ── Bulk Refresh action bar (visible when ≥1 tenant selected) ── */}
                        {selectedTenants.size > 0 && (
                            <div className="flex flex-wrap items-center gap-3 bg-primary/5 border border-primary/30 rounded-xl px-4 py-3"
                                data-testid="s2-bulk-refresh-bar">
                                <RefreshCw className="w-4 h-4 text-primary flex-shrink-0"/>
                                <span className="text-sm text-foreground flex-1 min-w-0">
                                    Refresh all active cases for <strong>{selectedTenants.size}</strong> selected tenant{selectedTenants.size > 1 ? "s" : ""}
                                </span>
                                <button onClick={refreshSelected} disabled={refreshBusy}
                                    data-testid="s2-refresh-selected-btn"
                                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] font-medium hover:bg-primary/80 disabled:opacity-40 rounded-lg transition-colors shrink-0">
                                    {refreshBusy
                                        ? <><Loader2 className="w-3.5 h-3.5 animate-spin"/>Refreshing…</>
                                        : <><RefreshCw className="w-3.5 h-3.5"/>Refresh &amp; Update</>
                                    }
                                </button>
                                <button onClick={() => setSelectedTenants(new Set())} disabled={refreshBusy}
                                    className="text-xs text-muted-foreground hover:text-foreground underline disabled:opacity-40">Clear</button>
                            </div>
                        )}

                        {/* ── Live job progress ─────────────────────────────────────── */}
                        {refreshJob && (
                            <div className={`border rounded-xl px-4 py-3 text-xs space-y-1 ${refreshJob.status === "completed" ? "border-emerald-500/30 bg-emerald-500/5" : refreshJob.status === "failed" ? "border-destructive/30 bg-destructive/5" : "border-border bg-muted/30"}`}
                                data-testid="s2-refresh-job-status">
                                <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium capitalize text-foreground">{refreshJob.status === "queued" ? "Queued…" : refreshJob.status === "running" ? "Running…" : refreshJob.status}</span>
                                    {(refreshJob.status === "completed" || refreshJob.status === "failed") && (
                                        <button onClick={() => setRefreshJob(null)} className="text-muted-foreground hover:text-foreground text-[10px] underline">Dismiss</button>
                                    )}
                                </div>
                                {refreshJob.progress && (
                                    <div className="text-muted-foreground">
                                        Tenants: {refreshJob.progress.tenants_processed} · Cases: {refreshJob.progress.cases_refreshed} · Pushes: {refreshJob.progress.pushes_fired}
                                        {refreshJob.progress.current_tenant && <span> · Current: <code>{refreshJob.progress.current_tenant}</code></span>}
                                    </div>
                                )}
                                {refreshJob.result && refreshJob.status === "completed" && (
                                    <div className="text-emerald-600 font-medium">
                                        Done — {refreshJob.result.cases_refreshed} cases refreshed · {refreshJob.result.pushes_fired} pushes fired
                                    </div>
                                )}
                                {refreshJob.error && <div className="text-destructive">{refreshJob.error}</div>}
                            </div>
                        )}

                        <div className="overflow-x-auto">
                            <table className="w-full text-xs">
                                <thead>
                                    <tr className="border-b border-border text-muted-foreground">
                                        {/* Select-all checkbox */}
                                        <th className="py-2 pr-2 w-8">
                                            <label className="flex items-center justify-center cursor-pointer" data-testid="s2-select-all-checkbox">
                                                <input type="checkbox" className="sr-only"
                                                    checked={allFilteredSelected}
                                                    onChange={toggleSelectAll}/>
                                                <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${allFilteredSelected ? "bg-primary border-primary" : "bg-background border-border"}`}>
                                                    {allFilteredSelected && <svg className="w-2.5 h-2.5 text-primary-foreground" viewBox="0 0 10 8" fill="none">
                                                        <path d="M1 4l2.5 3L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                    </svg>}
                                                    {!allFilteredSelected && selectedTenants.size > 0 && filteredTenants.some(t => selectedTenants.has(t.tenant_id)) && (
                                                        <span className="w-2 h-0.5 bg-muted-foreground rounded"/>
                                                    )}
                                                </span>
                                            </label>
                                        </th>
                                        <th className="text-left py-2 pr-3 font-medium">Tenant</th>
                                        <th className="text-left py-2 pr-3 font-medium">Strategy</th>
                                        <th className="text-left py-2 pr-3 font-medium">HOT Slots</th>
                                        <th className="text-left py-2 pr-3 font-medium">Cost (₹)</th>
                                        <th className="text-left py-2 pr-3 font-medium">Bulk CL</th>
                                        <th className="text-left py-2 pr-3 font-medium">CL Time (IST)</th>
                                        <th className="text-left py-2 pr-3 font-medium">Yest. Hearing</th>
                                        <th className="text-left py-2 pr-3 font-medium">COLD Statuses</th>
                                        <th className="text-left py-2 font-medium">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredTenants.length === 0 && (
                                        <tr><td colSpan="9" className="py-6 text-center text-muted-foreground">
                                            {tenantSearch ? `No tenants match "${tenantSearch}"` : "No tenants found"}
                                        </td></tr>
                                    )}
                                    {filteredTenants.map(t => (
                                        <TenantS2OverrideRow
                                            key={t.tenant_id}
                                            tenant={t}
                                            platformCfg={cfg}
                                            onSave={saveTenantOverride}
                                            saving={savingTenant === t.tenant_id}
                                            selected={selectedTenants.has(t.tenant_id)}
                                            onToggle={() => toggleTenant(t.tenant_id)}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}


function TenantS2OverrideRow({ tenant, platformCfg, onSave, saving, selected, onToggle }) {
    const ov = tenant.overrides || {};
    const [expanded, setExpanded]     = useState(false);
    const [hotSlots, setHotSlots]     = useState(ov.hot_slots_max ?? "");
    const [costLimit, setCostLimit]   = useState(ov.daily_cost_limit_inr ?? "");
    const [bulkCL, setBulkCL]         = useState(ov.bulk_causelist_enabled ?? null);
    const [bulkCLTime, setBulkCLTime] = useState(ov.bulk_causelist_ist ?? "");
    const [hdrEnabled, setHdrEnabled] = useState(ov.yesterday_hearing_refresh_enabled ?? null);
    const [coldStatuses, setColdStatuses] = useState(ov.cold_case_statuses ?? null);

    // Derive from strategy_2_enabled: null=inherit, true=s2, false=s1
    const deriveActive = (s2e) => (s2e === true ? "s2" : s2e === false ? "s1" : null);
    const [activeStrategy, setActiveStrategy] = useState(deriveActive(ov.strategy_2_enabled ?? null));

    const allStatuses  = platformCfg.all_case_statuses || [];
    const platformCold = platformCfg.cold_case_statuses || platformCfg.default_cold_statuses || [];

    const handleSave = () => {
        const overrides = {};
        // Translate 3-way strategy picker → strategy_2_enabled field
        if (activeStrategy === "s2")      overrides.strategy_2_enabled = true;
        else if (activeStrategy === "s1") overrides.strategy_2_enabled = false;
        // null (inherit) → omit strategy_2_enabled entirely
        if (hotSlots  !== "")      overrides.hot_slots_max         = +hotSlots;
        if (costLimit !== "")      overrides.daily_cost_limit_inr  = +costLimit;
        if (bulkCL    !== null)    overrides.bulk_causelist_enabled = bulkCL;
        if (bulkCLTime !== "")     overrides.bulk_causelist_ist     = bulkCLTime;
        if (hdrEnabled !== null)   overrides.yesterday_hearing_refresh_enabled = hdrEnabled;
        if (coldStatuses !== null) overrides.cold_case_statuses     = coldStatuses;
        onSave(tenant.tenant_id, overrides);
    };

    const activeCold    = coldStatuses ?? platformCold;
    const hasAnyOverride = activeStrategy !== null || hotSlots !== "" || costLimit !== "" ||
        bulkCL !== null || bulkCLTime !== "" || hdrEnabled !== null || coldStatuses !== null;

    const cycleEnabled = (cur) => (cur === null ? true : cur === true ? false : null);

    return (
        <>
            <tr className="border-b border-border/50 hover:bg-muted/20"
                data-testid={`s2-tenant-row-${tenant.tenant_id}`}>

                {/* Row checkbox */}
                <td className="py-2.5 pr-2 w-8">
                    <label className="flex items-center justify-center cursor-pointer">
                        <input type="checkbox" className="sr-only" checked={!!selected} onChange={onToggle}
                            data-testid={`s2-tenant-check-${tenant.tenant_id}`}/>
                        <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${selected ? "bg-primary border-primary" : "bg-background border-border"}`}>
                            {selected && <svg className="w-2.5 h-2.5 text-primary-foreground" viewBox="0 0 10 8" fill="none">
                                <path d="M1 4l2.5 3L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>}
                        </span>
                    </label>
                </td>

                <td className="py-2.5 pr-3 font-medium text-foreground max-w-[120px] truncate">
                    {tenant.name}
                    {hasAnyOverride && <span className="ml-1.5 text-[9px] text-primary uppercase tracking-wider">custom</span>}
                </td>

                {/* Active Strategy — mutually exclusive 3-way */}
                <td className="py-2.5 pr-3">
                    <div className="flex items-center gap-1">
                        {[null, "s1", "s2"].map(opt => (
                            <button key={String(opt)}
                                onClick={() => setActiveStrategy(opt)}
                                data-testid={`s2-tenant-strat-${tenant.tenant_id}-${opt ?? "inherit"}`}
                                className={`px-2 py-1 text-[10px] border rounded transition-colors
                                    ${activeStrategy === opt
                                        ? "border-primary bg-primary/10 text-primary font-medium"
                                        : "border-border hover:border-primary/60 text-muted-foreground"}`}>
                                {opt === null ? "inherit" : opt.toUpperCase()}
                            </button>
                        ))}
                    </div>
                </td>

                <td className="py-2.5 pr-3">
                    <input type="number" value={hotSlots} min={1} max={200}
                        onChange={e => setHotSlots(e.target.value)}
                        placeholder={`${platformCfg.hot_slots_max ?? 50}`}
                        className="w-14 bg-background border border-border px-2 py-1 text-xs outline-none focus:border-primary"/>
                </td>

                <td className="py-2.5 pr-3">
                    <input type="number" value={costLimit} min={5} max={500}
                        onChange={e => setCostLimit(e.target.value)}
                        placeholder={`${platformCfg.daily_cost_limit_inr ?? 20}`}
                        className="w-14 bg-background border border-border px-2 py-1 text-xs outline-none focus:border-primary"/>
                </td>

                <td className="py-2.5 pr-3">
                    <button onClick={() => setBulkCL(cycleEnabled(bulkCL))}
                        className="px-2 py-1 rounded border border-border text-[10px] hover:border-primary min-w-[56px] text-center">
                        {bulkCL === true ? <span className="text-green-400">ON</span>
                            : bulkCL === false ? <span className="text-red-400">OFF</span>
                            : <span className="text-muted-foreground">~inherit</span>}
                    </button>
                </td>

                <td className="py-2.5 pr-3">
                    <div className="flex flex-col gap-0.5">
                        <input type="time" value={bulkCLTime}
                            onChange={e => setBulkCLTime(e.target.value)}
                            data-testid={`s2-tenant-bulkcl-time-${tenant.tenant_id}`}
                            className="bg-background border border-border px-2 py-1 text-xs outline-none focus:border-primary w-[88px]"/>
                        <span className="text-[9px] text-muted-foreground">
                            {bulkCLTime
                                ? <button onClick={() => setBulkCLTime("")} className="hover:text-primary">reset</button>
                                : `~${platformCfg.bulk_causelist_ist ?? "19:30"}`}
                        </span>
                    </div>
                </td>

                {/* Hearing Day Refresh override */}
                <td className="py-2.5 pr-3">
                    <button onClick={() => setHdrEnabled(cycleEnabled(hdrEnabled))}
                        data-testid={`s2-tenant-hdr-${tenant.tenant_id}`}
                        className="px-2 py-1 rounded border border-border text-[10px] hover:border-amber-500 min-w-[56px] text-center">
                        {hdrEnabled === true  ? <span className="text-amber-400">ON</span>
                            : hdrEnabled === false ? <span className="text-red-400">OFF</span>
                            : <span className="text-muted-foreground">~inherit</span>}
                    </button>
                </td>

                <td className="py-2.5 pr-3">
                    <button onClick={() => setExpanded(v => !v)}
                        data-testid={`s2-tenant-expand-${tenant.tenant_id}`}
                        className="px-2 py-1 text-[10px] border border-border hover:border-primary flex items-center gap-1 whitespace-nowrap">
                        <Filter className="w-2.5 h-2.5"/>
                        {coldStatuses !== null ? `${coldStatuses.length} sel.` : "inherit"}
                        {expanded ? <ChevronUp className="w-2.5 h-2.5"/> : <ChevronDown className="w-2.5 h-2.5"/>}
                    </button>
                </td>

                <td className="py-2.5">
                    <button onClick={handleSave} disabled={saving}
                        data-testid={`s2-tenant-save-${tenant.tenant_id}`}
                        className="px-3 py-1 bg-primary text-primary-foreground text-[10px] uppercase tracking-[0.12em] hover:bg-primary/80 disabled:opacity-30">
                        {saving ? <Loader2 className="w-3 h-3 animate-spin inline"/> : "Save"}
                    </button>
                </td>
            </tr>

            {expanded && (
                <tr className="border-b border-border/50">
                    <td colSpan="8" className="pb-4 pt-1 px-2">
                        <div className="bg-muted/30 border border-border/50 rounded-lg p-4 space-y-3">
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">COLD Case Statuses</span>
                                {coldStatuses !== null
                                    ? <button onClick={() => setColdStatuses(null)} className="text-[10px] text-primary hover:underline">Reset — inherit platform</button>
                                    : <span className="text-[10px] text-muted-foreground">Inheriting platform ({platformCold.length} statuses)</span>}
                            </div>
                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5" data-testid={`s2-tenant-statuses-${tenant.tenant_id}`}>
                                {allStatuses.map(st => {
                                    const isChecked = activeCold.includes(st.value);
                                    return (
                                        <label key={st.value}
                                            onClick={() => {
                                                const base = coldStatuses ?? platformCold;
                                                setColdStatuses(base.includes(st.value) ? base.filter(v => v !== st.value) : [...base, st.value]);
                                            }}
                                            className={`flex items-center gap-1.5 p-2 rounded border cursor-pointer select-none
                                                ${isChecked ? "border-primary/60 bg-primary/5" : "border-border bg-muted/20 opacity-60"}
                                                ${coldStatuses === null ? "opacity-30 pointer-events-none" : ""}`}>
                                            <span className={`w-3 h-3 rounded border flex items-center justify-center flex-shrink-0
                                                ${isChecked ? "bg-primary border-primary" : "bg-background border-border"}`}>
                                                {isChecked && <svg className="w-2 h-2 text-primary-foreground" viewBox="0 0 10 8" fill="none">
                                                    <path d="M1 4l2.5 3L9 1" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>}
                                            </span>
                                            <span className="text-[10px] truncate">{st.label}</span>
                                        </label>
                                    );
                                })}
                            </div>
                            {coldStatuses === null && (
                                <p className="text-[10px] text-muted-foreground">Click any status to customise for this tenant.</p>
                            )}
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}


// ============================================================================
// Strategy 3 Tab — Lightweight sweep (YHR + 1/7 bulk_refresh + Bulk Causelist)
// ============================================================================
function Strategy3Tab() {
    const [cfg, setCfg]             = useState({});
    const [loading, setLoading]     = useState(true);
    const [saving, setSaving]       = useState(false);
    const [running, setRunning]     = useState("");
    const [lastResult, setLastResult] = useState(null);
    const [shrTenantId, setShrTenantId] = useState("");
    const [runningSHR, setRunningSHR]   = useState(false);
    const [lastSHRResult, setLastSHRResult] = useState(null);
    const [tenantRows, setTenantRows]   = useState([]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ecourts/strategy3/config");
            setCfg(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    // Load tenants independently so a config failure never empties the dropdown
    const loadTenants = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ecourts/tenants-summary");
            setTenantRows(Array.isArray(data) ? data : []);
        } catch { setTenantRows([]); }
    }, []);

    useEffect(() => { load(); loadTenants(); }, [load, loadTenants]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/ecourts/strategy3/config", cfg);
            setCfg(data);
            toast.success("Strategy 3 config saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const runNow = async (which) => {
        setRunning(which);
        setLastResult(null);
        try {
            const { data } = await api.post(`/admin/ecourts/strategy3/run-now?which=${which}`, {}, { timeout: 300000 });
            setLastResult({ which, ...data });
            const cost = data.total_cost_inr ?? data.cost_inr ?? 0;
            toast.success(`${which} done — ₹${Number(cost).toFixed(2)}`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(""); }
    };

    const runSHR = async () => {
        const scope = shrTenantId ? "selected tenant" : "ALL tenants";
        if (!window.confirm(`Run Stale Hearing Refresh for ${scope}?\nStep 1: bulk_refresh (₹0.15/CNR)\nStep 2: case_detail (₹1.50/CNR)\nThis will charge the tenant wallet.`)) return;
        setRunningSHR(true); setLastSHRResult(null);
        try {
            const body = shrTenantId ? { tenant_id: shrTenantId } : {};
            const { data } = await api.post("/admin/ecourts/strategy3/run-stale-refresh", body, { timeout: 600000 });
            setLastSHRResult(data);
            toast.success(`Stale Refresh done — ${data.total_step2 ?? 0} cases refreshed, ${data.total_promoted ?? 0} promoted`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunningSHR(false); }
    };

    if (loading) return <div className="text-muted-foreground text-sm p-6">Loading…</div>;

    const Toggle3 = ({ on, onChange, testId }) => (
        <button type="button" onClick={() => onChange(!on)}
            data-testid={testId}
            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-muted"}`}>
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${on ? "translate-x-4" : "translate-x-0.5"}`}/>
        </button>
    );

    return (
        <div className="space-y-6 max-w-4xl" data-testid="strategy3-tab">
            {/* ── Header + Enable toggle ───────────────────────────── */}
            <div className="border border-border rounded-xl p-5 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Strategy 3 · Lightweight Sweep</div>
                    <div className="text-2xl font-medium">YHR + 1/7 Daily + Bulk Causelist</div>
                    <p className="text-xs text-muted-foreground mt-2 max-w-xl">
                        Three daily jobs, no HOT/COLD tier split. All 350 cases rotate 1/7 per day via <code>bulk_refresh</code> (₹0.15).
                        YHR refreshes yesterday's hearings (₹1.65). Bulk Causelist fires for tomorrow's hearings (₹0.30/CNR).
                        Estimated cost: <strong>~₹1,337/month</strong> for 350 cases (19 hearings/day).
                    </p>
                    <p className="text-[11px] text-amber-400 mt-2">
                        Once enabled, Strategy 1 and Strategy 2 will be automatically disabled (only one strategy active at a time).
                    </p>
                </div>
                <div className="flex flex-col items-center gap-2 min-w-[80px]">
                    <Toggle3 on={!!cfg.strategy_3_enabled}
                        onChange={v => setCfg(c => ({ ...c, strategy_3_enabled: v }))}
                        testId="s3-master-toggle"/>
                    <span className={`text-[10px] uppercase font-semibold ${cfg.strategy_3_enabled ? "text-emerald-400" : "text-muted-foreground"}`}>
                        {cfg.strategy_3_enabled ? "Active" : "Inactive"}
                    </span>
                    {cfg.strategy_3_enabled && (
                        <span className="text-[9px] text-emerald-400/70 text-center leading-tight">Default strategy</span>
                    )}
                </div>
            </div>

            {/* ── Bulk Refresh Wait ─────────────────────────────────── */}
            <div className="bg-card border border-border rounded-xl p-4">
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                            <span className="text-sm font-medium text-foreground">Bulk Refresh Wait</span>
                            <span className="text-[10px] px-2 py-0.5 bg-amber-500/10 text-amber-600 border border-amber-500/20 rounded-full">Shared with S2</span>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">
                            Seconds to wait after <code className="bg-muted px-1 rounded text-[10px]">bulk_refresh</code> completes before calling <code className="bg-muted px-1 rounded text-[10px]">case_detail</code>. The government server needs this time to process its scrape queue. Applies to YHR and Stale Refresh. <strong>Recommended ≥ 30s.</strong>
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <input
                            type="number" min={0} max={120} step={5}
                            value={cfg.bulk_refresh_wait_sec ?? 35}
                            onChange={e => setCfg(c => ({ ...c, bulk_refresh_wait_sec: Math.max(0, Math.min(120, +e.target.value)) }))}
                            data-testid="s3-bulk-refresh-wait-sec"
                            className="w-20 px-2 py-1.5 text-sm text-center bg-background border border-border rounded-lg focus:outline-none focus:ring-1 focus:ring-primary text-foreground"
                        />
                        <span className="text-xs text-muted-foreground">sec</span>
                    </div>
                </div>
            </div>

            {/* ── Three cron jobs ──────────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {[
                    {
                        key: "yhr",   enableKey: "s3_yhr_enabled",   timeKey: "s3_yhr_ist",
                        label: "Yesterday Hearing Refresh",
                        desc: "bulk_refresh + case_detail for cases heard yesterday. Updates next hearing date.",
                        cost: "₹1.65/case",   badge: "bg-amber-500/15 text-amber-400 border-amber-400/40",
                    },
                    {
                        key: "bulk_refresh", enableKey: "s3_bulk_refresh_enabled", timeKey: "s3_bulk_refresh_ist",
                        label: "1/7 Daily bulk_refresh",
                        desc: "One-seventh of ALL cases get a bulk_refresh every day (7-day rotation). No HOT/COLD split.",
                        cost: "₹0.15/case",  badge: "bg-sky-500/15 text-sky-400 border-sky-400/40",
                    },
                    {
                        key: "bulk_causelist", enableKey: "s3_bulk_causelist_enabled", timeKey: "s3_bulk_causelist_ist",
                        label: "Bulk Cause List",
                        desc: "cnr_causelist_batch for all cases with next_hearing_date = tomorrow. Sends Push + Email + WhatsApp.",
                        cost: "₹0.30/case",  badge: "bg-emerald-500/15 text-emerald-400 border-emerald-400/40",
                    },
                ].map(job => (
                    <div key={job.key} className="border border-border rounded-xl p-4 space-y-3"
                        data-testid={`s3-job-${job.key}`}>
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-medium">{job.label}</span>
                            <Toggle3 on={cfg[job.enableKey] !== false}
                                onChange={v => setCfg(c => ({ ...c, [job.enableKey]: v }))}
                                testId={`s3-toggle-${job.key}`}/>
                        </div>
                        <p className="text-[11px] text-muted-foreground leading-relaxed">{job.desc}</p>
                        <div className="flex items-center justify-between gap-2">
                            <span className={`text-[10px] px-2 py-0.5 rounded border ${job.badge}`}>{job.cost}</span>
                            <input type="time" value={cfg[job.timeKey] ?? "04:30"}
                                onChange={e => setCfg(c => ({ ...c, [job.timeKey]: e.target.value }))}
                                data-testid={`s3-time-${job.key}`}
                                className="bg-background border border-border px-2 py-1 text-xs focus:border-primary outline-none w-24"/>
                        </div>
                        <button onClick={() => runNow(job.key)}
                            disabled={running === job.key || !cfg.strategy_3_enabled}
                            data-testid={`s3-run-${job.key}`}
                            className="w-full flex items-center justify-center gap-2 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-border hover:border-primary/60 disabled:opacity-30 disabled:cursor-not-allowed">
                            {running === job.key
                                ? <><Loader2 className="w-3 h-3 animate-spin"/> Running…</>
                                : <><Play className="w-3 h-3"/> Run Now</>}
                        </button>
                    </div>
                ))}
            </div>

            {/* ── Last run result ──────────────────────────────────── */}
            {lastResult && (
                <div className="border border-border rounded px-4 py-2.5 text-xs flex flex-wrap gap-4 items-center"
                    data-testid="s3-last-result">
                    <span className="text-primary font-semibold capitalize">{lastResult.which} result:</span>
                    {lastResult.total_cases   != null && <span><strong>{lastResult.total_cases}</strong> cases refreshed</span>}
                    {lastResult.total_found   != null && <span><strong>{lastResult.total_found}</strong> found</span>}
                    {lastResult.total_step2   != null && <span><strong>{lastResult.total_step2}</strong> case_detail</span>}
                    {lastResult.total_cnrs    != null && <span><strong>{lastResult.total_cnrs}</strong> CNRs</span>}
                    {(lastResult.total_cost_inr ?? lastResult.cost_inr) != null &&
                        <span className="text-rose-400">₹{Number(lastResult.total_cost_inr ?? lastResult.cost_inr ?? 0).toFixed(2)}</span>}
                    <button onClick={() => setLastResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline text-[10px]">Dismiss</button>
                </div>
            )}

            {/* ── Stale Hearing Refresh ────────────────────────────── */}
            <div className="border border-border rounded-xl p-4 space-y-4" data-testid="s3-shr-card">
                <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-rose-400"/>
                        Stale Hearing Refresh
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Bulk-refresh <strong>all</strong> cases with <code>next_hearing_date &lt; today</code>.
                        Step 1: <code>bulk_refresh</code> (₹0.15/CNR) → Step 2: <code>case_detail</code> (₹1.50/CNR).
                        Use for recovery after downtime or to batch-update overdue dates.
                    </p>
                </div>
                <div className="flex items-end gap-4 flex-wrap">
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Filter by Tenant (optional)</label>
                        <select value={shrTenantId} onChange={e => setShrTenantId(e.target.value)}
                            data-testid="s3-shr-tenant-filter"
                            className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none min-w-[200px]">
                            <option value="">— All Tenants —</option>
                            {tenantRows.map(t => (
                                <option key={t.id || t.tenant_id} value={t.id || t.tenant_id}>{t.name || t.id || t.tenant_id}</option>
                            ))}
                        </select>
                    </div>
                    <button onClick={runSHR} disabled={runningSHR}
                        data-testid="s3-run-shr-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-rose-500/50 text-rose-400 text-xs uppercase tracking-[0.14em] hover:border-rose-500 hover:text-rose-300 disabled:opacity-30 mb-6">
                        {runningSHR ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
                        {runningSHR ? "Refreshing…" : "Run Stale Refresh"}
                    </button>
                </div>
                {lastSHRResult && (
                    <div className="flex flex-wrap gap-4 text-xs border border-rose-500/30 bg-rose-500/5 rounded-xl px-4 py-2.5"
                        data-testid="s3-last-shr-result">
                        <span className="text-rose-400 font-semibold">Last run:</span>
                        <span><strong>{lastSHRResult.total_found ?? "—"}</strong> stale cases found</span>
                        <span><strong>{lastSHRResult.total_step1 ?? "—"}</strong> bulk_refresh sent</span>
                        <span><strong>{lastSHRResult.total_step2 ?? "—"}</strong> case_detail refreshed</span>
                        {(lastSHRResult.total_promoted ?? 0) > 0 &&
                            <span className="text-emerald-400"><strong>{lastSHRResult.total_promoted}</strong> auto-promoted</span>}
                        <span className="text-rose-500">₹{Number(lastSHRResult.total_cost_inr ?? 0).toFixed(2)}</span>
                        <button onClick={() => setLastSHRResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}
            </div>

            {/* ── Cost Reference ───────────────────────────────────── */}
            <div className="bg-muted border border-border rounded p-4 text-xs space-y-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Monthly Cost Reference (350 cases, 19 hearings/day)</div>
                <div className="flex justify-between"><span>1/7 bulk_refresh (50/day)</span><code>₹225/month</code></div>
                <div className="flex justify-between"><span className="text-amber-400">YHR (19/day × ₹1.65)</span><code>₹940/month</code></div>
                <div className="flex justify-between"><span className="text-emerald-400">Bulk Causelist (19/day × ₹0.30)</span><code>₹171/month</code></div>
                <div className="flex justify-between font-semibold border-t border-border pt-2"><span>Total</span><code>~₹1,337/month</code></div>
                <p className="text-muted-foreground pt-1 text-[10px]">
                    vs Strategy 2 at ₹4,600/month — 71% cheaper. HOT/COLD tags are preserved as labels for advocate preference.
                </p>
            </div>

            {/* ── Save button ──────────────────────────────────────── */}
            <div className="flex items-center gap-4">
                <button onClick={save} disabled={saving}
                    data-testid="s3-save-btn"
                    className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-30">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null}
                    {saving ? "Saving…" : "Save Strategy 3 Config"}
                </button>
                <button onClick={load} disabled={loading}
                    className="text-xs text-muted-foreground hover:text-foreground underline">Refresh</button>
            </div>
        </div>
    );
}


function Strategy4Tab() {
    const [cfg, setCfg]               = useState({});
    const [loading, setLoading]       = useState(true);
    const [saving, setSaving]         = useState(false);
    const [running, setRunning]       = useState("");
    const [lastResult, setLastResult] = useState(null);
    const [staleLoading, setStaleLoading]   = useState(false);
    const [lastStaleResult, setLastStaleResult] = useState(null);
    const [staleTenantId, setStaleTenantId] = useState("");
    const [tenantRows, setTenantRows] = useState([]);
    const [triggering, setTriggering]   = useState(false);
    const [triggerResult, setTriggerResult] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/ecourts/strategy4/config");
            setCfg(data);
        } catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);

    const loadTenants = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ecourts/tenants-summary");
            setTenantRows(Array.isArray(data) ? data : []);
        } catch { setTenantRows([]); }
    }, []);

    useEffect(() => { load(); loadTenants(); }, [load, loadTenants]);

    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch("/admin/ecourts/strategy4/config", cfg);
            setCfg(data);
            toast.success("Strategy 4 config saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };

    const runNow = async (which) => {
        setRunning(which);
        setLastResult(null);
        try {
            const { data } = await api.post(`/admin/ecourts/strategy4/run-now?which=${which}`, {}, { timeout: 120000 });
            setLastResult({ which, ...data });
            if (which === "bulk_causelist") {
                toast.success(`Cause list notifications sent for ${data.total_cnrs ?? 0} CNRs`);
            } else {
                const tot = data.total_marked ?? 0;
                toast.success(`Morning sweep done — ${tot} cases marked (HOT: ${data.total_hot ?? 0}, YHR: ${data.total_yhr ?? 0}, Daily: ${data.total_daily ?? 0})`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setRunning(""); }
    };

    const runMarkStale = async () => {
        const scope = staleTenantId ? "selected tenant" : "ALL tenants";
        if (!window.confirm(`Mark all stale cases (next_hearing_date < today) for scraping for ${scope}?\nThis has zero eCourts wallet cost.`)) return;
        setStaleLoading(true);
        setLastStaleResult(null);
        try {
            const body = staleTenantId ? { tenant_id: staleTenantId } : {};
            const { data } = await api.post("/admin/ecourts/strategy4/mark-stale", body, { timeout: 120000 });
            setLastStaleResult(data);
            toast.success(`Marked ${data.total_marked ?? 0} stale cases for scraping`);
        } catch (e) { toast.error(apiError(e)); }
        finally { setStaleLoading(false); }
    };

    const triggerScraper = async () => {
        setTriggering(true);
        setTriggerResult(null);
        try {
            const { data } = await api.post("/admin/ecourts/strategy4/trigger-scraper", {}, { timeout: 20000 });
            setTriggerResult(data);
            if (data.triggered) {
                toast.success(`Scraper triggered successfully (HTTP ${data.status_code})`);
            } else {
                toast.error(`Trigger failed: ${data.reason || data.error || "unknown"}`);
            }
        } catch (e) { toast.error(apiError(e)); }
        finally { setTriggering(false); }
    };

    if (loading) return <div className="text-muted-foreground text-sm p-6">Loading…</div>;

    const Toggle4 = ({ on, onChange, testId }) => (
        <button type="button" onClick={() => onChange(!on)}
            data-testid={testId}
            className={`relative inline-flex h-5 w-9 rounded-full transition-colors ${on ? "bg-emerald-500" : "bg-muted"}`}>
            <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition-transform mt-0.5 ${on ? "translate-x-4" : "translate-x-0.5"}`}/>
        </button>
    );

    return (
        <div className="space-y-6 max-w-4xl" data-testid="strategy4-tab">

            {/* ── Header + Master Enable toggle ─────────────────────── */}
            <div className="border border-border rounded-xl p-5 flex items-start justify-between gap-6">
                <div>
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Strategy 4 · Scraper-Driven Sync</div>
                    <div className="text-2xl font-medium">100% Scraper-Based · Zero eCourts API Cost</div>
                    <p className="text-xs text-muted-foreground mt-2 max-w-xl">
                        All data comes from the external scraper. Platform marks cases for priority scraping
                        (<code>scrape_requested_at</code>); the scraper fetches from eCourts and pushes data
                        back via <code>POST /api/v1/sync/case</code>. No <code>bulk_refresh</code>,
                        <code>case_detail</code>, or <code>cnr_causelist_batch</code> calls are made.
                        Estimated cost: <strong className="text-emerald-400">₹0.00/month</strong> from eCourts wallet.
                    </p>
                    <p className="text-[11px] text-amber-400 mt-2">
                        Enabling Strategy 4 automatically disables Strategies 1, 2, and 3.
                    </p>
                </div>
                <div className="flex flex-col items-center gap-2 min-w-[80px]">
                    <Toggle4
                        on={!!cfg.strategy_4_enabled}
                        onChange={v => setCfg(c => ({ ...c, strategy_4_enabled: v }))}
                        testId="s4-master-toggle"
                    />
                    <span className={`text-[10px] uppercase font-semibold ${cfg.strategy_4_enabled ? "text-emerald-400" : "text-muted-foreground"}`}>
                        {cfg.strategy_4_enabled ? "Active" : "Inactive"}
                    </span>
                </div>
            </div>

            {/* ── Two scheduled jobs ───────────────────────────────── */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

                {/* ── Job 1: Morning Sweep ────────────────────────── */}
                <div className="border border-border rounded-xl p-5 space-y-4" data-testid="s4-job-morning_sweep">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Daily · 01:00 AM IST</div>
                            <div className="text-sm font-medium mt-0.5">Morning Sweep</div>
                        </div>
                        <Toggle4
                            on={cfg.s4_morning_sweep_enabled !== false}
                            onChange={v => setCfg(c => ({ ...c, s4_morning_sweep_enabled: v }))}
                            testId="s4-toggle-morning_sweep"
                        />
                    </div>

                    {/* Priority order visual */}
                    <div className="space-y-1.5">
                        {[
                            { label: "Phase 1 — HOT cases",           badge: "bg-rose-500/15 text-rose-400 border-rose-400/40",   note: "watch_tier = hot → priority 1" },
                            { label: "Phase 2 — Yesterday's hearings", badge: "bg-amber-500/15 text-amber-400 border-amber-400/40", note: "next_hearing_date = yesterday → priority 2" },
                            { label: "Phase 3 — 1/7 daily rotation",  badge: "bg-sky-500/15 text-sky-400 border-sky-400/40",      note: "all other cases, 7-day cycle → priority 3" },
                        ].map((ph, i) => (
                            <div key={i} className="flex items-center gap-2">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded border shrink-0 ${ph.badge}`}>{ph.label}</span>
                                <span className="text-[10px] text-muted-foreground truncate">{ph.note}</span>
                            </div>
                        ))}
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        Marks cases with <code>scrape_requested_at</code> and <code>scrape_priority</code>.
                        The scraper's CNR feed returns HOT first, then YHR, then daily — no eCourts API call.
                    </p>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground uppercase">Time (IST)</span>
                            <input
                                type="time"
                                value={cfg.s4_morning_sweep_ist ?? "01:00"}
                                onChange={e => setCfg(c => ({ ...c, s4_morning_sweep_ist: e.target.value }))}
                                data-testid="s4-time-morning_sweep"
                                className="bg-background border border-border px-2 py-1 text-xs focus:border-primary outline-none w-24"
                            />
                        </div>
                        <button
                            onClick={() => runNow("morning_sweep")}
                            disabled={running === "morning_sweep" || !cfg.strategy_4_enabled}
                            data-testid="s4-run-morning_sweep"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-border hover:border-primary/60 disabled:opacity-30 disabled:cursor-not-allowed">
                            {running === "morning_sweep"
                                ? <><Loader2 className="w-3 h-3 animate-spin"/> Running…</>
                                : <><Play className="w-3 h-3"/> Run Now</>}
                        </button>
                    </div>

                    {/* Per-job last result */}
                    {lastResult?.which === "morning_sweep" && (
                        <div className="rounded border border-border bg-muted/30 px-3 py-2 text-[11px] space-y-1"
                            data-testid="s4-sweep-result">
                            <div className="flex gap-3 flex-wrap">
                                <span className="text-rose-400">HOT <strong>{lastResult.total_hot ?? 0}</strong></span>
                                <span className="text-amber-400">YHR <strong>{lastResult.total_yhr ?? 0}</strong></span>
                                <span className="text-sky-400">Daily <strong>{lastResult.total_daily ?? 0}</strong></span>
                                <span className="font-semibold">Total <strong>{lastResult.total_marked ?? 0}</strong></span>
                                <span className="text-emerald-400 ml-auto">₹0.00</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* ── Job 2: Cause List Notifications ─────────────── */}
                <div className="border border-border rounded-xl p-5 space-y-4" data-testid="s4-job-bulk_causelist">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Daily · 07:00 PM IST</div>
                            <div className="text-sm font-medium mt-0.5">Cause List Notifications</div>
                        </div>
                        <Toggle4
                            on={cfg.s4_bulk_causelist_enabled !== false}
                            onChange={v => setCfg(c => ({ ...c, s4_bulk_causelist_enabled: v }))}
                            testId="s4-toggle-bulk_causelist"
                        />
                    </div>

                    <p className="text-[11px] text-muted-foreground">
                        Finds all cases with <code>next_hearing_date = tomorrow</code> using data already
                        pushed by the scraper. Sends <strong>Push + Email + WhatsApp</strong> notifications.
                        No <code>cnr_causelist_batch</code> API call — zero wallet cost.
                    </p>

                    <div className="space-y-1">
                        {["Push Notification", "Email", "WhatsApp"].map(ch => (
                            <div key={ch} className="flex items-center gap-2 text-[11px] text-muted-foreground">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0"/>
                                {ch}
                            </div>
                        ))}
                    </div>

                    <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-muted-foreground uppercase">Time (IST)</span>
                            <input
                                type="time"
                                value={cfg.s4_bulk_causelist_ist ?? "19:00"}
                                onChange={e => setCfg(c => ({ ...c, s4_bulk_causelist_ist: e.target.value }))}
                                data-testid="s4-time-bulk_causelist"
                                className="bg-background border border-border px-2 py-1 text-xs focus:border-primary outline-none w-24"
                            />
                        </div>
                        <button
                            onClick={() => runNow("bulk_causelist")}
                            disabled={running === "bulk_causelist" || !cfg.strategy_4_enabled}
                            data-testid="s4-run-bulk_causelist"
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.14em] border border-border hover:border-primary/60 disabled:opacity-30 disabled:cursor-not-allowed">
                            {running === "bulk_causelist"
                                ? <><Loader2 className="w-3 h-3 animate-spin"/> Running…</>
                                : <><Play className="w-3 h-3"/> Run Now</>}
                        </button>
                    </div>

                    {/* Per-job last result */}
                    {lastResult?.which === "bulk_causelist" && (
                        <div className="rounded border border-border bg-muted/30 px-3 py-2 text-[11px] flex gap-3 flex-wrap"
                            data-testid="s4-cl-result">
                            <span>CNRs <strong>{lastResult.total_cnrs ?? 0}</strong></span>
                            <span className="text-emerald-400">Notified <strong>{lastResult.notifications_sent ?? 0}</strong> tenants</span>
                            <span className="text-emerald-400 ml-auto">₹0.00</span>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Mark Stale for Scraping ───────────────────────────── */}
            <div className="border border-border rounded-xl p-4 space-y-4" data-testid="s4-stale-card">
                <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-sky-400"/>
                        Mark Stale for Scraping
                    </div>
                    <p className="text-[11px] text-muted-foreground mt-1">
                        Marks <strong>all</strong> cases with <code>next_hearing_date &lt; today</code> as
                        <code> scrape_requested_at = now</code>.  The scraper will pick them up and push
                        fresh data at zero eCourts wallet cost.  Use for recovery after scraper downtime.
                    </p>
                </div>
                <div className="flex items-end gap-4 flex-wrap">
                    <div className="space-y-1">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Filter by Tenant (optional)</label>
                        <select
                            value={staleTenantId}
                            onChange={e => setStaleTenantId(e.target.value)}
                            data-testid="s4-stale-tenant-filter"
                            className="bg-background border border-border px-3 py-2 text-sm focus:border-primary outline-none min-w-[200px]">
                            <option value="">— All Tenants —</option>
                            {tenantRows.map(t => (
                                <option key={t.id || t.tenant_id} value={t.id || t.tenant_id}>{t.name || t.id}</option>
                            ))}
                        </select>
                    </div>
                    <button
                        onClick={runMarkStale}
                        disabled={staleLoading}
                        data-testid="s4-run-stale-btn"
                        className="inline-flex items-center gap-2 px-4 py-2 border border-sky-500/50 text-sky-400 text-xs uppercase tracking-[0.14em] hover:border-sky-500 hover:text-sky-300 disabled:opacity-30 mb-6">
                        {staleLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <RefreshCw className="w-3.5 h-3.5"/>}
                        {staleLoading ? "Marking…" : "Mark Stale for Scraping"}
                    </button>
                </div>
                {lastStaleResult && (
                    <div className="flex flex-wrap gap-4 text-xs border border-sky-500/30 bg-sky-500/5 rounded-xl px-4 py-2.5"
                        data-testid="s4-last-stale-result">
                        <span className="text-sky-400 font-semibold">Last run:</span>
                        <span><strong>{lastStaleResult.total_found ?? "—"}</strong> stale cases found</span>
                        <span><strong>{lastStaleResult.total_marked ?? "—"}</strong> marked for scraping</span>
                        <span className="text-emerald-400 font-semibold">₹0.00</span>
                        <button onClick={() => setLastStaleResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}
            </div>

            {/* ── Scraper Trigger Configuration ─────────────────────── */}
            <div className="border border-border rounded-xl p-5 space-y-4" data-testid="s4-trigger-card">
                <div className="flex items-start justify-between">
                    <div>
                        <div className="text-sm font-medium flex items-center gap-2">
                            <Play className="w-4 h-4 text-emerald-400"/>
                            Scraper Trigger
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1 max-w-xl">
                            After the morning sweep queues CNRs, Law OS actively wakes the scraper via HTTP POST.
                            The scraper fetches CNRs from <code>GET /api/v1/sync/cnrs</code>, scrapes eCourts,
                            and pushes the data back to <code>POST /api/v1/sync/case</code> — updating the platform instantly.
                        </p>
                    </div>
                    <Toggle4
                        on={!!cfg.scraper_trigger_enabled}
                        onChange={v => setCfg(c => ({ ...c, scraper_trigger_enabled: v }))}
                        testId="s4-toggle-trigger"
                    />
                </div>

                <div className="space-y-2">
                    <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                        Scraper Trigger URL
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="url"
                            placeholder="https://your-scraper.example.com/trigger"
                            value={cfg.scraper_trigger_url ?? ""}
                            onChange={e => setCfg(c => ({ ...c, scraper_trigger_url: e.target.value }))}
                            data-testid="s4-trigger-url-input"
                            className="flex-1 bg-background border border-border px-3 py-2 text-xs focus:border-primary outline-none font-mono"
                        />
                        <button
                            onClick={triggerScraper}
                            disabled={triggering || !cfg.scraper_trigger_enabled || !cfg.scraper_trigger_url}
                            data-testid="s4-trigger-now-btn"
                            className="inline-flex items-center gap-2 px-4 py-2 border border-emerald-500/50 text-emerald-400 text-xs uppercase tracking-[0.14em] hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-30 disabled:cursor-not-allowed whitespace-nowrap">
                            {triggering ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : <Play className="w-3.5 h-3.5"/>}
                            {triggering ? "Triggering…" : "Trigger Now"}
                        </button>
                    </div>
                    <p className="text-[10px] text-muted-foreground">
                        Law OS will POST to this URL with <code>Authorization: Bearer SYNC_API_KEY</code>.
                        The scraper authenticates the same key to call the CNR feed.
                    </p>
                </div>

                {triggerResult && (
                    <div className={`rounded border px-3 py-2 text-[11px] flex items-center gap-3 flex-wrap ${triggerResult.triggered ? "border-emerald-500/30 bg-emerald-500/5 text-emerald-300" : "border-red-500/30 bg-red-500/5 text-red-300"}`}
                        data-testid="s4-trigger-result">
                        {triggerResult.triggered
                            ? <><span className="font-semibold">Triggered</span><span>HTTP {triggerResult.status_code}</span><span className="text-muted-foreground">{triggerResult.url}</span></>
                            : <><span className="font-semibold">Failed</span><span>{triggerResult.reason || triggerResult.error}</span></>
                        }
                        <button onClick={() => setTriggerResult(null)} className="ml-auto text-muted-foreground hover:text-foreground underline">Dismiss</button>
                    </div>
                )}

                {/* Flow diagram */}
                <div className="flex items-center gap-1 text-[10px] text-muted-foreground flex-wrap mt-1">
                    {[
                        { label: "Morning Sweep",     color: "bg-sky-500/15 text-sky-400 border-sky-400/30" },
                        { label: "→" },
                        { label: "Mark CNRs",         color: "bg-amber-500/15 text-amber-400 border-amber-400/30" },
                        { label: "→" },
                        { label: "Trigger Scraper",   color: "bg-emerald-500/15 text-emerald-400 border-emerald-400/30" },
                        { label: "→" },
                        { label: "Scrape eCourts",    color: "bg-rose-500/15 text-rose-400 border-rose-400/30" },
                        { label: "→" },
                        { label: "Push to Platform",  color: "bg-primary/15 text-primary border-primary/30" },
                    ].map((s, i) => s.color
                        ? <span key={i} className={`px-1.5 py-0.5 rounded border ${s.color}`}>{s.label}</span>
                        : <span key={i} className="text-muted-foreground/60">{s.label}</span>
                    )}
                </div>
            </div>

            {/* ── Cost Reference (S4 tab) ───────────────────────────────── */}
            <div className="bg-muted border border-border rounded p-4 text-xs space-y-2">
                <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Cost Comparison vs Other Strategies</div>
                <div className="flex justify-between"><span className="text-muted-foreground">Strategy 1 (hearing-anchored)</span><code>~₹2,000–4,000/month</code></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Strategy 2 (HOT/COLD tiered)</span><code>~₹4,600/month</code></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Strategy 3 (lightweight sweep)</span><code>~₹1,337/month</code></div>
                <div className="flex justify-between font-semibold text-emerald-400 border-t border-border pt-2">
                    <span>Strategy 4 (scraper-driven)</span><code className="text-emerald-400">₹0.00/month</code>
                </div>
                <p className="text-muted-foreground pt-1 text-[10px]">
                    Scraper infrastructure and hosting costs apply separately. eCourts wallet is never debited.
                </p>
            </div>

            {/* ── Save button ──────────────────────────────────────── */}
            <div className="flex items-center gap-4">
                <button
                    onClick={save}
                    disabled={saving}
                    data-testid="s4-save-btn"
                    className="inline-flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-30">
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin"/> : null}
                    {saving ? "Saving…" : "Save Strategy 4 Config"}
                </button>
                <button onClick={load} disabled={loading}
                    className="text-xs text-muted-foreground hover:text-foreground underline">Refresh</button>
            </div>
        </div>
    );
}


// ── Job Progress Stepper ──────────────────────────────────────────────────────
const JOB_STEPS = [
    { key: "queued",     label: "Queued",           desc: "Import request received"              },
    { key: "refreshing", label: "Refreshing",        desc: "Asking eCourts to process cases"      },
    { key: "fetching",   label: "Fetching details",  desc: "Downloading each case from eCourts"   },
    { key: "completed",  label: "Done",              desc: "All cases added to the platform"      },
];

function JobProgressStepper({ status, job }) {
    const currentIdx = JOB_STEPS.findIndex(s => s.key === status);
    const isComplete = status === "completed";
    const isError    = status === "error";

    return (
        <div className="border border-border rounded-xl p-4 space-y-4" data-testid="job-progress-stepper">
            {/* Steps */}
            <div className="flex items-start gap-0">
                {JOB_STEPS.map((step, i) => {
                    const done    = currentIdx > i || isComplete;
                    const active  = currentIdx === i && !isComplete;
                    const pending = currentIdx < i && !isComplete;
                    return (
                        <div key={step.key} className="flex-1 flex flex-col items-center gap-1 relative">
                            {/* Connector line */}
                            {i > 0 && (
                                <div className={`absolute left-0 top-3.5 w-full h-0.5 -translate-y-1/2 ${done ? "bg-emerald-500" : "bg-border"}`}
                                    style={{ right: "50%", left: "0", width: "50%", marginLeft: 0 }} />
                            )}
                            {i < JOB_STEPS.length - 1 && (
                                <div className={`absolute right-0 top-3.5 w-1/2 h-0.5 -translate-y-1/2 ${done ? "bg-emerald-500" : "bg-border"}`} />
                            )}
                            {/* Circle */}
                            <div className={`relative z-10 w-7 h-7 rounded-full flex items-center justify-center border-2 transition-all
                                ${done    ? "bg-emerald-500 border-emerald-500 text-white"       : ""}
                                ${active  ? "bg-background border-primary text-primary"          : ""}
                                ${pending ? "bg-background border-border text-muted-foreground"  : ""}
                                ${isError && i === currentIdx ? "bg-red-500 border-red-500 text-white" : ""}
                            `}>
                                {done    ? <CheckCircle2 className="w-4 h-4" /> : null}
                                {active  ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                                {pending ? <span className="text-[10px] font-bold">{i + 1}</span> : null}
                            </div>
                            {/* Label */}
                            <span className={`text-[10px] font-medium text-center leading-tight mt-0.5
                                ${done ? "text-emerald-600 dark:text-emerald-400" : ""}
                                ${active ? "text-primary" : ""}
                                ${pending ? "text-muted-foreground" : ""}
                            `}>{step.label}</span>
                        </div>
                    );
                })}
            </div>

            {/* Current step description */}
            <div className="text-xs text-muted-foreground text-center min-h-[1.25rem]">
                {isError
                    ? <span className="text-red-500">{job?.error || "Import failed"}</span>
                    : isComplete
                        ? <span className="text-emerald-600 dark:text-emerald-400 font-medium">
                            {job?.imported_count ?? 0} case(s) added · {job?.failed_count ?? 0} failed
                          </span>
                        : JOB_STEPS[currentIdx]?.desc ?? "Processing…"
                }
            </div>

            {/* Completed detail rows */}
            {isComplete && job?.results?.length > 0 && (
                <div className="border border-border rounded overflow-auto max-h-48">
                    <table className="w-full text-[11px]">
                        <thead className="bg-muted">
                            <tr>
                                <th className="p-2 text-left text-muted-foreground font-medium">CNR</th>
                                <th className="p-2 text-left text-muted-foreground font-medium">Status</th>
                                <th className="p-2 text-left text-muted-foreground font-medium">Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {job.results.map((r, i) => (
                                <tr key={r.cnr || i} className="border-t border-border">
                                    <td className="p-2 font-mono text-[10px]">{r.cnr}</td>
                                    <td className="p-2">
                                        <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium
                                            ${r.status === "imported" ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : ""}
                                            ${r.status === "updated"  ? "bg-sky-500/15 text-sky-600 dark:text-sky-400" : ""}
                                            ${r.status === "failed"   ? "bg-red-500/15 text-red-500" : ""}
                                            ${r.status === "skipped"  ? "bg-muted text-muted-foreground" : ""}
                                        `}>{r.status}</span>
                                    </td>
                                    <td className="p-2 text-muted-foreground max-w-[200px] truncate">{r.reason || r.title || "—"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Polling indicator */}
            {!isComplete && !isError && (
                <div className="flex items-center justify-center gap-2 text-[10px] text-muted-foreground">
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
                    Auto-refreshing every 5 seconds…
                </div>
            )}
        </div>
    );
}


// ============================================================================
// Import on Behalf of Tenant
// ============================================================================
function ImportOnBehalfTab() {
    const [tenants, setTenants]         = useState([]);
    const [tenantId, setTenantId]       = useState("");
    const [tenantClients, setTenantClients] = useState([]);
    const [clientId, setClientId]       = useState("");
    const [form, setForm]               = useState({ advocate_name: "", year: "" });
    const [step, setStep]               = useState("search"); // search | searching | results | importing | done
    const [searching, setSearching]     = useState(false);
    const [results, setResults]         = useState([]);
    const [selected, setSelected]       = useState(new Set());
    const [searchMeta, setSearchMeta]   = useState(null);
    const [importing, setImporting]     = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [filterText, setFilterText]   = useState("");
    const [jobStatus, setJobStatus]     = useState(null); // live import job status
    const [searchJobStatus, setSearchJobStatus] = useState(null); // live search job status
    const [statusFilter, setStatusFilter] = useState(new Set()); // selected case statuses
    const [stateFilter, setStateFilter]   = useState("");
    const [districtFilter, setDistrictFilter] = useState("");
    const [courtFilter, setCourtFilter]   = useState("");
    const [costInfo, setCostInfo]         = useState(null); // { per_page_inr, wallet_balance, actual_search_cost_inr, per_cnr_inr }
    const pollRef = useRef(null);
    const searchPollRef = useRef(null);

    // Load tenants on mount
    useEffect(() => {
        api.get("/tenants?limit=200")
            .then(r => setTenants(Array.isArray(r.data) ? r.data : r.data?.tenants || []))
            .catch(() => {});
    }, []);

    // Load clients whenever tenant changes
    useEffect(() => {
        if (!tenantId) { setTenantClients([]); setClientId(""); return; }
        api.get(`/admin/tenants/${tenantId}/clients`)
            .then(r => setTenantClients(Array.isArray(r.data) ? r.data : r.data?.clients || []))
            .catch(() => setTenantClients([]));
    }, [tenantId]);

    const runSearch = async () => {
        if (!tenantId) { toast.error("Select a tenant first"); return; }
        if (!form.advocate_name.trim()) { toast.error("Advocate name is required"); return; }
        setSearching(true);
        setResults([]);
        setSelected(new Set());
        setFilterText("");
        setSearchJobStatus(null);
        try {
            const { data } = await api.post("/admin/ecourts/import/search", {
                tenant_id:     tenantId,
                advocate_name: form.advocate_name.trim(),
                year:          form.year ? parseInt(form.year) : null,
            });
            const jobId = data.search_job_id;
            if (!jobId) throw new Error("No search_job_id returned from server");
            setCostInfo({ per_page_inr: data.per_page_inr ?? 0, wallet_balance: data.wallet_balance ?? null });
            setSearchJobStatus({ status: "queued", total_variants: data.total_variants });
            setStep("searching");
            startSearchPolling(jobId);
        } catch (e) {
            toast.error(apiError(e));
            setSearching(false);
        }
    };

    const startSearchPolling = (jobId) => {
        if (searchPollRef.current) clearInterval(searchPollRef.current);
        searchPollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/admin/ecourts/search-jobs/${jobId}`);
                setSearchJobStatus(data);
                if (data.status === "completed") {
                    clearInterval(searchPollRef.current);
                    searchPollRef.current = null;
                    setSearching(false);
                    const res = data.results || [];
                    setResults(res);
                    setSearchMeta(data);
                    // Only pre-select new (not already imported) cases
                    setSelected(new Set(res.filter(r => !r.already_imported).map(r => r.cnr)));
                    // Init status filter with all unique statuses active
                    const statuses = [...new Set(res.map(r => (r.case_status || "Unknown").toUpperCase()).filter(Boolean))];
                    setStatusFilter(new Set(statuses));
                    // Update cost info with actual search cost
                    setCostInfo(prev => ({ ...prev, actual_search_cost_inr: data.actual_search_cost_inr ?? null, per_page_inr: data.per_page_inr ?? prev?.per_page_inr }));
                    setStep("results");
                    const newCount = res.filter(r => !r.already_imported).length;
                    if (!newCount) {
                        toast.info(data.already_imported_count > 0
                            ? `No new cases (${data.already_imported_count} already imported by this tenant)`
                            : "No cases found in eCourts");
                    } else {
                        toast.success(`Found ${newCount} new case(s) across ${data.variants_searched?.length || 1} name variant(s)`);
                    }
                } else if (data.status === "error") {
                    clearInterval(searchPollRef.current);
                    searchPollRef.current = null;
                    setSearching(false);
                    toast.error(`Search failed: ${data.error || "Unknown error"}`);
                    setStep("search");
                }
            } catch (_) { /* ignore transient poll errors */ }
        }, 3000);
    };

    const toggle = cnr => {
        const row = results.find(r => r.cnr === cnr);
        if (row?.already_imported) return; // cannot select already-imported rows
        const next = new Set(selected);
        next.has(cnr) ? next.delete(cnr) : next.add(cnr);
        setSelected(next);
    };

    const runImport = async () => {
        if (!selected.size) { toast.error("Select at least one case"); return; }
        setImporting(true);
        setStep("importing");
        try {
            const { data } = await api.post("/admin/ecourts/import/bulk", {
                tenant_id: tenantId,
                cnrs:      Array.from(selected),
                client_id: clientId || null,
            }, { timeout: 5 * 60 * 1000 });
            setImportResult(data);
            setJobStatus({ status: "queued", total_cnrs: data.cnr_count });
            setCostInfo(prev => ({ ...prev, per_cnr_inr: data.per_cnr_inr ?? null, estimated_import_cost_inr: data.estimated_cost_inr ?? null }));
            setStep("done");
            toast.success(data.message || `Import queued for ${data.cnr_count} case(s)`);
            // Start polling job status
            if (data.job_id) {
                startPolling(data.job_id);
            }
        } catch (e) { toast.error(apiError(e)); setStep("results"); }
        finally { setImporting(false); }
    };

    const startPolling = (jobId) => {
        // Clear any existing poll
        if (pollRef.current) clearInterval(pollRef.current);
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/admin/ecourts/import/jobs/${jobId}`);
                setJobStatus(data);
                if (data.status === "completed" || data.status === "error") {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    if (data.status === "completed") {
                        toast.success(`Import complete — ${data.imported_count ?? 0} case(s) added`);
                    }
                }
            } catch (_) { /* ignore transient errors */ }
        }, 5000); // poll every 5 s
    };

    // Cleanup polls on unmount
    useEffect(() => () => {
        if (pollRef.current) clearInterval(pollRef.current);
        if (searchPollRef.current) clearInterval(searchPollRef.current);
    }, []);

    const [retrying, setRetrying] = useState(false);

    const retryFailed = async () => {
        if (!importResult?.job_id) return;
        setRetrying(true);
        try {
            const { data } = await api.post(`/admin/ecourts/import/jobs/${importResult.job_id}/retry-failed`);
            toast.success(`Retrying ${data.cnr_count} failed case(s) — est. ₹${data.estimated_cost_inr ?? "?"}`);
            // Switch polling to the new retry job
            setImportResult(prev => ({ ...prev, ...data }));
            setJobStatus({ status: "queued", total_cnrs: data.cnr_count });
            setCostInfo(prev => ({ ...prev, estimated_import_cost_inr: data.estimated_cost_inr ?? null }));
            if (pollRef.current) clearInterval(pollRef.current);
            startPolling(data.job_id);
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setRetrying(false);
        }
    };

    const reset = () => {
        if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        if (searchPollRef.current) { clearInterval(searchPollRef.current); searchPollRef.current = null; }
        setStep("search");
        setSearching(false);
        setResults([]);
        setSelected(new Set());
        setSearchMeta(null);
        setSearchJobStatus(null);
        setStatusFilter(new Set());
        setStateFilter("");
        setDistrictFilter("");
        setCourtFilter("");
        setCostInfo(null);
        setImportResult(null);
        setJobStatus(null);
        setFilterText("");
        setClientId("");
        setForm({ advocate_name: "", year: "" });
    };

    const selectedTenant = tenants.find(t => (t.id || t._id) === tenantId);

    // Status counts (over ALL results, ignoring current filters)
    const statusCounts = results.reduce((acc, r) => {
        const s = (r.case_status || "Unknown").toUpperCase();
        acc[s] = (acc[s] || 0) + 1;
        return acc;
    }, {});
    const allStatuses = Object.keys(statusCounts).sort();

    // Dropdown options (cascading: district narrows by state, court by district)
    const allStates    = [...new Set(results.map(r => r.state_name).filter(Boolean))].sort();
    const allDistricts = [...new Set(results.filter(r => !stateFilter || r.state_name === stateFilter).map(r => r.district_name).filter(Boolean))].sort();
    const allCourts    = [...new Set(results.filter(r => (!stateFilter || r.state_name === stateFilter) && (!districtFilter || r.district_name === districtFilter)).map(r => r.court_name).filter(Boolean))].sort();

    const filtered = results.filter(r => {
        if (filterText) {
            const q = filterText.toLowerCase();
            if (!`${r.cnr} ${r.title} ${r.court_name} ${r.state_name} ${r.district_name}`.toLowerCase().includes(q)) return false;
        }
        if (statusFilter.size > 0) {
            const s = (r.case_status || "Unknown").toUpperCase();
            if (!statusFilter.has(s)) return false;
        }
        if (stateFilter    && r.state_name    !== stateFilter)    return false;
        if (districtFilter && r.district_name !== districtFilter) return false;
        if (courtFilter    && r.court_name    !== courtFilter)    return false;
        return true;
    });
    const newInFiltered = filtered.filter(r => !r.already_imported).length;

    const toggleStatus = (s) => {
        const next = new Set(statusFilter);
        next.has(s) ? next.delete(s) : next.add(s);
        setStatusFilter(next);
    };
    const clearAllFilters = () => {
        setFilterText("");
        setStatusFilter(new Set(allStatuses));
        setStateFilter("");
        setDistrictFilter("");
        setCourtFilter("");
    };
    const activeFilterCount = (stateFilter ? 1 : 0) + (districtFilter ? 1 : 0) + (courtFilter ? 1 : 0) + (statusFilter.size < allStatuses.length ? 1 : 0);

    return (
        <div className="space-y-6 max-w-4xl">
            <div className="flex items-start justify-between">
                <div>
                    <h3 className="text-sm font-semibold flex items-center gap-2">
                        <UserCheck className="w-4 h-4 text-primary" />
                        Import Cases on Behalf of Tenant
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Search eCourts by advocate name for any tenant and import selected cases into their account.
                        Costs are debited from the tenant's AI wallet.
                    </p>
                </div>
                {step !== "search" && (
                    <button onClick={reset}
                        className="text-xs text-muted-foreground hover:text-foreground underline">
                        Start over
                    </button>
                )}
            </div>

            {/* ── PERSISTENT COST STRIP (visible from searching onward) ── */}
            {costInfo && (
                <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2.5 bg-muted/50 border border-border rounded-lg text-xs" data-testid="admin-import-cost-strip">
                    {/* Wallet balance */}
                    {costInfo.wallet_balance != null && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Wallet</span>
                            <span className={`font-semibold tabular-nums ${costInfo.wallet_balance < 50 ? "text-red-500" : "text-foreground"}`}>
                                ₹{costInfo.wallet_balance.toFixed(2)}
                            </span>
                        </div>
                    )}
                    {/* Search cost */}
                    {costInfo.per_page_inr != null && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Search</span>
                            {costInfo.actual_search_cost_inr != null ? (
                                <span className="font-semibold tabular-nums text-foreground">
                                    ₹{costInfo.actual_search_cost_inr.toFixed(4)}
                                    <span className="text-muted-foreground font-normal ml-1 text-[10px]">
                                        ({searchJobStatus?.pages_called ?? 0} pages × ₹{costInfo.per_page_inr}/page)
                                    </span>
                                </span>
                            ) : (
                                <span className="font-semibold tabular-nums text-foreground">
                                    ≈ ₹{((searchJobStatus?.pages_called ?? 0) * costInfo.per_page_inr).toFixed(4)}
                                    <span className="text-muted-foreground font-normal ml-1 text-[10px]">(running)</span>
                                </span>
                            )}
                        </div>
                    )}
                    {/* Import estimate (shown once cases are selected) */}
                    {costInfo.per_cnr_inr != null && selected.size > 0 && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Import</span>
                            <span className="font-semibold tabular-nums text-foreground">
                                ≈ ₹{(costInfo.per_cnr_inr * selected.size).toFixed(2)}
                                <span className="text-muted-foreground font-normal ml-1 text-[10px]">
                                    ({selected.size} cases × ₹{costInfo.per_cnr_inr}/case)
                                </span>
                            </span>
                        </div>
                    )}
                    {step === "results" && costInfo.per_cnr_inr == null && selected.size > 0 && (
                        <div className="flex items-center gap-1.5">
                            <span className="text-muted-foreground">Import rate</span>
                            <span className="text-muted-foreground">loaded on import</span>
                        </div>
                    )}
                    {/* Total after import queued */}
                    {costInfo.estimated_import_cost_inr != null && (
                        <div className="flex items-center gap-1.5 border-l border-border pl-4 ml-1">
                            <span className="text-muted-foreground">Total est.</span>
                            <span className="font-bold tabular-nums text-primary">
                                ₹{((costInfo.actual_search_cost_inr ?? 0) + costInfo.estimated_import_cost_inr).toFixed(2)}
                            </span>
                        </div>
                    )}
                </div>
            )}

            {/* ── STEP: SEARCH ── */}
            {step === "search" && (
                <div className="border border-border rounded-xl p-5 space-y-4" data-testid="admin-import-search-card">
                    {/* Tenant selector */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Tenant *</label>
                        <select
                            data-testid="admin-import-tenant-select"
                            value={tenantId}
                            onChange={e => { setTenantId(e.target.value); setClientId(""); }}
                            className="w-full px-3 py-2 bg-background border border-border text-sm focus:border-primary outline-none"
                        >
                            <option value="">-- Select a tenant --</option>
                            {tenants.map(t => (
                                <option key={t.id || t._id} value={t.id || t._id}>
                                    {t.name || t.firm_name || t.email} {t.email ? `(${t.email})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Advocate name */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Advocate Name *</label>
                        <input
                            data-testid="admin-import-advocate-name"
                            type="text"
                            placeholder="e.g. B.P.Saseendran"
                            value={form.advocate_name}
                            onChange={e => setForm(f => ({ ...f, advocate_name: e.target.value }))}
                            onKeyDown={e => e.key === "Enter" && runSearch()}
                            className="w-full px-3 py-2 bg-background border border-border text-sm focus:border-primary outline-none"
                        />
                        <p className="text-[10px] text-muted-foreground">
                            The system searches up to 7 spelling variants automatically (e.g. "B P Saseendran", "Saseendran B P", …).
                        </p>
                    </div>

                    {/* Year */}
                    <div className="space-y-1.5">
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Year (optional)</label>
                        <input
                            data-testid="admin-import-year"
                            type="number"
                            placeholder="e.g. 2024"
                            value={form.year}
                            onChange={e => setForm(f => ({ ...f, year: e.target.value }))}
                            className="w-40 px-3 py-2 bg-background border border-border text-sm focus:border-primary outline-none"
                        />
                    </div>

                    <button
                        data-testid="admin-import-search-btn"
                        onClick={runSearch}
                        disabled={searching || !tenantId || !form.advocate_name.trim()}
                        className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-40"
                    >
                        {searching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Search className="w-3.5 h-3.5" />}
                        {searching ? "Starting search…" : "Search eCourts"}
                    </button>
                </div>
            )}

            {/* ── STEP: SEARCHING (background polling) ── */}
            {step === "searching" && (
                <div className="border border-border rounded-xl p-6 space-y-5" data-testid="admin-import-searching">
                    <div className="flex items-center gap-3">
                        <Loader2 className="w-5 h-5 animate-spin text-primary shrink-0" />
                        <div>
                            <p className="text-sm font-semibold">Searching eCourts…</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                                Fetching ALL pages for all name variants — no case will be missed.
                            </p>
                        </div>
                    </div>

                    {searchJobStatus && (
                        <div className="bg-muted/40 rounded-lg p-4 space-y-3 text-xs">
                            {/* Progress bar */}
                            {searchJobStatus.total_variants > 0 && (
                                <div className="space-y-1">
                                    <div className="flex justify-between text-muted-foreground">
                                        <span>Variants searched</span>
                                        <span className="font-mono">
                                            {searchJobStatus.variant_index || 0} / {searchJobStatus.total_variants}
                                        </span>
                                    </div>
                                    <div className="h-1.5 bg-border rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-primary rounded-full transition-all duration-500"
                                            style={{ width: `${Math.round(((searchJobStatus.variant_index || 0) / searchJobStatus.total_variants) * 100)}%` }}
                                        />
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <p className="text-muted-foreground">Current variant</p>
                                    <p className="font-medium mt-0.5 truncate">
                                        {searchJobStatus.current_variant || "Queued…"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Cases found so far</p>
                                    <p className="font-semibold text-primary mt-0.5">
                                        {searchJobStatus.found_so_far ?? 0}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Pages fetched</p>
                                    <p className="font-medium mt-0.5">{searchJobStatus.pages_called ?? 0}</p>
                                </div>
                                <div>
                                    <p className="text-muted-foreground">Status</p>
                                    <p className="font-medium mt-0.5 capitalize">{searchJobStatus.status}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    <p className="text-[11px] text-muted-foreground animate-pulse">
                        Auto-refreshing every 3 s — results will appear automatically when complete…
                    </p>
                </div>
            )}

            {/* ── STEP: RESULTS ── */}
            {step === "results" && (
                <div className="space-y-4" data-testid="admin-import-results-section">
                    {/* Meta summary */}
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                            <span className="font-semibold text-foreground">
                                {results.filter(r => !r.already_imported).length} new case(s) found
                            </span>
                            {searchMeta?.already_imported_count > 0 && (
                                <span className="text-amber-600">{searchMeta.already_imported_count} already imported</span>
                            )}
                            {searchMeta?.partial && (
                                <span className="text-amber-500">Partial (rate limited)</span>
                            )}
                            <span>Tenant: <strong>{selectedTenant?.name || tenantId}</strong></span>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">{selected.size} selected</span>
                            <button
                                onClick={() => setSelected(new Set(filtered.filter(r => !r.already_imported).map(r => r.cnr)))}
                                className="text-xs text-primary hover:underline">All visible</button>
                            <button onClick={() => setSelected(new Set())}
                                className="text-xs text-muted-foreground hover:underline">None</button>
                        </div>
                    </div>

                    {/* Filters row */}
                    <div className="space-y-2">
                        {/* Text filter + clear */}
                        <div className="flex gap-2">
                            <input
                                data-testid="admin-import-filter"
                                type="text"
                                placeholder="Filter by CNR, title, court, state…"
                                value={filterText}
                                onChange={e => setFilterText(e.target.value)}
                                className="flex-1 px-3 py-2 bg-background border border-border text-sm focus:border-primary outline-none"
                            />
                            {activeFilterCount > 0 && (
                                <button onClick={clearAllFilters}
                                    className="px-3 py-2 text-xs border border-border hover:border-primary hover:text-primary text-muted-foreground whitespace-nowrap">
                                    Clear all
                                </button>
                            )}
                        </div>

                        {/* Status pills with counts */}
                        {allStatuses.length > 0 && (
                            <div className="flex flex-wrap gap-1.5 items-center" data-testid="admin-import-status-filter">
                                <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Status:</span>
                                <button
                                    onClick={() => setStatusFilter(new Set(allStatuses))}
                                    className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${statusFilter.size === allStatuses.length ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                                    data-testid="status-filter-all"
                                >
                                    All ({results.length})
                                </button>
                                {allStatuses.map(s => (
                                    <button
                                        key={s}
                                        onClick={() => toggleStatus(s)}
                                        data-testid={`status-filter-${s}`}
                                        className={`px-2 py-0.5 rounded-full text-[10px] border transition-colors ${
                                            statusFilter.has(s)
                                                ? s.includes("PEND") || s.includes("ACTIV")
                                                    ? "bg-amber-500/20 text-amber-700 border-amber-400"
                                                    : "bg-primary/10 text-primary border-primary/40"
                                                : "border-border text-muted-foreground opacity-50 hover:opacity-80"
                                        }`}
                                    >
                                        {s} ({statusCounts[s] ?? 0})
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Location filters: State / District / Court */}
                        {(allStates.length > 1 || allDistricts.length > 1 || allCourts.length > 1) && (
                            <div className="flex flex-wrap gap-2 items-center" data-testid="admin-import-location-filters">
                                <span className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">Filter:</span>
                                {/* State — only show when multi-state */}
                                {allStates.length > 1 && (
                                    <select
                                        data-testid="admin-import-state-filter"
                                        value={stateFilter}
                                        onChange={e => { setStateFilter(e.target.value); setDistrictFilter(""); setCourtFilter(""); }}
                                        className="px-2 py-1 bg-background border border-border text-xs focus:border-primary outline-none max-w-[160px]"
                                    >
                                        <option value="">All States</option>
                                        {allStates.map(s => <option key={s} value={s}>{s}</option>)}
                                    </select>
                                )}
                                {/* District */}
                                {allDistricts.length > 1 && (
                                    <select
                                        data-testid="admin-import-district-filter"
                                        value={districtFilter}
                                        onChange={e => { setDistrictFilter(e.target.value); setCourtFilter(""); }}
                                        className="px-2 py-1 bg-background border border-border text-xs focus:border-primary outline-none max-w-[160px]"
                                    >
                                        <option value="">All Districts</option>
                                        {allDistricts.map(d => <option key={d} value={d}>{d}</option>)}
                                    </select>
                                )}
                                {/* Court */}
                                {allCourts.length > 1 && (
                                    <select
                                        data-testid="admin-import-court-filter"
                                        value={courtFilter}
                                        onChange={e => setCourtFilter(e.target.value)}
                                        className="px-2 py-1 bg-background border border-border text-xs focus:border-primary outline-none max-w-[200px]"
                                    >
                                        <option value="">All Courts</option>
                                        {allCourts.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                )}
                                {(stateFilter || districtFilter || courtFilter) && (
                                    <span className="text-[10px] text-muted-foreground">
                                        {filtered.length} shown
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Results table */}
                    <div className="border border-border rounded-lg overflow-auto max-h-[50vh]">
                        <table className="w-full text-xs">
                            <thead className="bg-muted sticky top-0">
                                <tr>
                                    <th className="p-2 w-8"></th>
                                    <th className="p-2 text-left font-medium text-muted-foreground">CNR</th>
                                    <th className="p-2 text-left font-medium text-muted-foreground">Title</th>
                                    <th className="p-2 text-left font-medium text-muted-foreground">Court</th>
                                    <th className="p-2 text-left font-medium text-muted-foreground">Status</th>
                                    <th className="p-2 text-left font-medium text-muted-foreground">Next Hearing</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map(r => {
                                    const isImported = r.already_imported;
                                    return (
                                        <tr key={r.cnr}
                                            onClick={() => !isImported && toggle(r.cnr)}
                                            className={`border-t border-border transition-colors ${
                                                isImported
                                                    ? "opacity-40 cursor-not-allowed bg-muted/20"
                                                    : `cursor-pointer hover:bg-muted/50 ${selected.has(r.cnr) ? "bg-primary/5" : ""}`
                                            }`}
                                            data-testid={`admin-import-row-${r.cnr}`}>
                                            <td className="p-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    readOnly
                                                    disabled={isImported}
                                                    checked={!isImported && selected.has(r.cnr)}
                                                    className="accent-primary cursor-pointer disabled:cursor-not-allowed"
                                                />
                                            </td>
                                            <td className="p-2 font-mono text-[10px]">{r.cnr}</td>
                                            <td className="p-2 max-w-[200px] truncate" title={r.title}>
                                                {r.title || "—"}
                                                {isImported && (
                                                    <span className="ml-1.5 px-1 py-0.5 bg-muted text-muted-foreground text-[9px] rounded">
                                                        Imported
                                                    </span>
                                                )}
                                            </td>
                                            <td className="p-2 text-muted-foreground max-w-[140px] truncate">
                                                {r.court_name ? `${r.court_name}${r.district_name ? ", " + r.district_name : ""}` : "—"}
                                            </td>
                                            <td className="p-2">
                                                {r.case_status ? (
                                                    <span className={`px-1.5 py-0.5 rounded text-[10px] ${r.case_status?.toUpperCase().includes("PEND") || r.case_status?.toUpperCase().includes("ACTIV") ? "bg-amber-500/15 text-amber-600 dark:text-amber-400" : "bg-muted text-muted-foreground"}`}>
                                                        {r.case_status}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td className="p-2 text-muted-foreground">{r.next_hearing_date || "—"}</td>
                                        </tr>
                                    );
                                })}
                                {!filtered.length && (
                                    <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No results match filter</td></tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Client selector + Import button */}
                    <div className="border border-border rounded-xl p-4 space-y-3 bg-muted/30">
                        <div className="space-y-1.5">
                            <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
                                Assign Client (optional)
                            </label>
                            <select
                                data-testid="admin-import-client-select"
                                value={clientId}
                                onChange={e => setClientId(e.target.value)}
                                className="w-full px-3 py-2 bg-background border border-border text-sm focus:border-primary outline-none"
                            >
                                <option value="">-- No client assignment --</option>
                                {tenantClients.map(c => (
                                    <option key={c.id || c._id} value={c.id || c._id}>
                                        {c.name || c.full_name || c.email}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                data-testid="admin-import-btn"
                                onClick={runImport}
                                disabled={!selected.size || importing}
                                className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground text-xs uppercase tracking-[0.14em] hover:opacity-90 disabled:opacity-40"
                            >
                                {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                {importing ? "Queuing import…" : `Import ${selected.size} Case(s)`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── STEP: IMPORTING ── */}
            {step === "importing" && (
                <div className="flex flex-col items-center gap-4 py-12" data-testid="admin-import-loading">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                    <p className="text-sm text-muted-foreground">Queuing import for {selected.size} case(s)…</p>
                </div>
            )}

            {/* ── STEP: DONE (with live polling) ── */}
            {step === "done" && importResult && (
                <div className="space-y-4" data-testid="admin-import-done">
                    {/* Header */}
                    <div className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                        <div>
                            <div className="font-semibold text-sm">Import queued for {importResult.tenant_name || tenantId}</div>
                            <div className="text-xs text-muted-foreground mt-0.5">
                                Job <code className="font-mono bg-muted px-1 rounded text-[10px]">{importResult.job_id}</code>
                                &nbsp;·&nbsp;{importResult.cnr_count} case(s) queued
                                {importResult.skipped_count > 0 && ` · ${importResult.skipped_count} already imported`}
                                {importResult.retry_of && <span className="ml-1 text-amber-600">(retry)</span>}
                            </div>
                        </div>
                    </div>

                    {/* Live progress stepper */}
                    <JobProgressStepper status={jobStatus?.status} job={jobStatus} />

                    {/* Failed cases — retry button */}
                    {jobStatus?.status === "completed" && jobStatus?.failed_count > 0 && (
                        <div className="flex items-center gap-3 p-3 border border-destructive/30 rounded-lg bg-destructive/5"
                             data-testid="admin-import-failed-banner">
                            <span className="text-xs text-destructive font-medium">
                                {jobStatus.failed_count} case(s) failed to import
                            </span>
                            <button
                                data-testid="admin-import-retry-btn"
                                onClick={retryFailed}
                                disabled={retrying}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-destructive text-destructive-foreground text-xs uppercase tracking-[0.12em] hover:opacity-90 disabled:opacity-40"
                            >
                                {retrying
                                    ? <><Loader2 className="w-3 h-3 animate-spin" /> Retrying…</>
                                    : <><RefreshCw className="w-3 h-3" /> Re-import {jobStatus.failed_count} failed</>}
                            </button>
                        </div>
                    )}

                    <button
                        data-testid="admin-import-again-btn"
                        onClick={reset}
                        className="inline-flex items-center gap-2 px-4 py-2 border border-border text-xs uppercase tracking-[0.14em] hover:border-primary hover:text-primary">
                        Import for Another Tenant
                    </button>
                </div>
            )}
        </div>
    );
}
